import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  FileText,
  Users,
  Bike,
  Shield,
  Gift,
  Star,
  Phone,
  Mail,
  MapPin,
  Upload,
  ChevronRight,
  ChevronDown,
  Building2,
  Handshake,
  Trophy,
  Medal,
  Crown,
  Bell,
  TrendingUp,
  CreditCard,
  AlertCircle,
  FileCheck,
  Car,
  IdCard,
  Loader2,
  Globe,
  HelpCircle,
  BookOpen,
  MessageCircle,
  Send,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Toaster, toast } from "../components/ui/sonner";
import axios from "axios";

// Use relative URLs for API calls - works in both dev and production
const API = "/api";

const content = {
  en: {
    nav: {
      packages: "Packages",
      howItWorks: "How It Works",
      leaderboard: "Leaderboard",
      info: "Info",
      forShops: "For Shops",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "For NEW riders only — not yet registered on Uber Eats or DoorDash (or at least one of them)",
      newRidersShort: "New riders only — Not yet registered on Uber Eats/DoorDash",
      newRidersImportant: "Important: This service is ONLY for new riders not yet registered on Uber Eats or DoorDash (or at least one of the two platforms)",
      premiumOnly: "Premium Members Only",
      leaderboardPremium: "To participate in the leaderboard and win cash bonuses, you must have purchased the Premium package",
    },
    hero: {
      headline: "Start Your Delivery Career in Australia",
      subheadline: "Complete onboarding support for Uber Eats & DoorDash. Get registered, find a vehicle, and start earning — all with expert guidance.",
      cta1: "Get Started",
      cta2: "View Packages",
      badge: "No Risk • Easy Start • Real Bonuses",
    },
    packages: {
      title: "Choose Your Package",
      subtitle: "One-time payment. No hidden fees. Full support.",
      base: {
        name: "Base",
        price: "$49",
        description: "Perfect for self-starters",
        features: [
          "ABN application preparation (ready for final submission)",
          "Step-by-step Uber/DoorDash registration guidance",
          "Email support",
          "Referral bonus included",
        ],
        cta: "Choose Base",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "Most Popular",
        description: "Full service experience",
        features: [
          "Everything in Base package",
          "Direct coordination with our trusted rental partners",
          "We prepare and forward your documents to the rental shop to speed up vehicle setup",
          "We help reduce onboarding delays so you can start delivering faster",
          "Priority WhatsApp support",
        ],
        highlighted: "Minimum 4-week delivery activity commitment may apply when using partner rental shops",
        cta: "Choose Premium",
      },
    },
    howItWorks: {
      title: "How It Works",
      subtitle: "Start delivering in 4 simple steps",
      steps: [
        { number: "01", title: "Upload Documents", description: "Send us your ID, visa, and license" },
        { number: "02", title: "Choose Package", description: "Select Base ($49) or Premium ($79)" },
        { number: "03", title: "Complete Payment", description: "Secure PayID bank transfer" },
        { number: "04", title: "Start Earning", description: "We guide you through Uber/DoorDash setup" },
      ],
    },
    documents: {
      title: "Required Documents",
      subtitle: "To begin your eligibility review, please upload the following documents:",
      items: [
        { icon: "id", title: "Proof of Identity", desc: "Valid passport or ID card" },
        { icon: "car", title: "Driver Licence", desc: "Only required if you plan to deliver using a scooter or motorbike. E-bike delivery requires no licence. Scooter/motorbike delivery may require conversion of an overseas licence." },
        { icon: "file", title: "Valid Visa", desc: "Copy of visa allowing you to work legally in Australia" },
      ],
      licenseWarning: {
        title: "Important: Scooter License Requirements",
        nsw_vic: "NSW & VIC: Motorcycle license (R or RE class) required to ride ANY scooter, regardless of engine size.",
        qld: "QLD: With a car license (C class) you can ride scooters up to 50cc only.",
      },
      licenseTitle: "How to Convert Foreign License",
      licenseSteps: [
        "Contact Transport and Main Roads in your state",
        "Present your foreign license and required translations",
        "Complete verification and tests (if required)",
        "Receive your equivalent Australian license",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Compete & Earn Extra Cash",
      subtitle: "Every Week & Month",
      description: "Compete with other riders and earn extra cash every week and month!",
      comingSoon: "Leaderboard Coming Soon",
      comingSoonText: "Top performers will win exclusive bonuses. Be among the first to compete!",
      notifyBtn: "Notify Me When Live",
      weekly: { title: "Weekly Leaderboard", subtitle: "Weekly bonus for top performers", prize: "Weekly Prize" },
      monthly: { title: "Monthly Leaderboard", subtitle: "Bigger rewards for consistent performers", prize: "Monthly Prize" },
      tableHeaders: { rank: "Rank", rider: "Rider", deliveries: "Deliveries", bonus: "Bonus" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "You?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Partner With Us",
      subtitle: "Are you a rental shop or vehicle provider?",
      description: "We send new customers your way — no commitment required.",
      features: ["Receive pre-qualified rider leads", "No upfront costs", "Optional referral bonus program", "Direct communication"],
      cta: "Become a Partner",
    },
    testimonials: {
      title: "What Riders Say",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Just Delivery made everything so easy!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "The Premium package was worth it.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Their support team explained everything clearly.", rating: 5 },
      ],
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Who can use this service?", a: "This service is ONLY for NEW riders who are not yet registered on Uber Eats or DoorDash (or at least one of the two platforms). If you're already a registered rider, this service is not for you." },
        { q: "What's included in the package price?", a: "One-time fee covering registration support. Base includes ABN/Uber/DoorDash guidance. Premium adds priority rental partner coordination and faster activation." },
        { q: "Do I need a Police Check?", a: "Some delivery platforms may require a Police Check as part of their onboarding. This is arranged directly through the platform (e.g., Uber may charge ~$49 AUD). This fee is NOT included in Just Delivery services and is paid directly to the platform." },
        { q: "What are the driver licence requirements?", a: "For car delivery, some platforms may accept overseas licences under certain conditions. For scooter/motorbike delivery, an Australian driver's licence is typically required. E-bike delivery usually requires no licence." },
        { q: "What are the Premium rental benefits?", a: "Premium users receive priority coordination with our rental partners. A 20% discount may apply subject to provider availability." },
        { q: "What documents do I need?", a: "Valid passport/ID, visa with work rights (VEVO), Australian phone, bank account, and driver's license if using a scooter or car." },
      ],
    },
    contact: {
      title: "Ready to Start?",
      subtitle: "Upload your documents and complete payment",
      form: {
        name: "Full Name",
        email: "Email Address",
        phone: "Phone Number",
        city: "City",
        cityPlaceholder: "Select your city",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Package",
        packagePlaceholder: "Select package",
        documents: "Upload Documents",
        documentsHint: "Upload: Passport/ID, Driver's License, Visa (all in one upload)",
        submit: "Submit & Pay",
        processing: "Processing...",
      },
      paymentSuccess: "Payment Successful!",
      paymentSuccessText: "Thank you! We'll contact you within 24 hours to start your registration.",
      paymentCancelled: "Payment Cancelled",
      paymentCancelledText: "No worries! You can try again when you're ready.",
    },
    footer: {
      tagline: "Your delivery career starts here.",
      copyright: "Just Delivery. All rights reserved.",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Terms of Service",
      websiteDisclaimer: "Website Disclaimer",
      refundPolicy: "Refund Policy",
    },
    ui: {
      guideLink: "New to delivery? Read our complete guide",
      guideSection: {
        title: "New to Food Delivery in Australia?",
        subtitle: "Starting delivery work in Australia can be confusing for new riders. Our complete guide explains the requirements, vehicle options, and the most common mistakes to avoid when signing up for platforms like Uber Eats and DoorDash.",
        cta: "Read the Complete Delivery Guide",
        chooseCity: "Choose Your City",
        sydneyDesc: "Australia's largest market with high demand",
        melbourneDesc: "Food capital with thriving café culture",
        brisbaneDesc: "Sunny climate with growing delivery market",
      },
      liveLeaderboard: "LIVE Leaderboard",
      joinCompetition: "Join the Competition!",
      notifyDescription: "Get notified about weekly challenges and exclusive bonus opportunities",
      enterEmail: "Enter your email",
      rentalPartners: "Rental Partners",
      growCustomerBase: "Grow your customer base",
      newLeadsPerMonth: "New leads per month",
      commissionFees: "Commission fees",
      setupTime: "Setup time",
      documentsSubmitted: "Documents Submitted!",
      thankYouSubmission: "Thank you for your submission.",
      whatHappensNext: "What happens next?",
      reviewDocuments: "We will review your documents within",
      hours24to48: "24-48 hours",
      ifEligible: "If eligible, you will receive",
      paymentInstructionsEmail: "payment instructions via email",
      afterPayment: "After payment confirmation, we'll start your",
      onboardingProcess: "onboarding process",
      additionalInfo: "If we need additional information, we will contact you via your preferred method.",
      backToHome: "Back to Home",
      submitForReview: "Submit your documents for eligibility review",
      disclaimer: "Disclaimer:",
      disclaimerText: "Just Delivery / JD NETWORK PTY LTD (ACN 695 764 071) is not affiliated with, endorsed by, or partnered with Uber Eats or DoorDash. Approval decisions are made solely by the platforms. We provide guidance services only — platform approval is not guaranteed.",
      legalDisclaimer: "(operating as Just Delivery) is an independent onboarding support service and is",
      notAffiliated: "not affiliated with, endorsed by, or partnered with Uber Eats or DoorDash",
      platformApproval: "Platform approval decisions are made solely by the respective platforms.",
      uploadDocuments: "Upload Documents",
      acceptedFormats: "Accepted formats: PDF, JPG, PNG (Max 10MB each)",
      proofOfIdentity: "Proof of Identity (Passport/ID)",
      workRightsVisa: "Work Rights / Visa (VEVO or Visa Grant)",
      driverLicence: "Driver Licence",
      onlyForScooter: "(Only required for scooter/motorbike delivery)",
      uploaded: "Uploaded",
      chooseFile: "Choose file",
      noFileSelected: "No file selected",
      privacyNotice: "Your documents are stored securely and encrypted. We only use them to verify your eligibility and will not share with third parties without your consent.",
      consentAgreements: "Consent & Agreements",
      consentDocumentsText: "I confirm that all documents submitted are <strong>accurate and authentic</strong>. I understand that submitting false documents may result in rejection and legal consequences.",
      consentTermsText: "I confirm that I have read and agree to the",
      andRiderAgreement: "and Rider Service Agreement. I understand that Just Delivery is not affiliated with Uber Eats or DoorDash and platform approval is not guaranteed.",
      consentPrivacyText: "I consent to the processing of my personal data as described in the",
      reviewDocs24h: "We review your documents (24–48h)",
      receiveConfirmation: "You receive eligibility confirmation",
      completePayment: "You complete payment",
      beginOnboarding: "We begin onboarding support",
      processing: "Processing...",
      submitReview: "Submit for Review",
      noPaymentNow: "No payment required now. You will receive payment instructions after document review.",
      basePackage: "Base - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Contact Just Delivery",
      subtitle: "Questions about delivery work in Australia?",
      description: "Whether you are already in Australia or planning to arrive soon, you can contact Just Delivery for information and guidance.",
      description2: "Even if you are not sure how delivery apps work or where to start, feel free to reach out.",
      helpWith: "We are happy to answer questions about:",
      topics: [
        "how food delivery works in Australia",
        "which apps you can work with",
        "documents and requirements",
        "bikes, scooters and legal vehicles",
        "how to start working faster",
        "general questions about delivery work"
      ],
      notRiderYet: "You do not need to be a rider yet.",
      exploring: "If you are curious or just exploring your options, you are welcome to contact us.",
      ourGoal: "Our goal is simply to help people understand how to start delivery work in Australia the right way.",
      formPrompt: "If you have questions, fill out the form below and our team will get back to you.",
      form: {
        firstName: "First Name",
        lastName: "Last Name",
        email: "Email Address",
        country: "Country",
        countryPlaceholder: "Select your country",
        city: "City",
        cityPlaceholder: "Select your city",
        interest: "What are you interested in?",
        interestPlaceholder: "Select an option",
        interestOptions: [
          "Delivery work information",
          "Rider onboarding",
          "Vehicle advice",
          "General questions"
        ],
        message: "Message",
        messagePlaceholder: "Write your question or message here...",
        submit: "Send Message",
        sending: "Sending..."
      },
      successTitle: "Message sent successfully.",
      successMessage: "Our team will contact you soon."
    },
  },
  it: {
    nav: {
      packages: "Pacchetti",
      howItWorks: "Come Funziona",
      leaderboard: "Classifica",
      info: "Info",
      forShops: "Per i Negozi",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "SOLO per NUOVI rider — non ancora registrati su Uber Eats o DoorDash (o almeno una delle due)",
      newRidersShort: "Solo nuovi rider — Non ancora registrati su Uber Eats/DoorDash",
      newRidersImportant: "Importante: Questo servizio è SOLO per nuovi rider non ancora registrati su Uber Eats o DoorDash (o almeno una delle due piattaforme)",
      premiumOnly: "Solo Membri Premium",
      leaderboardPremium: "Per partecipare alla classifica e vincere bonus in denaro, devi aver acquistato il pacchetto Premium",
    },
    hero: {
      headline: "Inizia la Tua Carriera di Delivery in Australia",
      subheadline: "Supporto completo per Uber Eats e DoorDash. Registrati, trova un veicolo e inizia a guadagnare.",
      cta1: "Inizia Ora",
      cta2: "Vedi Pacchetti",
      badge: "Nessun Rischio • Facile Inizio • Bonus Reali",
    },
    packages: {
      title: "Scegli il Tuo Pacchetto",
      subtitle: "Pagamento unico. Nessun costo nascosto. Supporto completo.",
      base: {
        name: "Base",
        price: "$49",
        description: "Perfetto per chi vuole iniziare da solo",
        features: [
          "Preparazione domanda ABN (pronta per l'invio finale)",
          "Guida passo-passo per registrazione Uber/DoorDash",
          "Supporto via email",
          "Bonus referral incluso",
        ],
        cta: "Scegli Base",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "Più Popolare",
        description: "Esperienza di servizio completo",
        features: [
          "Tutto del pacchetto Base",
          "Coordinamento diretto con i nostri partner di noleggio",
          "Prepariamo e inoltriamo i tuoi documenti al negozio per velocizzare il setup del veicolo",
          "Ti aiutiamo a ridurre i ritardi di onboarding per iniziare prima a consegnare",
          "Supporto prioritario WhatsApp",
        ],
        highlighted: "Impegno minimo di 4 settimane di attività di consegna quando si usano i negozi partner",
        cta: "Scegli Premium",
      },
    },
    howItWorks: {
      title: "Come Funziona",
      subtitle: "Inizia a consegnare in 4 semplici passi",
      steps: [
        { number: "01", title: "Carica Documenti", description: "Inviaci ID, visto e patente" },
        { number: "02", title: "Scegli Pacchetto", description: "Base ($49) o Premium ($79)" },
        { number: "03", title: "Completa Pagamento", description: "Bonifico sicuro via PayID" },
        { number: "04", title: "Inizia a Guadagnare", description: "Ti guidiamo nella registrazione Uber/DoorDash" },
      ],
    },
    documents: {
      title: "Documenti Richiesti",
      subtitle: "Prima dell'acquisto, inviaci:",
      items: [
        { icon: "id", title: "Prova di Identità", desc: "Passaporto o carta d'identità valida" },
        { icon: "car", title: "Patente di Guida", desc: "Necessaria solo se prevedi di consegnare con scooter o motocicletta. La consegna con e-bike non richiede patente. Per scooter/moto potrebbe essere necessaria la conversione della patente estera." },
        { icon: "file", title: "Visto Valido", desc: "Copia del visto che ti permette di lavorare in Australia" },
      ],
      licenseWarning: {
        title: "Importante: Requisiti Patente per Scooter",
        nsw_vic: "NSW & VIC: Patente moto (classe R o RE) richiesta per guidare QUALSIASI scooter, indipendentemente dalla cilindrata.",
        qld: "QLD: Con la patente auto (classe C) puoi guidare scooter fino a 50cc.",
      },
      licenseTitle: "Come Convertire la Patente Straniera",
      licenseSteps: [
        "Rivolgiti al Transport and Main Roads del tuo stato",
        "Presenta la patente straniera e traduzioni richieste",
        "Completa verifica e test (se richiesti)",
        "Ricevi la patente australiana equivalente",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Competi e Guadagna Extra",
      subtitle: "Ogni Settimana e Mese",
      description: "Competi con altri rider e guadagna soldi extra!",
      comingSoon: "Classifica in Arrivo",
      comingSoonText: "I migliori vinceranno bonus esclusivi!",
      notifyBtn: "Avvisami Quando è Attivo",
      weekly: { title: "Classifica Settimanale", subtitle: "Bonus per i migliori", prize: "Premio Settimanale" },
      monthly: { title: "Classifica Mensile", subtitle: "Premi maggiori", prize: "Premio Mensile" },
      tableHeaders: { rank: "Posizione", rider: "Rider", deliveries: "Consegne", bonus: "Bonus" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Tu?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Diventa Partner",
      subtitle: "Sei un negozio di noleggio?",
      description: "Ti inviamo nuovi clienti — senza impegno.",
      features: ["Lead pre-qualificati", "Nessun costo iniziale", "Programma bonus opzionale", "Comunicazione diretta"],
      cta: "Diventa Partner",
    },
    testimonials: {
      title: "Cosa Dicono i Rider",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Just Delivery ha reso tutto facile!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "Il pacchetto Premium ne è valsa la pena.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Supporto eccellente!", rating: 5 },
      ],
    },
    faq: {
      title: "Domande Frequenti",
      items: [
        { q: "Chi può usare questo servizio?", a: "Questo servizio è SOLO per NUOVI rider che non sono ancora registrati su Uber Eats o DoorDash (o almeno una delle due piattaforme). Se sei già un rider registrato, questo servizio non fa per te." },
        { q: "Cosa include il prezzo?", a: "Pagamento unico per supporto registrazione. Base include ABN/Uber/DoorDash. Premium aggiunge coordinamento prioritario partner noleggio e attivazione più veloce." },
        { q: "Quali sono i vantaggi Premium per il noleggio?", a: "Gli utenti Premium ricevono coordinamento prioritario con i nostri partner di noleggio. Gli incentivi possono variare in base alla disponibilità." },
        { q: "Come funziona la Classifica?", a: "I rider con pacchetto Premium competono per numero di consegne. I migliori vincono bonus settimanali e mensili. Nota: la partecipazione alla classifica richiede il pacchetto Premium." },
        { q: "Quali documenti servono?", a: "Passaporto/ID, telefono australiano, conto bancario, patente per noleggio veicoli." },
      ],
    },
    contact: {
      title: "Pronto per Iniziare?",
      subtitle: "Carica i documenti e completa il pagamento",
      form: {
        name: "Nome Completo",
        email: "Email",
        phone: "Telefono",
        city: "Città",
        cityPlaceholder: "Seleziona città",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Pacchetto",
        packagePlaceholder: "Seleziona pacchetto",
        documents: "Carica Documenti",
        documentsHint: "Carica: Passaporto/ID, Patente, Visto (tutto insieme)",
        submit: "Invia e Paga",
        processing: "Elaborazione...",
      },
      paymentSuccess: "Pagamento Completato!",
      paymentSuccessText: "Grazie! Ti contatteremo entro 24 ore.",
      paymentCancelled: "Pagamento Annullato",
      paymentCancelledText: "Nessun problema! Puoi riprovare quando vuoi.",
    },
    footer: {
      tagline: "La tua carriera di delivery inizia qui.",
      copyright: "Just Delivery. Tutti i diritti riservati.",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Termini di Servizio",
      websiteDisclaimer: "Disclaimer del Sito",
      refundPolicy: "Politica di Rimborso",
    },
    ui: {
      guideLink: "Nuovo nel delivery? Leggi la nostra guida completa",
      guideSection: {
        title: "Nuovo nelle Consegne in Australia?",
        subtitle: "Iniziare a lavorare nelle consegne in Australia può essere complicato per i nuovi rider. La nostra guida completa spiega i requisiti, le opzioni di veicolo e gli errori più comuni da evitare quando ci si iscrive a piattaforme come Uber Eats e DoorDash.",
        cta: "Leggi la Guida Completa",
        chooseCity: "Scegli la Tua Città",
        sydneyDesc: "Il mercato più grande d'Australia con alta domanda",
        melbourneDesc: "Capitale gastronomica con cultura dei caffè",
        brisbaneDesc: "Clima soleggiato e mercato in crescita",
      },
      liveLeaderboard: "Classifica LIVE",
      joinCompetition: "Unisciti alla Competizione!",
      notifyDescription: "Ricevi notifiche sulle sfide settimanali e bonus esclusivi",
      enterEmail: "Inserisci la tua email",
      rentalPartners: "Partner Noleggio",
      growCustomerBase: "Fai crescere i tuoi clienti",
      newLeadsPerMonth: "Nuovi lead al mese",
      commissionFees: "Commissioni",
      setupTime: "Tempo di configurazione",
      documentsSubmitted: "Documenti Inviati!",
      thankYouSubmission: "Grazie per l'invio.",
      whatHappensNext: "Cosa succede adesso?",
      reviewDocuments: "Esamineremo i tuoi documenti entro",
      hours24to48: "24-48 ore",
      ifEligible: "Se idoneo, riceverai",
      paymentInstructionsEmail: "istruzioni di pagamento via email",
      afterPayment: "Dopo la conferma del pagamento, inizieremo il tuo",
      onboardingProcess: "processo di onboarding",
      additionalInfo: "Se avremo bisogno di informazioni aggiuntive, ti contatteremo tramite il metodo preferito.",
      backToHome: "Torna alla Home",
      submitForReview: "Invia i tuoi documenti per la verifica di idoneità",
      disclaimer: "Avviso:",
      disclaimerText: "Just Delivery / JD NETWORK PTY LTD (ACN 695 764 071) non è affiliato, approvato o partner di Uber Eats o DoorDash. Le decisioni di approvazione sono prese esclusivamente dalle piattaforme. Forniamo solo servizi di consulenza — l'approvazione non è garantita.",
      legalDisclaimer: "(operante come Just Delivery) è un servizio di supporto indipendente ed è",
      notAffiliated: "non affiliato, approvato o partner di Uber Eats o DoorDash",
      platformApproval: "Le decisioni di approvazione sono prese esclusivamente dalle rispettive piattaforme.",
      uploadDocuments: "Carica Documenti",
      acceptedFormats: "Formati accettati: PDF, JPG, PNG (Max 10MB ciascuno)",
      proofOfIdentity: "Prova di Identità (Passaporto/ID)",
      workRightsVisa: "Diritti di Lavoro / Visto (VEVO o Visa Grant)",
      driverLicence: "Patente di Guida",
      onlyForScooter: "(Solo per consegne con scooter/moto)",
      uploaded: "Caricato",
      chooseFile: "Scegli file",
      noFileSelected: "Nessun file selezionato",
      privacyNotice: "I tuoi documenti sono conservati in modo sicuro e crittografato. Li usiamo solo per verificare la tua idoneità e non li condivideremo con terzi senza il tuo consenso.",
      consentAgreements: "Consenso e Accordi",
      consentDocumentsText: "Confermo che tutti i documenti inviati sono <strong>accurati e autentici</strong>. Comprendo che l'invio di documenti falsi può comportare il rifiuto e conseguenze legali.",
      consentTermsText: "Confermo di aver letto e accettato i",
      andRiderAgreement: "e l'Accordo di Servizio Rider. Comprendo che Just Delivery non è affiliato con Uber Eats o DoorDash e l'approvazione non è garantita.",
      consentPrivacyText: "Acconsento al trattamento dei miei dati personali come descritto nella",
      reviewDocs24h: "Esaminiamo i tuoi documenti (24-48h)",
      receiveConfirmation: "Ricevi la conferma di idoneità",
      completePayment: "Completi il pagamento",
      beginOnboarding: "Iniziamo il supporto onboarding",
      processing: "Elaborazione...",
      submitReview: "Invia per Revisione",
      noPaymentNow: "Nessun pagamento richiesto ora. Riceverai le istruzioni di pagamento dopo la revisione dei documenti.",
      basePackage: "Base - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Contatta Just Delivery",
      subtitle: "Domande sul lavoro di consegna in Australia?",
      description: "Che tu sia già in Australia o stia pianificando di arrivare presto, puoi contattare Just Delivery per informazioni e assistenza.",
      description2: "Anche se non sei sicuro di come funzionano le app di consegna o da dove iniziare, sentiti libero di contattarci.",
      helpWith: "Siamo felici di rispondere a domande su:",
      topics: [
        "come funziona la consegna di cibo in Australia",
        "con quali app puoi lavorare",
        "documenti e requisiti",
        "bici, scooter e veicoli legali",
        "come iniziare a lavorare più velocemente",
        "domande generali sul lavoro di consegna"
      ],
      notRiderYet: "Non devi essere già un rider.",
      exploring: "Se sei curioso o stai solo esplorando le tue opzioni, sei il benvenuto a contattarci.",
      ourGoal: "Il nostro obiettivo è semplicemente aiutare le persone a capire come iniziare il lavoro di consegna in Australia nel modo giusto.",
      formPrompt: "Se hai domande, compila il modulo qui sotto e il nostro team ti risponderà.",
      form: {
        firstName: "Nome",
        lastName: "Cognome",
        email: "Indirizzo Email",
        country: "Paese",
        countryPlaceholder: "Seleziona il tuo paese",
        city: "Città",
        cityPlaceholder: "Seleziona la tua città",
        interest: "Cosa ti interessa?",
        interestPlaceholder: "Seleziona un'opzione",
        interestOptions: [
          "Informazioni sul lavoro di consegna",
          "Onboarding rider",
          "Consigli sui veicoli",
          "Domande generali"
        ],
        message: "Messaggio",
        messagePlaceholder: "Scrivi la tua domanda o messaggio qui...",
        submit: "Invia Messaggio",
        sending: "Invio in corso..."
      },
      successTitle: "Messaggio inviato con successo.",
      successMessage: "Il nostro team ti contatterà presto."
    },
  },
  es: {
    nav: {
      packages: "Paquetes",
      howItWorks: "Cómo Funciona",
      leaderboard: "Clasificación",
      info: "Info",
      forShops: "Para Tiendas",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "SOLO para riders NUEVOS — aún no registrados en Uber Eats o DoorDash (o al menos uno de ellos)",
      newRidersShort: "Solo riders nuevos — No registrados en Uber Eats/DoorDash",
      newRidersImportant: "Importante: Este servicio es SOLO para riders nuevos no registrados en Uber Eats o DoorDash (o al menos una de las dos plataformas)",
      premiumOnly: "Solo Miembros Premium",
      leaderboardPremium: "Para participar en la clasificación y ganar bonos en efectivo, debes haber comprado el paquete Premium",
    },
    hero: {
      headline: "Comienza Tu Carrera de Delivery en Australia",
      subheadline: "Soporte completo para Uber Eats y DoorDash. Regístrate, encuentra vehículo y gana.",
      cta1: "Empezar",
      cta2: "Ver Paquetes",
      badge: "Sin Riesgo • Fácil Inicio • Bonos Reales",
    },
    packages: {
      title: "Elige Tu Paquete",
      subtitle: "Pago único. Sin costos ocultos. Soporte completo.",
      base: {
        name: "Base",
        price: "$49",
        description: "Perfecto para emprendedores",
        features: [
          "Preparación de solicitud ABN (lista para envío final)",
          "Guía paso a paso para registro Uber/DoorDash",
          "Soporte por email",
          "Bono de referido incluido",
        ],
        cta: "Elegir Base",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "Más Popular",
        description: "Experiencia de servicio completo",
        features: [
          "Todo del paquete Base",
          "Coordinación directa con nuestros socios de alquiler",
          "Preparamos y enviamos tus documentos a la tienda para acelerar la configuración del vehículo",
          "Te ayudamos a reducir retrasos de onboarding para empezar a entregar más rápido",
          "Soporte prioritario WhatsApp",
        ],
        highlighted: "Compromiso mínimo de 4 semanas de actividad de entrega al usar tiendas asociadas",
        cta: "Elegir Premium",
      },
    },
    howItWorks: {
      title: "Cómo Funciona",
      subtitle: "Empieza a entregar en 4 pasos",
      steps: [
        { number: "01", title: "Sube Documentos", description: "Envíanos ID, visa y licencia" },
        { number: "02", title: "Elige Paquete", description: "Base ($49) o Premium ($79)" },
        { number: "03", title: "Completa Pago", description: "Transferencia segura via PayID" },
        { number: "04", title: "Empieza a Ganar", description: "Te registramos en Uber/DoorDash" },
      ],
    },
    documents: {
      title: "Documentos Requeridos",
      subtitle: "Antes de comprar, envíanos:",
      items: [
        { icon: "id", title: "Prueba de Identidad", desc: "Pasaporte o ID válido" },
        { icon: "car", title: "Licencia de Conducir", desc: "Solo requerida si planeas entregar usando scooter o motocicleta. La entrega con e-bike no requiere licencia. La entrega con scooter/moto puede requerir conversión de licencia extranjera." },
        { icon: "file", title: "Visa Válida", desc: "Copia de visa que te permite trabajar en Australia" },
      ],
      licenseWarning: {
        title: "Importante: Requisitos de Licencia para Scooter",
        nsw_vic: "NSW & VIC: Licencia de moto (clase R o RE) requerida para conducir CUALQUIER scooter, sin importar el tamaño del motor.",
        qld: "QLD: Con licencia de auto (clase C) puedes conducir scooters hasta 50cc.",
      },
      licenseTitle: "Cómo Convertir Licencia Extranjera",
      licenseSteps: [
        "Contacta Transport and Main Roads de tu estado",
        "Presenta licencia extranjera y traducciones",
        "Completa verificación y tests (si es necesario)",
        "Recibe licencia australiana equivalente",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Compite y Gana Extra",
      subtitle: "Cada Semana y Mes",
      description: "¡Compite con otros riders y gana dinero extra!",
      comingSoon: "Clasificación Próximamente",
      comingSoonText: "¡Los mejores ganarán bonos exclusivos!",
      notifyBtn: "Notificarme",
      weekly: { title: "Clasificación Semanal", subtitle: "Bono para los mejores", prize: "Premio Semanal" },
      monthly: { title: "Clasificación Mensual", subtitle: "Mayores recompensas", prize: "Premio Mensual" },
      tableHeaders: { rank: "Posición", rider: "Rider", deliveries: "Entregas", bonus: "Bono" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "¿Tú?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Asóciate",
      subtitle: "¿Eres una tienda de alquiler?",
      description: "Te enviamos clientes — sin compromiso.",
      features: ["Leads pre-calificados", "Sin costos iniciales", "Programa de bonos opcional", "Comunicación directa"],
      cta: "Ser Socio",
    },
    testimonials: {
      title: "Lo Que Dicen Los Riders",
      items: [
        { name: "Marco R.", location: "Sydney", text: "¡Just Delivery hizo todo fácil!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "El Premium valió la pena.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "¡Soporte excelente!", rating: 5 },
      ],
    },
    faq: {
      title: "Preguntas Frecuentes",
      items: [
        { q: "¿Quién puede usar este servicio?", a: "Este servicio es SOLO para NUEVOS riders que aún no están registrados en Uber Eats o DoorDash (o al menos una de las dos plataformas). Si ya eres un rider registrado, este servicio no es para ti." },
        { q: "¿Qué incluye el precio?", a: "Pago único para soporte de registro. Base incluye ABN/Uber/DoorDash. Premium agrega coordinación prioritaria con socios de alquiler y activación más rápida." },
        { q: "¿Cuáles son los beneficios Premium de alquiler?", a: "Los usuarios Premium reciben coordinación prioritaria con nuestros socios de alquiler. Los incentivos pueden variar según disponibilidad." },
        { q: "¿Cómo funciona la Clasificación?", a: "Riders con paquete Premium compiten por entregas. Los mejores ganan bonos semanales y mensuales. Nota: participar en la clasificación requiere paquete Premium." },
        { q: "¿Qué documentos necesito?", a: "Pasaporte/ID, teléfono australiano, cuenta bancaria, licencia para alquiler." },
      ],
    },
    contact: {
      title: "¿Listo Para Empezar?",
      subtitle: "Sube documentos y completa el pago",
      form: {
        name: "Nombre Completo",
        email: "Email",
        phone: "Teléfono",
        city: "Ciudad",
        cityPlaceholder: "Selecciona ciudad",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Paquete",
        packagePlaceholder: "Selecciona paquete",
        documents: "Subir Documentos",
        documentsHint: "Sube: Pasaporte/ID, Licencia, Visa (todo junto)",
        submit: "Enviar y Pagar",
        processing: "Procesando...",
      },
      paymentSuccess: "¡Pago Exitoso!",
      paymentSuccessText: "¡Gracias! Te contactaremos en 24 horas.",
      paymentCancelled: "Pago Cancelado",
      paymentCancelledText: "¡Sin problema! Puedes intentar cuando quieras.",
    },
    footer: {
      tagline: "Tu carrera de delivery comienza aquí.",
      copyright: "Just Delivery. Todos los derechos reservados.",
      privacyPolicy: "Política de Privacidad",
      termsOfService: "Términos de Servicio",
      websiteDisclaimer: "Descargo de Responsabilidad",
      refundPolicy: "Política de Reembolso",
    },
    ui: {
      guideLink: "¿Nuevo en delivery? Lee nuestra guía completa",
      guideSection: {
        title: "¿Nuevo en Entregas de Comida en Australia?",
        subtitle: "Empezar a trabajar en entregas en Australia puede ser confuso para los nuevos repartidores. Nuestra guía completa explica los requisitos, opciones de vehículos y los errores más comunes a evitar al registrarse en plataformas como Uber Eats y DoorDash.",
        cta: "Lee la Guía Completa de Entregas",
        chooseCity: "Elige Tu Ciudad",
        sydneyDesc: "El mercado más grande de Australia con alta demanda",
        melbourneDesc: "Capital gastronómica con cultura de cafés",
        brisbaneDesc: "Clima soleado con mercado en crecimiento",
      },
      liveLeaderboard: "Clasificación EN VIVO",
      joinCompetition: "¡Únete a la Competencia!",
      notifyDescription: "Recibe notificaciones sobre desafíos semanales y oportunidades de bonos exclusivos",
      enterEmail: "Ingresa tu email",
      rentalPartners: "Socios de Alquiler",
      growCustomerBase: "Haz crecer tu clientela",
      newLeadsPerMonth: "Nuevos leads por mes",
      commissionFees: "Comisiones",
      setupTime: "Tiempo de configuración",
      documentsSubmitted: "¡Documentos Enviados!",
      thankYouSubmission: "Gracias por tu envío.",
      whatHappensNext: "¿Qué sigue?",
      reviewDocuments: "Revisaremos tus documentos en",
      hours24to48: "24-48 horas",
      ifEligible: "Si eres elegible, recibirás",
      paymentInstructionsEmail: "instrucciones de pago por email",
      afterPayment: "Después de confirmar el pago, comenzaremos tu",
      onboardingProcess: "proceso de incorporación",
      additionalInfo: "Si necesitamos información adicional, te contactaremos por tu método preferido.",
      backToHome: "Volver al Inicio",
      submitForReview: "Envía tus documentos para revisión de elegibilidad",
      disclaimer: "Descargo:",
      disclaimerText: "Just Delivery / JD NETWORK PTY LTD (ACN 695 764 071) no está afiliado, respaldado o asociado con Uber Eats o DoorDash. Las decisiones de aprobación son tomadas únicamente por las plataformas. Solo proporcionamos servicios de consultoría — la aprobación no está garantizada.",
      legalDisclaimer: "(operando como Just Delivery) es un servicio de soporte independiente y",
      notAffiliated: "no está afiliado, respaldado o asociado con Uber Eats o DoorDash",
      platformApproval: "Las decisiones de aprobación son tomadas únicamente por las respectivas plataformas.",
      uploadDocuments: "Subir Documentos",
      acceptedFormats: "Formatos aceptados: PDF, JPG, PNG (Máx 10MB cada uno)",
      proofOfIdentity: "Prueba de Identidad (Pasaporte/ID)",
      workRightsVisa: "Derechos de Trabajo / Visa (VEVO o Visa Grant)",
      driverLicence: "Licencia de Conducir",
      onlyForScooter: "(Solo requerida para entrega con scooter/moto)",
      uploaded: "Subido",
      chooseFile: "Elegir archivo",
      noFileSelected: "Ningún archivo seleccionado",
      privacyNotice: "Tus documentos se almacenan de forma segura y encriptada. Solo los usamos para verificar tu elegibilidad y no los compartiremos con terceros sin tu consentimiento.",
      consentAgreements: "Consentimiento y Acuerdos",
      consentDocumentsText: "Confirmo que todos los documentos enviados son <strong>precisos y auténticos</strong>. Entiendo que enviar documentos falsos puede resultar en rechazo y consecuencias legales.",
      consentTermsText: "Confirmo que he leído y acepto los",
      andRiderAgreement: "y el Acuerdo de Servicio de Rider. Entiendo que Just Delivery no está afiliado con Uber Eats o DoorDash y la aprobación no está garantizada.",
      consentPrivacyText: "Consiento al procesamiento de mis datos personales como se describe en la",
      reviewDocs24h: "Revisamos tus documentos (24-48h)",
      receiveConfirmation: "Recibes confirmación de elegibilidad",
      completePayment: "Completas el pago",
      beginOnboarding: "Comenzamos el soporte de incorporación",
      processing: "Procesando...",
      submitReview: "Enviar para Revisión",
      noPaymentNow: "No se requiere pago ahora. Recibirás instrucciones de pago después de la revisión de documentos.",
      basePackage: "Base - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Contacta Just Delivery",
      subtitle: "¿Preguntas sobre trabajo de reparto en Australia?",
      description: "Ya sea que estés en Australia o planees llegar pronto, puedes contactar a Just Delivery para información y orientación.",
      description2: "Incluso si no estás seguro de cómo funcionan las apps de reparto o por dónde empezar, no dudes en contactarnos.",
      helpWith: "Estamos felices de responder preguntas sobre:",
      topics: [
        "cómo funciona el reparto de comida en Australia",
        "con qué apps puedes trabajar",
        "documentos y requisitos",
        "bicicletas, scooters y vehículos legales",
        "cómo empezar a trabajar más rápido",
        "preguntas generales sobre trabajo de reparto"
      ],
      notRiderYet: "No necesitas ser rider todavía.",
      exploring: "Si tienes curiosidad o solo estás explorando tus opciones, eres bienvenido a contactarnos.",
      ourGoal: "Nuestro objetivo es simplemente ayudar a las personas a entender cómo comenzar el trabajo de reparto en Australia de la manera correcta.",
      formPrompt: "Si tienes preguntas, completa el formulario a continuación y nuestro equipo te responderá.",
      form: {
        firstName: "Nombre",
        lastName: "Apellido",
        email: "Correo Electrónico",
        country: "País",
        countryPlaceholder: "Selecciona tu país",
        city: "Ciudad",
        cityPlaceholder: "Selecciona tu ciudad",
        interest: "¿Qué te interesa?",
        interestPlaceholder: "Selecciona una opción",
        interestOptions: [
          "Información sobre trabajo de reparto",
          "Incorporación de rider",
          "Consejos sobre vehículos",
          "Preguntas generales"
        ],
        message: "Mensaje",
        messagePlaceholder: "Escribe tu pregunta o mensaje aquí...",
        submit: "Enviar Mensaje",
        sending: "Enviando..."
      },
      successTitle: "Mensaje enviado con éxito.",
      successMessage: "Nuestro equipo te contactará pronto."
    },
  },
  fr: {
    nav: {
      packages: "Forfaits",
      howItWorks: "Comment Ça Marche",
      leaderboard: "Classement",
      info: "Info",
      forShops: "Pour Boutiques",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "UNIQUEMENT pour les NOUVEAUX livreurs — pas encore inscrits sur Uber Eats ou DoorDash (ou au moins l'un des deux)",
      newRidersShort: "Nouveaux livreurs uniquement — Non inscrits sur Uber Eats/DoorDash",
      newRidersImportant: "Important: Ce service est UNIQUEMENT pour les nouveaux livreurs non inscrits sur Uber Eats ou DoorDash (ou au moins l'une des deux plateformes)",
      premiumOnly: "Membres Premium Uniquement",
      leaderboardPremium: "Pour participer au classement et gagner des bonus en espèces, vous devez avoir acheté le forfait Premium",
    },
    hero: {
      headline: "Lancez Votre Carrière de Livreur en Australie",
      subheadline: "Support complet pour Uber Eats et DoorDash. Inscrivez-vous, trouvez un véhicule et gagnez.",
      cta1: "Commencer",
      cta2: "Voir Forfaits",
      badge: "Sans Risque • Démarrage Facile • Vrais Bonus",
    },
    packages: {
      title: "Choisissez Votre Forfait",
      subtitle: "Paiement unique. Pas de frais cachés. Support complet.",
      base: {
        name: "Base",
        price: "$49",
        description: "Parfait pour les autonomes",
        features: [
          "Préparation de la demande ABN (prête pour soumission finale)",
          "Guide étape par étape pour inscription Uber/DoorDash",
          "Support par email",
          "Bonus de parrainage inclus",
        ],
        cta: "Choisir Base",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "Le Plus Populaire",
        description: "Expérience service complet",
        features: [
          "Tout le forfait Base",
          "Coordination directe avec nos partenaires de location",
          "Nous préparons et transmettons vos documents à la boutique pour accélérer la mise en place du véhicule",
          "Nous aidons à réduire les délais d'intégration pour commencer à livrer plus rapidement",
          "Support WhatsApp prioritaire",
        ],
        highlighted: "Engagement minimum de 4 semaines d'activité de livraison lors de l'utilisation des boutiques partenaires",
        cta: "Choisir Premium",
      },
    },
    howItWorks: {
      title: "Comment Ça Marche",
      subtitle: "Commencez à livrer en 4 étapes",
      steps: [
        { number: "01", title: "Télécharger Documents", description: "Envoyez ID, visa et permis" },
        { number: "02", title: "Choisir Forfait", description: "Base ($49) ou Premium ($79)" },
        { number: "03", title: "Compléter Paiement", description: "Virement sécurisé via PayID" },
        { number: "04", title: "Commencer à Gagner", description: "On vous inscrit sur Uber/DoorDash" },
      ],
    },
    documents: {
      title: "Documents Requis",
      subtitle: "Avant l'achat, envoyez-nous:",
      items: [
        { icon: "id", title: "Preuve d'Identité", desc: "Passeport ou carte d'identité valide" },
        { icon: "car", title: "Permis de Conduire", desc: "Uniquement requis si vous prévoyez de livrer avec un scooter ou une moto. La livraison en e-bike ne nécessite pas de permis. La livraison scooter/moto peut nécessiter la conversion d'un permis étranger." },
        { icon: "file", title: "Visa Valide", desc: "Copie du visa vous permettant de travailler en Australie" },
      ],
      licenseWarning: {
        title: "Important: Exigences de Permis pour Scooter",
        nsw_vic: "NSW & VIC: Permis moto (classe R ou RE) requis pour conduire TOUT scooter, quelle que soit la cylindrée.",
        qld: "QLD: Avec un permis voiture (classe C) vous pouvez conduire des scooters jusqu'à 50cc.",
      },
      licenseTitle: "Comment Convertir un Permis Étranger",
      licenseSteps: [
        "Contactez Transport and Main Roads de votre état",
        "Présentez permis étranger et traductions",
        "Complétez vérification et tests (si requis)",
        "Recevez permis australien équivalent",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Compétition & Gains Extra",
      subtitle: "Chaque Semaine et Mois",
      description: "Affrontez d'autres livreurs et gagnez de l'argent extra!",
      comingSoon: "Classement Bientôt",
      comingSoonText: "Les meilleurs gagneront des bonus exclusifs!",
      notifyBtn: "Me Notifier",
      weekly: { title: "Classement Hebdomadaire", subtitle: "Bonus pour les meilleurs", prize: "Prix Hebdomadaire" },
      monthly: { title: "Classement Mensuel", subtitle: "Récompenses plus élevées", prize: "Prix Mensuel" },
      tableHeaders: { rank: "Rang", rider: "Livreur", deliveries: "Livraisons", bonus: "Bonus" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Vous?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Devenez Partenaire",
      subtitle: "Vous êtes une boutique de location?",
      description: "On vous envoie de nouveaux clients — sans engagement.",
      features: ["Leads pré-qualifiés", "Pas de frais initiaux", "Programme bonus optionnel", "Communication directe"],
      cta: "Devenir Partenaire",
    },
    testimonials: {
      title: "Ce Que Disent Les Livreurs",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Just Delivery a tout rendu facile!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "Le Premium en valait la peine.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Support excellent!", rating: 5 },
      ],
    },
    faq: {
      title: "Questions Fréquentes",
      items: [
        { q: "Qui peut utiliser ce service?", a: "Ce service est UNIQUEMENT pour les NOUVEAUX livreurs qui ne sont pas encore inscrits sur Uber Eats ou DoorDash (ou au moins l'une des deux plateformes). Si vous êtes déjà un livreur inscrit, ce service n'est pas pour vous." },
        { q: "Qu'inclut le prix?", a: "Paiement unique pour support inscription. Base inclut ABN/Uber/DoorDash. Premium ajoute coordination prioritaire partenaires location et activation plus rapide." },
        { q: "Quels sont les avantages Premium location?", a: "Les utilisateurs Premium bénéficient d'une coordination prioritaire avec nos partenaires de location. Les incentives peuvent varier selon disponibilité." },
        { q: "Comment fonctionne le Classement?", a: "Livreurs avec forfait Premium compètent par livraisons. Les meilleurs gagnent des bonus hebdomadaires et mensuels. Note: la participation au classement nécessite le forfait Premium." },
        { q: "Quels documents nécessaires?", a: "Passeport/ID, téléphone australien, compte bancaire, permis pour location." },
      ],
    },
    contact: {
      title: "Prêt à Commencer?",
      subtitle: "Téléchargez documents et complétez le paiement",
      form: {
        name: "Nom Complet",
        email: "Email",
        phone: "Téléphone",
        city: "Ville",
        cityPlaceholder: "Sélectionnez ville",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Forfait",
        packagePlaceholder: "Sélectionnez forfait",
        documents: "Télécharger Documents",
        documentsHint: "Téléchargez: Passeport/ID, Permis, Visa (tout ensemble)",
        submit: "Envoyer et Payer",
        processing: "Traitement...",
      },
      paymentSuccess: "Paiement Réussi!",
      paymentSuccessText: "Merci! On vous contactera sous 24h.",
      paymentCancelled: "Paiement Annulé",
      paymentCancelledText: "Pas de souci! Vous pouvez réessayer.",
    },
    footer: {
      tagline: "Votre carrière de livreur commence ici.",
      copyright: "Just Delivery. Tous droits réservés.",
      privacyPolicy: "Politique de Confidentialité",
      termsOfService: "Conditions d'Utilisation",
      websiteDisclaimer: "Avertissement",
      refundPolicy: "Politique de Remboursement",
    },
    ui: {
      guideLink: "Nouveau dans la livraison? Lisez notre guide complet",
      guideSection: {
        title: "Nouveau dans la Livraison de Repas en Australie?",
        subtitle: "Commencer à travailler dans la livraison en Australie peut être déroutant pour les nouveaux livreurs. Notre guide complet explique les exigences, les options de véhicules et les erreurs les plus courantes à éviter lors de l'inscription sur des plateformes comme Uber Eats et DoorDash.",
        cta: "Lire le Guide Complet de Livraison",
        chooseCity: "Choisissez Votre Ville",
        sydneyDesc: "Le plus grand marché d'Australie avec forte demande",
        melbourneDesc: "Capitale gastronomique avec culture café",
        brisbaneDesc: "Climat ensoleillé et marché en croissance",
      },
      liveLeaderboard: "Classement EN DIRECT",
      joinCompetition: "Rejoignez la Compétition!",
      notifyDescription: "Recevez des notifications sur les défis hebdomadaires et les opportunités de bonus exclusifs",
      enterEmail: "Entrez votre email",
      rentalPartners: "Partenaires de Location",
      growCustomerBase: "Développez votre clientèle",
      newLeadsPerMonth: "Nouveaux leads par mois",
      commissionFees: "Frais de commission",
      setupTime: "Temps de configuration",
      documentsSubmitted: "Documents Soumis!",
      thankYouSubmission: "Merci pour votre soumission.",
      whatHappensNext: "Que se passe-t-il ensuite?",
      reviewDocuments: "Nous examinerons vos documents dans",
      hours24to48: "24-48 heures",
      ifEligible: "Si éligible, vous recevrez",
      paymentInstructionsEmail: "les instructions de paiement par email",
      afterPayment: "Après confirmation du paiement, nous commencerons votre",
      onboardingProcess: "processus d'intégration",
      additionalInfo: "Si nous avons besoin d'informations supplémentaires, nous vous contacterons par votre méthode préférée.",
      backToHome: "Retour à l'Accueil",
      submitForReview: "Soumettez vos documents pour vérification d'éligibilité",
      disclaimer: "Avertissement:",
      disclaimerText: "Just Delivery / JD NETWORK PTY LTD (ACN 695 764 071) n'est pas affilié, approuvé ou partenaire d'Uber Eats ou DoorDash. Les décisions d'approbation sont prises uniquement par les plateformes.",
      legalDisclaimer: "(opérant sous le nom Just Delivery) est un service de support indépendant et",
      notAffiliated: "n'est pas affilié, approuvé ou partenaire d'Uber Eats ou DoorDash",
      platformApproval: "Les décisions d'approbation sont prises uniquement par les plateformes respectives.",
      uploadDocuments: "Télécharger les Documents",
      acceptedFormats: "Formats acceptés: PDF, JPG, PNG (Max 10MB chacun)",
      proofOfIdentity: "Preuve d'Identité (Passeport/ID)",
      workRightsVisa: "Droits de Travail / Visa (VEVO ou Visa Grant)",
      driverLicence: "Permis de Conduire",
      onlyForScooter: "(Requis uniquement pour livraison scooter/moto)",
      uploaded: "Téléchargé",
      chooseFile: "Choisir un fichier",
      noFileSelected: "Aucun fichier sélectionné",
      privacyNotice: "Vos documents sont stockés de manière sécurisée et cryptée. Nous les utilisons uniquement pour vérifier votre éligibilité.",
      consentAgreements: "Consentement et Accords",
      consentDocumentsText: "Je confirme que tous les documents soumis sont <strong>exacts et authentiques</strong>.",
      consentTermsText: "Je confirme avoir lu et accepté les",
      andRiderAgreement: "et l'Accord de Service Livreur.",
      consentPrivacyText: "Je consens au traitement de mes données personnelles comme décrit dans la",
      reviewDocs24h: "Nous examinons vos documents (24-48h)",
      receiveConfirmation: "Vous recevez la confirmation d'éligibilité",
      completePayment: "Vous complétez le paiement",
      beginOnboarding: "Nous commençons le support d'intégration",
      processing: "Traitement...",
      submitReview: "Soumettre pour Révision",
      noPaymentNow: "Aucun paiement requis maintenant. Vous recevrez les instructions de paiement après la révision des documents.",
      basePackage: "Base - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Contactez Just Delivery",
      subtitle: "Des questions sur le travail de livraison en Australie?",
      description: "Que vous soyez déjà en Australie ou que vous prévoyiez d'arriver bientôt, vous pouvez contacter Just Delivery pour des informations et des conseils.",
      description2: "Même si vous n'êtes pas sûr du fonctionnement des applications de livraison ou par où commencer, n'hésitez pas à nous contacter.",
      helpWith: "Nous sommes heureux de répondre aux questions sur:",
      topics: [
        "comment fonctionne la livraison de nourriture en Australie",
        "avec quelles applications vous pouvez travailler",
        "documents et exigences",
        "vélos, scooters et véhicules légaux",
        "comment commencer à travailler plus vite",
        "questions générales sur le travail de livraison"
      ],
      notRiderYet: "Vous n'avez pas besoin d'être livreur.",
      exploring: "Si vous êtes curieux ou explorez vos options, vous êtes bienvenu à nous contacter.",
      ourGoal: "Notre objectif est simplement d'aider les gens à comprendre comment commencer le travail de livraison en Australie de la bonne manière.",
      formPrompt: "Si vous avez des questions, remplissez le formulaire ci-dessous et notre équipe vous répondra.",
      form: {
        firstName: "Prénom",
        lastName: "Nom",
        email: "Adresse Email",
        country: "Pays",
        countryPlaceholder: "Sélectionnez votre pays",
        city: "Ville",
        cityPlaceholder: "Votre ville (optionnel)",
        interest: "Qu'est-ce qui vous intéresse?",
        interestPlaceholder: "Sélectionnez une option",
        interestOptions: [
          "Informations sur le travail de livraison",
          "Intégration livreur",
          "Conseils véhicules",
          "Questions générales"
        ],
        message: "Message",
        messagePlaceholder: "Écrivez votre question ou message ici...",
        submit: "Envoyer le Message",
        sending: "Envoi en cours..."
      },
      successTitle: "Message envoyé avec succès.",
      successMessage: "Notre équipe vous contactera bientôt."
    },
  },
  vi: {
    nav: {
      packages: "Gói dịch vụ",
      howItWorks: "Cách hoạt động",
      leaderboard: "Bảng xếp hạng",
      info: "Thông tin",
      forShops: "Cho cửa hàng",
      faq: "Câu hỏi thường gặp",
    },
    notices: {
      newRidersOnly: "CHỈ dành cho tài xế MỚI — chưa đăng ký trên Uber Eats hoặc DoorDash (hoặc ít nhất một trong hai)",
      newRidersShort: "Chỉ tài xế mới — Chưa đăng ký Uber Eats/DoorDash",
      newRidersImportant: "Quan trọng: Dịch vụ này CHỈ dành cho tài xế mới chưa đăng ký trên Uber Eats hoặc DoorDash (hoặc ít nhất một trong hai nền tảng)",
      premiumOnly: "Chỉ Thành Viên Cao Cấp",
      leaderboardPremium: "Để tham gia bảng xếp hạng và giành tiền thưởng, bạn phải mua gói Cao cấp",
    },
    hero: {
      headline: "Bắt đầu sự nghiệp giao hàng tại Úc",
      subheadline: "Hỗ trợ đăng ký đầy đủ cho Uber Eats & DoorDash. Đăng ký, tìm xe và bắt đầu kiếm tiền — với hướng dẫn chuyên gia.",
      cta1: "Bắt đầu ngay",
      cta2: "Xem gói dịch vụ",
      badge: "Không rủi ro • Dễ bắt đầu • Thưởng thật",
    },
    packages: {
      title: "Chọn gói của bạn",
      subtitle: "Thanh toán một lần. Không phí ẩn. Hỗ trợ đầy đủ.",
      base: {
        name: "Cơ bản",
        price: "$49",
        description: "Hoàn hảo cho người tự khởi nghiệp",
        features: [
          "Chuẩn bị đơn ABN (sẵn sàng nộp cuối cùng)",
          "Hướng dẫn từng bước đăng ký Uber/DoorDash",
          "Hỗ trợ qua email",
          "Bao gồm thưởng giới thiệu",
        ],
        cta: "Chọn Cơ bản",
      },
      premium: {
        name: "Cao cấp",
        price: "$79",
        popular: "Phổ biến nhất",
        description: "Trải nghiệm dịch vụ đầy đủ",
        features: [
          "Tất cả trong gói Cơ bản",
          "Phối hợp trực tiếp với đối tác cho thuê xe tin cậy",
          "Chúng tôi chuẩn bị và chuyển tiếp hồ sơ của bạn đến cửa hàng để đẩy nhanh quá trình lấy xe",
          "Chúng tôi giúp giảm thời gian chờ đợi để bạn bắt đầu giao hàng nhanh hơn",
          "Hỗ trợ WhatsApp ưu tiên",
        ],
        highlighted: "Có thể áp dụng cam kết hoạt động giao hàng tối thiểu 4 tuần khi sử dụng cửa hàng đối tác",
        cta: "Chọn Cao cấp",
      },
    },
    howItWorks: {
      title: "Cách hoạt động",
      subtitle: "Bắt đầu giao hàng trong 4 bước đơn giản",
      steps: [
        { number: "01", title: "Tải lên hồ sơ", description: "Gửi ID, visa và bằng lái" },
        { number: "02", title: "Chọn gói", description: "Cơ bản ($49) hoặc Cao cấp ($79)" },
        { number: "03", title: "Thanh toán", description: "Chuyển khoản an toàn qua PayID" },
        { number: "04", title: "Bắt đầu kiếm tiền", description: "Chúng tôi đăng ký Uber/DoorDash cho bạn" },
      ],
    },
    documents: {
      title: "Hồ sơ cần thiết",
      subtitle: "Trước khi mua, gửi cho chúng tôi:",
      items: [
        { icon: "id", title: "Chứng minh danh tính", desc: "Hộ chiếu hoặc CMND hợp lệ" },
        { icon: "car", title: "Bằng lái xe", desc: "Chỉ cần thiết nếu bạn dự định giao hàng bằng xe tay ga hoặc mô tô. Giao hàng bằng xe đạp điện không cần bằng lái. Giao hàng xe tay ga/mô tô có thể cần chuyển đổi bằng lái nước ngoài." },
        { icon: "file", title: "Visa hợp lệ", desc: "Bản sao visa cho phép làm việc tại Úc" },
      ],
      licenseWarning: {
        title: "Quan trọng: Yêu cầu bằng lái xe tay ga",
        nsw_vic: "NSW & VIC: Cần bằng lái mô tô (hạng R hoặc RE) để lái BẤT KỲ xe tay ga nào, bất kể dung tích.",
        qld: "QLD: Với bằng lái ô tô (hạng C) bạn có thể lái xe tay ga đến 50cc.",
      },
      licenseTitle: "Cách chuyển đổi bằng lái nước ngoài",
      licenseSteps: [
        "Liên hệ Sở Giao thông bang của bạn",
        "Nộp bằng lái nước ngoài và bản dịch",
        "Hoàn thành xác minh và kiểm tra (nếu cần)",
        "Nhận bằng lái Úc tương đương",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Cạnh tranh & Kiếm thêm tiền",
      subtitle: "Mỗi tuần & tháng",
      description: "Cạnh tranh với các tài xế khác và kiếm thêm tiền!",
      comingSoon: "Bảng xếp hạng sắp ra mắt",
      comingSoonText: "Những người xuất sắc nhất sẽ nhận thưởng độc quyền!",
      notifyBtn: "Thông báo cho tôi",
      weekly: { title: "Bảng xếp hạng tuần", subtitle: "Thưởng tuần cho người xuất sắc", prize: "Giải tuần" },
      monthly: { title: "Bảng xếp hạng tháng", subtitle: "Phần thưởng lớn hơn", prize: "Giải tháng" },
      tableHeaders: { rank: "Hạng", rider: "Tài xế", deliveries: "Giao hàng", bonus: "Thưởng" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Bạn?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Hợp tác với chúng tôi",
      subtitle: "Bạn là cửa hàng cho thuê xe?",
      description: "Chúng tôi gửi khách hàng mới cho bạn — không cam kết.",
      features: ["Nhận lead tài xế đã sàng lọc", "Không phí trước", "Chương trình thưởng giới thiệu tùy chọn", "Kênh liên lạc trực tiếp"],
      cta: "Trở thành đối tác",
    },
    testimonials: {
      title: "Tài xế nói gì",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Just Delivery làm mọi thứ dễ dàng!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "Gói Cao cấp rất đáng tiền.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Đội ngũ hỗ trợ tuyệt vời!", rating: 5 },
      ],
    },
    faq: {
      title: "Câu hỏi thường gặp",
      items: [
        { q: "Ai có thể sử dụng dịch vụ này?", a: "Dịch vụ này CHỈ dành cho tài xế MỚI chưa đăng ký trên Uber Eats hoặc DoorDash (hoặc ít nhất một trong hai nền tảng). Nếu bạn đã là tài xế đã đăng ký, dịch vụ này không dành cho bạn." },
        { q: "Giá gói bao gồm gì?", a: "Phí một lần cho hỗ trợ đăng ký. Cơ bản gồm ABN/Uber/DoorDash. Cao cấp thêm phối hợp ưu tiên với đối tác thuê xe và kích hoạt nhanh hơn." },
        { q: "Lợi ích thuê xe Cao cấp là gì?", a: "Người dùng Cao cấp được phối hợp ưu tiên với các đối tác thuê xe của chúng tôi. Ưu đãi có thể thay đổi tùy theo tình trạng." },
        { q: "Bảng xếp hạng hoạt động thế nào?", a: "Tài xế gói Cao cấp cạnh tranh theo số lần giao hàng. Người xuất sắc nhất nhận thưởng tiền mặt. Lưu ý: tham gia bảng xếp hạng cần gói Cao cấp." },
        { q: "Cần hồ sơ gì?", a: "Hộ chiếu/CMND, số điện thoại Úc, tài khoản ngân hàng, bằng lái để thuê xe." },
      ],
    },
    contact: {
      title: "Sẵn sàng bắt đầu?",
      subtitle: "Tải lên hồ sơ và thanh toán",
      form: {
        name: "Họ tên",
        email: "Email",
        phone: "Số điện thoại",
        city: "Thành phố",
        cityPlaceholder: "Chọn thành phố",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Gói",
        packagePlaceholder: "Chọn gói",
        documents: "Tải lên hồ sơ",
        documentsHint: "Tải lên: Hộ chiếu/CMND, Bằng lái, Visa (tất cả cùng lúc)",
        submit: "Gửi & Thanh toán",
        processing: "Đang xử lý...",
      },
      paymentSuccess: "Thanh toán thành công!",
      paymentSuccessText: "Cảm ơn bạn! Chúng tôi sẽ liên hệ trong 24 giờ.",
      paymentCancelled: "Thanh toán bị hủy",
      paymentCancelledText: "Không sao! Bạn có thể thử lại khi sẵn sàng.",
    },
    footer: {
      tagline: "Sự nghiệp giao hàng của bạn bắt đầu từ đây.",
      copyright: "Just Delivery. Bảo lưu mọi quyền.",
      privacyPolicy: "Chính sách Bảo mật",
      termsOfService: "Điều khoản Dịch vụ",
      websiteDisclaimer: "Tuyên bố từ chối",
      refundPolicy: "Chính sách Hoàn tiền",
    },
    ui: {
      guideLink: "Mới bắt đầu giao hàng? Đọc hướng dẫn đầy đủ",
      guideSection: {
        title: "Mới với Giao Đồ Ăn tại Úc?",
        subtitle: "Bắt đầu công việc giao hàng tại Úc có thể gây nhầm lẫn cho các tài xế mới. Hướng dẫn đầy đủ của chúng tôi giải thích các yêu cầu, tùy chọn phương tiện và những sai lầm phổ biến cần tránh khi đăng ký trên các nền tảng như Uber Eats và DoorDash.",
        cta: "Đọc Hướng Dẫn Giao Hàng Đầy Đủ",
        chooseCity: "Chọn Thành Phố",
        sydneyDesc: "Thị trường lớn nhất Úc với nhu cầu cao",
        melbourneDesc: "Thủ đô ẩm thực với văn hóa cà phê",
        brisbaneDesc: "Khí hậu nắng ấm và thị trường đang phát triển",
      },
      joinCompetition: "Tham gia Cuộc thi!",
      notifyDescription: "Nhận thông báo về thử thách hàng tuần và cơ hội thưởng độc quyền",
      enterEmail: "Nhập email",
      documentsSubmitted: "Đã Gửi Hồ Sơ!",
      thankYouSubmission: "Cảm ơn bạn đã gửi.",
      whatHappensNext: "Tiếp theo là gì?",
      backToHome: "Về Trang Chủ",
      submitForReview: "Gửi hồ sơ để xác minh",
      uploadDocuments: "Tải Lên Hồ Sơ",
      acceptedFormats: "Định dạng: PDF, JPG, PNG (Tối đa 10MB)",
      proofOfIdentity: "Chứng minh Nhân thân (Hộ chiếu/CMND)",
      workRightsVisa: "Quyền Làm việc / Visa",
      driverLicence: "Bằng Lái Xe",
      onlyForScooter: "(Chỉ cần cho giao hàng xe tay ga/moto)",
      uploaded: "Đã tải",
      chooseFile: "Chọn tệp",
      noFileSelected: "Chưa chọn tệp nào",
      consentAgreements: "Đồng ý và Thỏa thuận",
      processing: "Đang xử lý...",
      submitReview: "Gửi để Xem xét",
      noPaymentNow: "Không cần thanh toán ngay. Bạn sẽ nhận hướng dẫn thanh toán sau khi xem xét hồ sơ.",
      basePackage: "Cơ bản - $49 AUD",
      premiumPackage: "Cao cấp - $79 AUD",
    },
    contactUs: {
      title: "Liên hệ Just Delivery",
      subtitle: "Có câu hỏi về công việc giao hàng tại Úc?",
      description: "Dù bạn đã ở Úc hay đang lên kế hoạch đến sớm, bạn có thể liên hệ Just Delivery để được thông tin và hướng dẫn.",
      description2: "Ngay cả khi bạn không chắc ứng dụng giao hàng hoạt động như thế nào hoặc bắt đầu từ đâu, hãy thoải mái liên hệ với chúng tôi.",
      helpWith: "Chúng tôi sẵn lòng trả lời câu hỏi về:",
      topics: [
        "cách giao đồ ăn hoạt động tại Úc",
        "bạn có thể làm việc với những ứng dụng nào",
        "giấy tờ và yêu cầu",
        "xe đạp, xe tay ga và phương tiện hợp pháp",
        "cách bắt đầu làm việc nhanh hơn",
        "câu hỏi chung về công việc giao hàng"
      ],
      notRiderYet: "Bạn chưa cần phải là tài xế.",
      exploring: "Nếu bạn tò mò hoặc chỉ đang khám phá các lựa chọn, hãy thoải mái liên hệ với chúng tôi.",
      ourGoal: "Mục tiêu của chúng tôi đơn giản là giúp mọi người hiểu cách bắt đầu công việc giao hàng tại Úc đúng cách.",
      formPrompt: "Nếu bạn có câu hỏi, hãy điền vào biểu mẫu bên dưới và đội ngũ của chúng tôi sẽ liên hệ lại với bạn.",
      form: {
        firstName: "Tên",
        lastName: "Họ",
        email: "Địa chỉ Email",
        country: "Quốc gia",
        countryPlaceholder: "Chọn quốc gia của bạn",
        city: "Thành phố",
        cityPlaceholder: "Thành phố của bạn (tùy chọn)",
        interest: "Bạn quan tâm đến điều gì?",
        interestPlaceholder: "Chọn một tùy chọn",
        interestOptions: [
          "Thông tin công việc giao hàng",
          "Đăng ký tài xế",
          "Tư vấn phương tiện",
          "Câu hỏi chung"
        ],
        message: "Tin nhắn",
        messagePlaceholder: "Viết câu hỏi hoặc tin nhắn của bạn ở đây...",
        submit: "Gửi Tin nhắn",
        sending: "Đang gửi..."
      },
      successTitle: "Tin nhắn đã được gửi thành công.",
      successMessage: "Đội ngũ của chúng tôi sẽ liên hệ với bạn sớm."
    },
  },
  tl: {
    nav: {
      packages: "Mga Package",
      howItWorks: "Paano Gumagana",
      leaderboard: "Leaderboard",
      info: "Impormasyon",
      forShops: "Para sa mga Shop",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "Para LAMANG sa mga BAGONG rider — hindi pa nakarehistro sa Uber Eats o DoorDash (o kahit isa)",
      newRidersShort: "Mga bagong rider lamang — Hindi pa nakarehistro sa Uber Eats/DoorDash",
      newRidersImportant: "Mahalaga: Ang serbisyong ito ay para LAMANG sa mga bagong rider na hindi pa nakarehistro sa Uber Eats o DoorDash (o kahit isa sa dalawang platform)",
      premiumOnly: "Mga Premium Member Lamang",
      leaderboardPremium: "Para makasali sa leaderboard at manalo ng cash bonus, kailangan mong bilhin ang Premium package",
    },
    hero: {
      headline: "Simulan ang Iyong Delivery Career sa Australia",
      subheadline: "Kumpletong suporta sa pagpaparehistro para sa Uber Eats at DoorDash. Magparehistro, maghanap ng sasakyan at kumita.",
      cta1: "Magsimula",
      cta2: "Tingnan ang Packages",
      badge: "Walang Panganib • Madaling Simula • Totoong Bonus",
    },
    packages: {
      title: "Piliin ang Iyong Package",
      subtitle: "Isang beses na bayad. Walang nakatagong bayarin. Buong suporta.",
      base: {
        name: "Base",
        price: "$49",
        description: "Perpekto para sa mga self-starter",
        features: [
          "Paghahanda ng aplikasyon sa ABN (handa na para sa huling pagsusumite)",
          "Hakbang-hakbang na gabay sa pagpaparehistro sa Uber/DoorDash",
          "Email support",
          "Kasama ang referral bonus",
        ],
        cta: "Piliin ang Base",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "Pinaka-Popular",
        description: "Buong karanasan sa serbisyo",
        features: [
          "Lahat sa Base package",
          "Direktang koordinasyon sa aming mga trusted rental partner",
          "Inihahanda at ipinapadala namin ang iyong mga dokumento sa shop para mapabilis ang vehicle setup",
          "Tinutulungan naming mabawasan ang onboarding delays para mas mabilis kang makapagsimulang mag-deliver",
          "Priority WhatsApp support",
        ],
        highlighted: "Maaaring mag-apply ang minimum 4-week delivery activity commitment kapag gumagamit ng partner shops",
        cta: "Piliin ang Premium",
      },
    },
    howItWorks: {
      title: "Paano Gumagana",
      subtitle: "Magsimulang mag-deliver sa 4 na simpleng hakbang",
      steps: [
        { number: "01", title: "I-upload ang Dokumento", description: "Ipadala ang ID, visa at lisensya" },
        { number: "02", title: "Pumili ng Package", description: "Base ($49) o Premium ($79)" },
        { number: "03", title: "Kumpletuhin ang Bayad", description: "Ligtas na bank transfer sa pamamagitan ng PayID" },
        { number: "04", title: "Magsimulang Kumita", description: "Ire-register ka namin sa Uber/DoorDash" },
      ],
    },
    documents: {
      title: "Kinakailangang Dokumento",
      subtitle: "Bago bumili, ipadala sa amin:",
      items: [
        { icon: "id", title: "Patunay ng Pagkakakilanlan", desc: "Valid na pasaporte o ID card" },
        { icon: "car", title: "Driver's License", desc: "Only required if you plan to deliver using a scooter or motorbike. E-bike delivery requires no license. Scooter/motorbike delivery may require conversion of an overseas license." },
        { icon: "file", title: "Valid na Visa", desc: "Kopya ng visa na nagpapahintulot sa iyo na magtrabaho sa Australia" },
      ],
      licenseWarning: {
        title: "Mahalaga: Mga Kinakailangan sa Lisensya para sa Scooter",
        nsw_vic: "NSW at VIC: Kailangan ng motorcycle license (R o RE class) para magmaneho ng ANUMANG scooter, anuman ang laki ng engine.",
        qld: "QLD: Sa car license (C class) puwede kang magmaneho ng scooter hanggang 50cc.",
      },
      licenseTitle: "Paano I-convert ang Foreign License",
      licenseSteps: [
        "Makipag-ugnayan sa Transport sa iyong estado",
        "Ipresenta ang foreign license at mga pagsasalin",
        "Kumpletuhin ang verification at tests (kung kailangan)",
        "Matanggap ang katumbas na Australian license",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Makipagkumpitensya at Kumita ng Extra",
      subtitle: "Bawat Linggo at Buwan",
      description: "Makipagkumpitensya sa ibang rider at kumita ng extra cash!",
      comingSoon: "Leaderboard Malapit Na",
      comingSoonText: "Ang mga top performer ay mananalo ng exclusive na bonus!",
      notifyBtn: "Abisuhan Ako",
      weekly: { title: "Weekly Leaderboard", subtitle: "Weekly bonus para sa top performers", prize: "Weekly Prize" },
      monthly: { title: "Monthly Leaderboard", subtitle: "Mas malaking reward para sa consistent performers", prize: "Monthly Prize" },
      tableHeaders: { rank: "Rank", rider: "Rider", deliveries: "Deliveries", bonus: "Bonus" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Ikaw?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Makipagtulungan sa Amin",
      subtitle: "Ikaw ba ay rental shop?",
      description: "Nagpapadala kami ng mga bagong customer sa iyo — walang commitment.",
      features: ["Makatanggap ng pre-qualified rider leads", "Walang upfront costs", "Optional na referral bonus program", "Direct na komunikasyon"],
      cta: "Maging Partner",
    },
    testimonials: {
      title: "Ano ang Sinasabi ng mga Rider",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Ginawang madali ng Just Delivery ang lahat!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "Sulit ang Premium package.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Mahusay na support team!", rating: 5 },
      ],
    },
    faq: {
      title: "Mga Karaniwang Tanong",
      items: [
        { q: "Sino ang pwedeng gumamit ng serbisyong ito?", a: "Ang serbisyong ito ay para LAMANG sa mga BAGONG rider na hindi pa nakarehistro sa Uber Eats o DoorDash (o kahit isa sa dalawang platform). Kung registered rider ka na, hindi ito para sa iyo." },
        { q: "Ano ang kasama sa presyo ng package?", a: "Isang beses na bayad para sa registration support. Base kasama ang ABN/Uber/DoorDash. Premium may priority rental partner coordination at mas mabilis na activation." },
        { q: "Ano ang Premium rental benefits?", a: "Ang Premium users ay nakakakuha ng priority coordination sa aming rental partners. Ang mga insentibo ay depende sa availability." },
        { q: "Paano gumagana ang Leaderboard?", a: "Nakikipagkumpitensya ang mga Premium rider base sa deliveries. Ang mga top performer ay nananalo ng cash bonus. Note: kailangan ng Premium package para sumali sa leaderboard." },
        { q: "Anong dokumento ang kailangan?", a: "Pasaporte/ID, Australian phone number, bank account, lisensya para sa rental." },
      ],
    },
    contact: {
      title: "Handa Ka Na Bang Magsimula?",
      subtitle: "I-upload ang mga dokumento at kumpletuhin ang bayad",
      form: {
        name: "Buong Pangalan",
        email: "Email",
        phone: "Phone Number",
        city: "Lungsod",
        cityPlaceholder: "Pumili ng lungsod",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Package",
        packagePlaceholder: "Pumili ng package",
        documents: "I-upload ang Dokumento",
        documentsHint: "I-upload: Pasaporte/ID, Lisensya, Visa (lahat sabay)",
        submit: "Isumite at Magbayad",
        processing: "Pinoproseso...",
      },
      paymentSuccess: "Matagumpay ang Bayad!",
      paymentSuccessText: "Salamat! Makikipag-ugnayan kami sa iyo sa loob ng 24 oras.",
      paymentCancelled: "Nakansela ang Bayad",
      paymentCancelledText: "Okay lang! Puwede kang subukan ulit kapag handa ka na.",
    },
    footer: {
      tagline: "Nagsisimula dito ang iyong delivery career.",
      copyright: "Just Delivery. All rights reserved.",
      privacyPolicy: "Privacy Policy",
      termsOfService: "Mga Tuntunin ng Serbisyo",
      websiteDisclaimer: "Disclaimer ng Website",
      refundPolicy: "Patakaran sa Refund",
    },
    ui: {
      guideLink: "Bago sa delivery? Basahin ang kumpletong gabay",
      guideSection: {
        title: "Bago sa Food Delivery sa Australia?",
        subtitle: "Ang pagsisimula ng delivery work sa Australia ay maaaring nakakalito para sa mga bagong rider. Ang aming kumpletong gabay ay nagpapaliwanag ng mga requirements, vehicle options, at mga karaniwang pagkakamaling dapat iwasan kapag nag-sign up sa mga platform tulad ng Uber Eats at DoorDash.",
        cta: "Basahin ang Kumpletong Gabay sa Delivery",
        chooseCity: "Piliin ang Iyong Lungsod",
        sydneyDesc: "Pinakamalaking market ng Australia na may mataas na demand",
        melbourneDesc: "Kabiserang pangkain na may maunlad na kultura ng kape",
        brisbaneDesc: "Maaraw na klima at lumalagong delivery market",
      },
      joinCompetition: "Sumali sa Kompetisyon!",
      documentsSubmitted: "Naisumite ang mga Dokumento!",
      thankYouSubmission: "Salamat sa iyong pagsusumite.",
      whatHappensNext: "Ano ang susunod?",
      backToHome: "Bumalik sa Home",
      uploadDocuments: "Mag-upload ng Dokumento",
      chooseFile: "Pumili ng file",
      noFileSelected: "Walang napiling file",
      processing: "Nagpoproseso...",
      submitReview: "Isumite para sa Pagsusuri",
      basePackage: "Base - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Makipag-ugnayan sa Just Delivery",
      subtitle: "May mga tanong tungkol sa delivery work sa Australia?",
      description: "Nasa Australia ka man o nagpaplano pang dumating, puwede kang makipag-ugnayan sa Just Delivery para sa impormasyon at gabay.",
      description2: "Kahit hindi ka sigurado kung paano gumagana ang mga delivery app o kung saan magsisimula, huwag mag-atubiling makipag-ugnayan.",
      helpWith: "Masaya naming sagutin ang mga tanong tungkol sa:",
      topics: [
        "paano gumagana ang food delivery sa Australia",
        "kung aling mga app ang puwede mong pagtrabahuan",
        "mga dokumento at requirements",
        "mga bisikleta, scooter at legal na sasakyan",
        "paano magsimulang magtrabaho nang mas mabilis",
        "pangkalahatang tanong tungkol sa delivery work"
      ],
      notRiderYet: "Hindi mo kailangang maging rider pa.",
      exploring: "Kung curious ka o nag-eexplore lang ng mga opsyon, welcome kang makipag-ugnayan sa amin.",
      ourGoal: "Ang layunin namin ay simpleng tulungan ang mga tao na maunawaan kung paano simulan ang delivery work sa Australia sa tamang paraan.",
      formPrompt: "Kung may mga tanong ka, punan ang form sa ibaba at makikipag-ugnayan sa iyo ang aming team.",
      form: {
        firstName: "Pangalan",
        lastName: "Apelyido",
        email: "Email Address",
        country: "Bansa",
        countryPlaceholder: "Piliin ang iyong bansa",
        city: "Lungsod",
        cityPlaceholder: "Ang iyong lungsod (opsyonal)",
        interest: "Ano ang interesado ka?",
        interestPlaceholder: "Pumili ng opsyon",
        interestOptions: [
          "Impormasyon tungkol sa delivery work",
          "Rider onboarding",
          "Payo sa sasakyan",
          "Pangkalahatang tanong"
        ],
        message: "Mensahe",
        messagePlaceholder: "Isulat ang iyong tanong o mensahe dito...",
        submit: "Ipadala ang Mensahe",
        sending: "Ipinapadala..."
      },
      successTitle: "Matagumpay na naipadala ang mensahe.",
      successMessage: "Makikipag-ugnayan sa iyo ang aming team sa lalong madaling panahon."
    },
  },
  ms: {
    nav: {
      packages: "Pakej",
      howItWorks: "Cara Ia Berfungsi",
      leaderboard: "Papan Pendahulu",
      info: "Maklumat",
      forShops: "Untuk Kedai",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "HANYA untuk rider BARU — belum berdaftar di Uber Eats atau DoorDash (atau sekurang-kurangnya satu)",
      newRidersShort: "Rider baru sahaja — Belum berdaftar di Uber Eats/DoorDash",
      newRidersImportant: "Penting: Perkhidmatan ini HANYA untuk rider baru yang belum berdaftar di Uber Eats atau DoorDash (atau sekurang-kurangnya satu platform)",
      premiumOnly: "Ahli Premium Sahaja",
      leaderboardPremium: "Untuk menyertai papan pendahulu dan memenangi bonus tunai, anda mesti membeli pakej Premium",
    },
    hero: {
      headline: "Mulakan Kerjaya Penghantaran Anda di Australia",
      subheadline: "Sokongan pendaftaran lengkap untuk Uber Eats & DoorDash. Daftar, cari kenderaan dan mula menjana — dengan panduan pakar.",
      cta1: "Mula Sekarang",
      cta2: "Lihat Pakej",
      badge: "Tiada Risiko • Mula Mudah • Bonus Sebenar",
    },
    packages: {
      title: "Pilih Pakej Anda",
      subtitle: "Bayaran sekali. Tiada yuran tersembunyi. Sokongan penuh.",
      base: {
        name: "Asas",
        price: "$49",
        description: "Sesuai untuk yang mahu mulakan sendiri",
        features: [
          "Penyediaan permohonan ABN (sedia untuk penghantaran akhir)",
          "Panduan langkah demi langkah untuk pendaftaran Uber/DoorDash",
          "Sokongan e-mel",
          "Bonus rujukan termasuk",
        ],
        cta: "Pilih Asas",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "Paling Popular",
        description: "Pengalaman perkhidmatan penuh",
        features: [
          "Semua dalam pakej Asas",
          "Penyelarasan terus dengan rakan sewa yang dipercayai",
          "Kami menyediakan dan menghantar dokumen anda ke kedai untuk mempercepatkan persediaan kenderaan",
          "Kami membantu mengurangkan kelewatan onboarding supaya anda boleh mula menghantar lebih cepat",
          "Sokongan WhatsApp keutamaan",
        ],
        highlighted: "Komitmen aktiviti penghantaran minimum 4 minggu mungkin dikenakan apabila menggunakan kedai rakan",
        cta: "Pilih Premium",
      },
    },
    howItWorks: {
      title: "Cara Ia Berfungsi",
      subtitle: "Mula menghantar dalam 4 langkah mudah",
      steps: [
        { number: "01", title: "Muat Naik Dokumen", description: "Hantar ID, visa dan lesen" },
        { number: "02", title: "Pilih Pakej", description: "Asas ($49) atau Premium ($79)" },
        { number: "03", title: "Lengkapkan Bayaran", description: "Pemindahan bank selamat melalui PayID" },
        { number: "04", title: "Mula Menjana", description: "Kami daftarkan anda di Uber/DoorDash" },
      ],
    },
    documents: {
      title: "Dokumen Diperlukan",
      subtitle: "Sebelum membeli, hantar kepada kami:",
      items: [
        { icon: "id", title: "Bukti Identiti", desc: "Pasport atau kad pengenalan sah" },
        { icon: "car", title: "Lesen Memandu", desc: "Hanya diperlukan jika anda merancang untuk menghantar menggunakan skuter atau motosikal. Penghantaran e-bike tidak memerlukan lesen. Penghantaran skuter/motosikal mungkin memerlukan penukaran lesen asing." },
        { icon: "file", title: "Visa Sah", desc: "Salinan visa yang membenarkan anda bekerja di Australia" },
      ],
      licenseWarning: {
        title: "Penting: Keperluan Lesen Skuter",
        nsw_vic: "NSW & VIC: Lesen motosikal (kelas R atau RE) diperlukan untuk SEBARANG skuter, tanpa mengira saiz enjin.",
        qld: "QLD: Dengan lesen kereta (kelas C) anda boleh memandu skuter sehingga 50cc.",
      },
      licenseTitle: "Cara Menukar Lesen Asing",
      licenseSteps: [
        "Hubungi jabatan pengangkutan negeri anda",
        "Kemukakan lesen asing dan terjemahan",
        "Lengkapkan pengesahan dan ujian (jika perlu)",
        "Terima lesen Australia yang setara",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Bersaing & Jana Lebih",
      subtitle: "Setiap Minggu & Bulan",
      description: "Bersaing dengan rider lain dan jana wang tunai tambahan!",
      comingSoon: "Papan Pendahulu Akan Datang",
      comingSoonText: "Pemain terbaik akan memenangi bonus eksklusif!",
      notifyBtn: "Beritahu Saya",
      weekly: { title: "Papan Pendahulu Mingguan", subtitle: "Bonus mingguan untuk pemain terbaik", prize: "Hadiah Mingguan" },
      monthly: { title: "Papan Pendahulu Bulanan", subtitle: "Ganjaran lebih besar untuk pemain konsisten", prize: "Hadiah Bulanan" },
      tableHeaders: { rank: "Kedudukan", rider: "Rider", deliveries: "Penghantaran", bonus: "Bonus" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Anda?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Rakan Kongsi Dengan Kami",
      subtitle: "Adakah anda kedai sewa?",
      description: "Kami hantar pelanggan baru kepada anda — tiada komitmen.",
      features: ["Terima lead rider yang disaring", "Tiada kos pendahuluan", "Program bonus rujukan pilihan", "Saluran komunikasi langsung"],
      cta: "Jadi Rakan Kongsi",
    },
    testimonials: {
      title: "Apa Kata Rider",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Just Delivery menjadikan semuanya mudah!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "Pakej Premium berbaloi.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Pasukan sokongan hebat!", rating: 5 },
      ],
    },
    faq: {
      title: "Soalan Lazim",
      items: [
        { q: "Siapa yang boleh menggunakan perkhidmatan ini?", a: "Perkhidmatan ini HANYA untuk rider BARU yang belum berdaftar di Uber Eats atau DoorDash (atau sekurang-kurangnya satu platform). Jika anda sudah rider berdaftar, perkhidmatan ini bukan untuk anda." },
        { q: "Apa yang termasuk dalam harga pakej?", a: "Bayaran sekali untuk sokongan pendaftaran. Asas termasuk ABN/Uber/DoorDash. Premium tambah koordinasi keutamaan rakan sewa dan pengaktifan lebih cepat." },
        { q: "Apa faedah sewa Premium?", a: "Pengguna Premium dapat koordinasi keutamaan dengan rakan sewa kami. Insentif mungkin berbeza bergantung pada ketersediaan." },
        { q: "Bagaimana Papan Pendahulu berfungsi?", a: "Rider pakej Premium bersaing berdasarkan penghantaran. Pemain terbaik memenangi bonus tunai. Nota: penyertaan papan pendahulu memerlukan pakej Premium." },
        { q: "Dokumen apa yang diperlukan?", a: "Pasport/ID, nombor telefon Australia, akaun bank, lesen untuk sewa." },
      ],
    },
    contact: {
      title: "Bersedia Untuk Mula?",
      subtitle: "Muat naik dokumen dan lengkapkan bayaran",
      form: {
        name: "Nama Penuh",
        email: "E-mel",
        phone: "Nombor Telefon",
        city: "Bandar",
        cityPlaceholder: "Pilih bandar",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Pakej",
        packagePlaceholder: "Pilih pakej",
        documents: "Muat Naik Dokumen",
        documentsHint: "Muat naik: Pasport/ID, Lesen, Visa (semua sekali)",
        submit: "Hantar & Bayar",
        processing: "Memproses...",
      },
      paymentSuccess: "Bayaran Berjaya!",
      paymentSuccessText: "Terima kasih! Kami akan hubungi anda dalam 24 jam.",
      paymentCancelled: "Bayaran Dibatalkan",
      paymentCancelledText: "Tidak mengapa! Anda boleh cuba lagi bila sedia.",
    },
    footer: {
      tagline: "Kerjaya penghantaran anda bermula di sini.",
      copyright: "Just Delivery. Hak cipta terpelihara.",
      privacyPolicy: "Dasar Privasi",
      termsOfService: "Terma Perkhidmatan",
      websiteDisclaimer: "Penafian Laman Web",
      refundPolicy: "Dasar Bayaran Balik",
    },
    ui: {
      guideLink: "Baru dalam penghantaran? Baca panduan lengkap",
      guideSection: {
        title: "Baru dalam Penghantaran Makanan di Australia?",
        subtitle: "Memulakan kerja penghantaran di Australia boleh mengelirukan untuk rider baru. Panduan lengkap kami menerangkan keperluan, pilihan kenderaan, dan kesilapan biasa yang perlu dielakkan semasa mendaftar di platform seperti Uber Eats dan DoorDash.",
        cta: "Baca Panduan Penghantaran Lengkap",
        chooseCity: "Pilih Bandar Anda",
        sydneyDesc: "Pasaran terbesar Australia dengan permintaan tinggi",
        melbourneDesc: "Ibu negara makanan dengan budaya kafe",
        brisbaneDesc: "Iklim cerah dan pasaran yang berkembang",
      },
      joinCompetition: "Sertai Pertandingan!",
      documentsSubmitted: "Dokumen Dihantar!",
      thankYouSubmission: "Terima kasih atas penghantaran anda.",
      whatHappensNext: "Apa seterusnya?",
      backToHome: "Kembali ke Utama",
      uploadDocuments: "Muat Naik Dokumen",
      chooseFile: "Pilih fail",
      noFileSelected: "Tiada fail dipilih",
      processing: "Memproses...",
      submitReview: "Hantar untuk Semakan",
      basePackage: "Asas - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Hubungi Just Delivery",
      subtitle: "Ada soalan tentang kerja penghantaran di Australia?",
      description: "Sama ada anda sudah berada di Australia atau merancang untuk tiba tidak lama lagi, anda boleh menghubungi Just Delivery untuk maklumat dan panduan.",
      description2: "Walaupun anda tidak pasti bagaimana aplikasi penghantaran berfungsi atau di mana untuk bermula, jangan teragak-agak untuk menghubungi kami.",
      helpWith: "Kami gembira menjawab soalan tentang:",
      topics: [
        "bagaimana penghantaran makanan berfungsi di Australia",
        "aplikasi mana yang boleh anda gunakan",
        "dokumen dan keperluan",
        "basikal, skuter dan kenderaan sah",
        "cara mula bekerja lebih cepat",
        "soalan umum tentang kerja penghantaran"
      ],
      notRiderYet: "Anda tidak perlu menjadi rider lagi.",
      exploring: "Jika anda ingin tahu atau sekadar meneroka pilihan anda, anda dialu-alukan untuk menghubungi kami.",
      ourGoal: "Matlamat kami adalah untuk membantu orang ramai memahami cara memulakan kerja penghantaran di Australia dengan cara yang betul.",
      formPrompt: "Jika anda mempunyai soalan, isi borang di bawah dan pasukan kami akan menghubungi anda.",
      form: {
        firstName: "Nama Pertama",
        lastName: "Nama Keluarga",
        email: "Alamat E-mel",
        country: "Negara",
        countryPlaceholder: "Pilih negara anda",
        city: "Bandar",
        cityPlaceholder: "Bandar anda (pilihan)",
        interest: "Apa yang anda minati?",
        interestPlaceholder: "Pilih pilihan",
        interestOptions: [
          "Maklumat kerja penghantaran",
          "Onboarding rider",
          "Nasihat kenderaan",
          "Soalan umum"
        ],
        message: "Mesej",
        messagePlaceholder: "Tulis soalan atau mesej anda di sini...",
        submit: "Hantar Mesej",
        sending: "Menghantar..."
      },
      successTitle: "Mesej berjaya dihantar.",
      successMessage: "Pasukan kami akan menghubungi anda tidak lama lagi."
    },
  },
  // Turkish - Türkçe
  tr: {
    nav: {
      packages: "Paketler",
      howItWorks: "Nasıl Çalışır",
      leaderboard: "Sıralama",
      info: "Bilgi",
      forShops: "Mağazalar İçin",
      faq: "SSS",
    },
    notices: {
      newRidersOnly: "SADECE YENİ kuryeler için — henüz Uber Eats veya DoorDash'e kayıtlı olmayan (veya en az biri)",
      newRidersShort: "Sadece yeni kuryeler — Uber Eats/DoorDash'e kayıtlı değil",
      newRidersImportant: "Önemli: Bu hizmet SADECE Uber Eats veya DoorDash'e henüz kayıtlı olmayan yeni kuryeler içindir (veya en az bir platform)",
      premiumOnly: "Sadece Premium Üyeler",
      leaderboardPremium: "Sıralamaya katılmak ve nakit bonus kazanmak için Premium paketi satın almış olmanız gerekir",
    },
    hero: {
      headline: "Avustralya'da Kurye Kariyerinize Başlayın",
      subheadline: "Uber Eats ve DoorDash için tam kayıt desteği. Kaydolun, araç bulun ve kazanmaya başlayın — uzman rehberliğiyle.",
      cta1: "Başla",
      cta2: "Paketleri Gör",
      badge: "Risk Yok • Kolay Başlangıç • Gerçek Bonuslar",
    },
    packages: {
      title: "Paketinizi Seçin",
      subtitle: "Tek seferlik ödeme. Gizli ücret yok. Tam destek.",
      base: {
        name: "Temel",
        price: "$49",
        description: "Kendi başına başlamak isteyenler için",
        features: [
          "ABN başvurusu hazırlığı (son gönderime hazır)",
          "Uber/DoorDash kaydı için adım adım rehber",
          "E-posta desteği",
          "Referans bonusu dahil",
        ],
        cta: "Temel Seç",
      },
      premium: {
        name: "Premium",
        price: "$79",
        popular: "En Popüler",
        description: "Tam hizmet deneyimi",
        features: [
          "Temel paketteki her şey",
          "Güvenilir kiralama ortaklarımızla doğrudan koordinasyon",
          "Belgelerinizi hazırlayıp araç kurulumunu hızlandırmak için mağazaya iletiyoruz",
          "Onboarding gecikmelerini azaltmaya yardımcı oluyoruz, böylece daha hızlı teslimat yapmaya başlayabilirsiniz",
          "Öncelikli WhatsApp desteği",
        ],
        highlighted: "Ortak mağazalar kullanılırken minimum 4 hafta teslimat aktivitesi taahhüdü geçerli olabilir",
        cta: "Premium Seç",
      },
    },
    howItWorks: {
      title: "Nasıl Çalışır",
      subtitle: "4 kolay adımda teslimat yapmaya başlayın",
      steps: [
        { number: "01", title: "Belgeleri Yükle", description: "Kimlik, vize ve ehliyet gönderin" },
        { number: "02", title: "Paket Seç", description: "Temel ($49) veya Premium ($79)" },
        { number: "03", title: "Ödemeyi Tamamla", description: "PayID ile güvenli banka transferi" },
        { number: "04", title: "Kazanmaya Başla", description: "Sizi Uber/DoorDash'a kaydediyoruz" },
      ],
    },
    documents: {
      title: "Gerekli Belgeler",
      subtitle: "Satın almadan önce bize gönderin:",
      items: [
        { icon: "id", title: "Kimlik Kanıtı", desc: "Geçerli pasaport veya kimlik kartı" },
        { icon: "car", title: "Ehliyet", desc: "Sadece scooter veya motorsiklet ile teslimat yapmayı planlıyorsanız gereklidir. E-bisiklet teslimatı ehliyet gerektirmez. Scooter/motorsiklet teslimatı yabancı ehliyetin dönüştürülmesini gerektirebilir." },
        { icon: "file", title: "Geçerli Vize", desc: "Avustralya'da çalışmanıza izin veren vize kopyası" },
      ],
      licenseWarning: {
        title: "Önemli: Scooter Ehliyet Gereksinimleri",
        nsw_vic: "NSW ve VIC: Motor boyutu ne olursa olsun HERHANGİ bir scooter için motorsiklet ehliyeti (R veya RE sınıfı) gereklidir.",
        qld: "QLD: Araba ehliyeti (C sınıfı) ile 50cc'ye kadar scooter kullanabilirsiniz.",
      },
      licenseTitle: "Yabancı Ehliyet Nasıl Dönüştürülür",
      licenseSteps: [
        "Eyaletinizin ulaşım departmanıyla iletişime geçin",
        "Yabancı ehliyet ve çevirileri sunun",
        "Doğrulama ve testleri tamamlayın (gerekirse)",
        "Eşdeğer Avustralya ehliyeti alın",
      ],
      offices: [
        { city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Yarış ve Ekstra Kazan",
      subtitle: "Her Hafta ve Ay",
      description: "Diğer kuryelerle yarışın ve ekstra para kazanın!",
      comingSoon: "Sıralama Yakında",
      comingSoonText: "En iyi performans gösterenler özel bonuslar kazanacak!",
      notifyBtn: "Beni Bilgilendir",
      weekly: { title: "Haftalık Sıralama", subtitle: "En iyiler için haftalık bonus", prize: "Haftalık Ödül" },
      monthly: { title: "Aylık Sıralama", subtitle: "Tutarlı performans gösterenler için daha büyük ödüller", prize: "Aylık Ödül" },
      tableHeaders: { rank: "Sıra", rider: "Kurye", deliveries: "Teslimat", bonus: "Bonus" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Sen?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Bizimle Ortak Olun",
      subtitle: "Kiralama mağazası mısınız?",
      description: "Size yeni müşteriler gönderiyoruz — taahhüt yok.",
      features: ["Ön elemeden geçmiş kurye adayları alın", "Ön ödeme yok", "İsteğe bağlı referans bonus programı", "Doğrudan iletişim kanalı"],
      cta: "Ortak Ol",
    },
    testimonials: {
      title: "Kuryeler Ne Diyor",
      items: [
        { name: "Marco R.", location: "Sydney", text: "Just Delivery her şeyi kolaylaştırdı!", rating: 5 },
        { name: "Sofia L.", location: "Melbourne", text: "Premium paket değdi.", rating: 5 },
        { name: "Carlos M.", location: "Brisbane", text: "Harika destek ekibi!", rating: 5 },
      ],
    },
    faq: {
      title: "Sık Sorulan Sorular",
      items: [
        { q: "Bu hizmeti kim kullanabilir?", a: "Bu hizmet SADECE Uber Eats veya DoorDash'e henüz kayıtlı olmayan YENİ kuryeler içindir (veya en az biri). Zaten kayıtlı bir kuryeyseniz, bu hizmet sizin için değil." },
        { q: "Paket fiyatına ne dahil?", a: "Kayıt desteği için tek seferlik ödeme. Temel ABN/Uber/DoorDash içerir. Premium kiralama ortağı koordinasyonu ve daha hızlı aktivasyon ekler." },
        { q: "Premium %50 indirim nedir?", a: "Premium kullanıcılar ortak mağazalarda ilk hafta kiralamada %50 indirim alır." },
        { q: "Sıralama nasıl çalışır?", a: "Premium paketli kuryeler teslimat sayısına göre yarışır. En iyi performans gösterenler nakit bonus kazanır. Not: sıralamaya katılım için Premium paketi gereklidir." },
        { q: "Hangi belgeler gerekli?", a: "Pasaport/kimlik, Avustralya telefon numarası, banka hesabı, kiralama için ehliyet." },
      ],
    },
    contact: {
      title: "Başlamaya Hazır mısınız?",
      subtitle: "Belgeleri yükleyin ve ödemeyi tamamlayın",
      form: {
        name: "Ad Soyad",
        email: "E-posta",
        phone: "Telefon",
        city: "Şehir",
        cityPlaceholder: "Şehir seçin",
        cities: ["Sydney", "Melbourne", "Brisbane"],
        package: "Paket",
        packagePlaceholder: "Paket seçin",
        documents: "Belge Yükle",
        documentsHint: "Yükle: Pasaport/Kimlik, Ehliyet, Vize (hepsi birden)",
        submit: "Gönder ve Öde",
        processing: "İşleniyor...",
      },
      paymentSuccess: "Ödeme Başarılı!",
      paymentSuccessText: "Teşekkürler! 24 saat içinde sizinle iletişime geçeceğiz.",
      paymentCancelled: "Ödeme İptal Edildi",
      paymentCancelledText: "Sorun değil! Hazır olduğunuzda tekrar deneyebilirsiniz.",
    },
    footer: {
      tagline: "Kurye kariyeriniz burada başlıyor.",
      copyright: "Just Delivery. Tüm hakları saklıdır.",
      privacyPolicy: "Gizlilik Politikası",
      termsOfService: "Hizmet Şartları",
      websiteDisclaimer: "Web Sitesi Feragati",
      refundPolicy: "İade Politikası",
    },
    ui: {
      guideLink: "Teslimatta yeni misiniz? Tam kılavuzu okuyun",
      guideSection: {
        title: "Avustralya'da Yemek Teslimatında Yeni misiniz?",
        subtitle: "Avustralya'da teslimat işine başlamak yeni kuryeler için kafa karıştırıcı olabilir. Kapsamlı kılavuzumuz gereksinimleri, araç seçeneklerini ve Uber Eats ve DoorDash gibi platformlara kaydolurken kaçınılması gereken en yaygın hataları açıklar.",
        cta: "Teslimat Kılavuzunun Tamamını Okuyun",
        chooseCity: "Şehrinizi Seçin",
        sydneyDesc: "Yüksek talep ile Avustralya'nın en büyük pazarı",
        melbourneDesc: "Kafe kültürü ile yemek başkenti",
        brisbaneDesc: "Güneşli iklim ve büyüyen teslimat pazarı",
      },
      joinCompetition: "Yarışmaya Katılın!",
      documentsSubmitted: "Belgeler Gönderildi!",
      thankYouSubmission: "Gönderiminiz için teşekkürler.",
      whatHappensNext: "Sırada ne var?",
      backToHome: "Ana Sayfaya Dön",
      uploadDocuments: "Belge Yükle",
      chooseFile: "Dosya seç",
      noFileSelected: "Dosya seçilmedi",
      processing: "İşleniyor...",
      submitReview: "İnceleme için Gönder",
      basePackage: "Temel - $49 AUD",
      premiumPackage: "Premium - $79 AUD",
    },
    contactUs: {
      title: "Just Delivery ile İletişim",
      subtitle: "Avustralya'da teslimat işi hakkında sorularınız mı var?",
      description: "İster zaten Avustralya'da olun ister yakında gelmeyi planlıyor olun, bilgi ve rehberlik için Just Delivery ile iletişime geçebilirsiniz.",
      description2: "Teslimat uygulamalarının nasıl çalıştığından veya nereden başlayacağınızdan emin olmasanız bile, bizimle iletişime geçmekten çekinmeyin.",
      helpWith: "Aşağıdaki konularda sorularınızı yanıtlamaktan mutluluk duyarız:",
      topics: [
        "Avustralya'da yemek teslimatı nasıl çalışır",
        "hangi uygulamalarla çalışabilirsiniz",
        "belgeler ve gereksinimler",
        "bisikletler, scooterlar ve yasal araçlar",
        "daha hızlı çalışmaya nasıl başlanır",
        "teslimat işi hakkında genel sorular"
      ],
      notRiderYet: "Henüz kurye olmanıza gerek yok.",
      exploring: "Meraklıysanız veya sadece seçeneklerinizi keşfediyorsanız, bizimle iletişime geçebilirsiniz.",
      ourGoal: "Amacımız basitçe insanların Avustralya'da teslimat işine doğru şekilde nasıl başlayacaklarını anlamalarına yardımcı olmaktır.",
      formPrompt: "Sorularınız varsa, aşağıdaki formu doldurun ve ekibimiz size geri dönecektir.",
      form: {
        firstName: "Ad",
        lastName: "Soyad",
        email: "E-posta Adresi",
        country: "Ülke",
        countryPlaceholder: "Ülkenizi seçin",
        city: "Şehir",
        cityPlaceholder: "Şehriniz (isteğe bağlı)",
        interest: "Neyle ilgileniyorsunuz?",
        interestPlaceholder: "Bir seçenek belirleyin",
        interestOptions: [
          "Teslimat işi bilgisi",
          "Kurye kaydı",
          "Araç tavsiyesi",
          "Genel sorular"
        ],
        message: "Mesaj",
        messagePlaceholder: "Sorunuzu veya mesajınızı buraya yazın...",
        submit: "Mesaj Gönder",
        sending: "Gönderiliyor..."
      },
      successTitle: "Mesaj başarıyla gönderildi.",
      successMessage: "Ekibimiz yakında sizinle iletişime geçecek."
    },
  },
  // Russian - Русский
  ru: {
    nav: {
      packages: "Пакеты",
      howItWorks: "Как это работает",
      leaderboard: "Рейтинг",
      info: "Информация",
      forShops: "Для магазинов",
      faq: "FAQ",
    },
    notices: {
      newRidersOnly: "ТОЛЬКО для НОВЫХ курьеров — ещё не зарегистрированных на Uber Eats или DoorDash (или хотя бы на одном)",
      newRidersShort: "Только новые курьеры — Не зарегистрированы на Uber Eats/DoorDash",
      newRidersImportant: "Важно: Этот сервис ТОЛЬКО для новых курьеров, ещё не зарегистрированных на Uber Eats или DoorDash (или хотя бы на одной из платформ)",
      premiumOnly: "Только Премиум Участники",
      leaderboardPremium: "Чтобы участвовать в рейтинге и выигрывать денежные бонусы, вы должны приобрести Премиум пакет",
    },
    hero: {
      headline: "Начните карьеру курьера в Австралии",
      subheadline: "Полная поддержка регистрации в Uber Eats и DoorDash. Зарегистрируйтесь, найдите транспорт и начните зарабатывать — с экспертным руководством.",
      cta1: "Начать",
      cta2: "Смотреть пакеты",
      badge: "Без риска • Легкий старт • Реальные бонусы",
    },
    packages: {
      title: "Выберите пакет",
      subtitle: "Разовый платеж. Без скрытых комиссий. Полная поддержка.",
      base: {
        name: "Базовый",
        price: "$49",
        description: "Идеально для самостоятельного старта",
        features: [
          "Подготовка заявки ABN (готова к финальной отправке)",
          "Пошаговое руководство по регистрации Uber/DoorDash",
          "Поддержка по email",
          "Реферальный бонус включен",
        ],
        cta: "Выбрать Базовый",
      },
      premium: {
        name: "Премиум",
        price: "$79",
        popular: "Самый популярный",
        description: "Полный сервис",
        features: [
          "Всё из Базового пакета",
          "Прямая координация с нашими надежными партнерами по аренде",
          "Мы подготовим и передадим ваши документы в магазин для ускорения настройки транспорта",
          "Мы помогаем сократить задержки при регистрации, чтобы вы быстрее начали доставлять",
          "Приоритетная поддержка WhatsApp",
        ],
        highlighted: "Минимальное обязательство по активности доставки 4 недели может применяться при использовании партнерских магазинов",
        cta: "Выбрать Премиум",
      },
    },
    howItWorks: {
      title: "Как это работает",
      subtitle: "Начните доставку за 4 простых шага",
      steps: [
        { number: "01", title: "Загрузите документы", description: "Отправьте ID, визу и права" },
        { number: "02", title: "Выберите пакет", description: "Базовый ($49) или Премиум ($79)" },
        { number: "03", title: "Завершите оплату", description: "Безопасный банковский перевод PayID" },
        { number: "04", title: "Начните зарабатывать", description: "Мы зарегистрируем вас в Uber/DoorDash" },
      ],
    },
    documents: {
      title: "Необходимые документы",
      subtitle: "Перед покупкой отправьте нам:",
      items: [
        { icon: "id", title: "Удостоверение личности", desc: "Действующий паспорт или ID-карта" },
        { icon: "car", title: "Водительские права", desc: "Необходимы только если вы планируете доставлять на скутере или мотоцикле. Доставка на электровелосипеде не требует прав. Для доставки на скутере/мотоцикле может потребоваться конвертация иностранных прав." },
        { icon: "file", title: "Действующая виза", desc: "Копия визы, разрешающей работу в Австралии" },
      ],
      licenseWarning: {
        title: "Важно: Требования к правам на скутер",
        nsw_vic: "NSW и VIC: Мотоциклетные права (класс R или RE) требуются для ЛЮБОГО скутера, независимо от объема двигателя.",
        qld: "QLD: С автомобильными правами (класс C) можно водить скутер до 50cc.",
      },
      licenseTitle: "Как конвертировать иностранные права",
      licenseSteps: [
        "Свяжитесь с транспортным департаментом вашего штата",
        "Предоставьте иностранные права и переводы",
        "Пройдите проверку и тесты (если требуется)",
        "Получите эквивалентные австралийские права",
      ],
      offices: [
        { city: "Брисбен (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" },
        { city: "Сидней (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" },
        { city: "Мельбурн (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" },
      ],
    },
    leaderboard: {
      title: "Соревнуйтесь и зарабатывайте больше",
      subtitle: "Каждую неделю и месяц",
      description: "Соревнуйтесь с другими курьерами и зарабатывайте дополнительные деньги!",
      comingSoon: "Рейтинг скоро",
      comingSoonText: "Лучшие исполнители получат эксклюзивные бонусы!",
      notifyBtn: "Уведомить меня",
      weekly: { title: "Недельный рейтинг", subtitle: "Недельный бонус лучшим", prize: "Недельный приз" },
      monthly: { title: "Месячный рейтинг", subtitle: "Большие награды за стабильность", prize: "Месячный приз" },
      tableHeaders: { rank: "Место", rider: "Курьер", deliveries: "Доставки", bonus: "Бонус" },
      sampleData: [
        { rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" },
        { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" },
        { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" },
        { rank: 4, name: "Вы?", deliveries: "—", bonus: "?" },
      ],
    },
    forShops: {
      title: "Станьте партнером",
      subtitle: "Вы магазин проката?",
      description: "Мы присылаем вам новых клиентов — без обязательств.",
      features: ["Получайте проверенных клиентов", "Без предоплаты", "Опциональная реферальная программа", "Прямой канал связи"],
      cta: "Стать партнером",
    },
    testimonials: {
      title: "Отзывы курьеров",
      items: [
        { name: "Marco R.", location: "Сидней", text: "Just Delivery сделал всё простым!", rating: 5 },
        { name: "Sofia L.", location: "Мельбурн", text: "Премиум пакет того стоил.", rating: 5 },
        { name: "Carlos M.", location: "Брисбен", text: "Отличная команда поддержки!", rating: 5 },
      ],
    },
    faq: {
      title: "Частые вопросы",
      items: [
        { q: "Кто может пользоваться этим сервисом?", a: "Этот сервис ТОЛЬКО для НОВЫХ курьеров, ещё не зарегистрированных на Uber Eats или DoorDash (или хотя бы на одной из платформ). Если вы уже зарегистрированный курьер, этот сервис не для вас." },
        { q: "Что входит в цену пакета?", a: "Разовый платеж за поддержку регистрации. Базовый включает ABN/Uber/DoorDash. Премиум добавляет приоритетную координацию с партнерами по аренде и быструю активацию." },
        { q: "Какие преимущества аренды Премиум?", a: "Пользователи Премиум получают приоритетную координацию с нашими партнерами по аренде. Поощрения могут варьироваться в зависимости от наличия." },
        { q: "Как работает рейтинг?", a: "Курьеры с Премиум пакетом соревнуются по количеству доставок. Лучшие получают денежные бонусы. Примечание: участие в рейтинге требует Премиум пакета." },
        { q: "Какие документы нужны?", a: "Паспорт/ID, австралийский телефон, банковский счет, права для аренды." },
      ],
    },
    contact: {
      title: "Готовы начать?",
      subtitle: "Загрузите документы и завершите оплату",
      form: {
        name: "Полное имя",
        email: "Email",
        phone: "Телефон",
        city: "Город",
        cityPlaceholder: "Выберите город",
        cities: ["Сидней", "Мельбурн", "Брисбен", "Перт", "Аделаида", "Голд-Кост"],
        package: "Пакет",
        packagePlaceholder: "Выберите пакет",
        documents: "Загрузить документы",
        documentsHint: "Загрузите: Паспорт/ID, Права, Визу (всё вместе)",
        submit: "Отправить и оплатить",
        processing: "Обработка...",
      },
      paymentSuccess: "Оплата успешна!",
      paymentSuccessText: "Спасибо! Мы свяжемся с вами в течение 24 часов.",
      paymentCancelled: "Оплата отменена",
      paymentCancelledText: "Ничего страшного! Можете попробовать снова, когда будете готовы.",
    },
    footer: {
      tagline: "Ваша карьера курьера начинается здесь.",
      copyright: "Just Delivery. Все права защищены.",
      privacyPolicy: "Политика конфиденциальности",
      termsOfService: "Условия использования",
      websiteDisclaimer: "Отказ от ответственности",
      refundPolicy: "Политика возврата",
    },
    ui: {
      guideLink: "Новичок в доставке? Читайте полное руководство",
      guideSection: {
        title: "Новичок в доставке еды в Австралии?",
        subtitle: "Начало работы курьером в Австралии может быть запутанным для новичков. Наше полное руководство объясняет требования, варианты транспорта и самые распространенные ошибки, которых следует избегать при регистрации на платформах вроде Uber Eats и DoorDash.",
        cta: "Читать полное руководство по доставке",
        chooseCity: "Выберите ваш город",
        sydneyDesc: "Крупнейший рынок Австралии с высоким спросом",
        melbourneDesc: "Гастрономическая столица с кофейной культурой",
        brisbaneDesc: "Солнечный климат и растущий рынок доставки",
      },
      joinCompetition: "Присоединяйтесь к соревнованию!",
      documentsSubmitted: "Документы отправлены!",
      thankYouSubmission: "Спасибо за отправку.",
      whatHappensNext: "Что дальше?",
      backToHome: "Вернуться на главную",
      uploadDocuments: "Загрузить документы",
      chooseFile: "Выбрать файл",
      noFileSelected: "Файл не выбран",
      processing: "Обработка...",
      submitReview: "Отправить на проверку",
      basePackage: "Базовый - $49 AUD",
      premiumPackage: "Премиум - $79 AUD",
    },
    contactUs: {
      title: "Связаться с Just Delivery",
      subtitle: "Есть вопросы о работе курьером в Австралии?",
      description: "Находитесь ли вы уже в Австралии или планируете приехать в ближайшее время, вы можете связаться с Just Delivery для получения информации и консультации.",
      description2: "Даже если вы не уверены, как работают приложения для доставки или с чего начать, не стесняйтесь обращаться к нам.",
      helpWith: "Мы рады ответить на вопросы о:",
      topics: [
        "как работает доставка еды в Австралии",
        "с какими приложениями можно работать",
        "документы и требования",
        "велосипеды, скутеры и легальный транспорт",
        "как начать работать быстрее",
        "общие вопросы о работе курьером"
      ],
      notRiderYet: "Вам не нужно быть курьером.",
      exploring: "Если вам интересно или вы просто изучаете варианты, мы будем рады вашему обращению.",
      ourGoal: "Наша цель — просто помочь людям понять, как правильно начать работу курьером в Австралии.",
      formPrompt: "Если у вас есть вопросы, заполните форму ниже, и наша команда свяжется с вами.",
      form: {
        firstName: "Имя",
        lastName: "Фамилия",
        email: "Email адрес",
        country: "Страна",
        countryPlaceholder: "Выберите вашу страну",
        city: "Город",
        cityPlaceholder: "Ваш город (необязательно)",
        interest: "Что вас интересует?",
        interestPlaceholder: "Выберите вариант",
        interestOptions: [
          "Информация о работе курьером",
          "Регистрация курьера",
          "Советы по транспорту",
          "Общие вопросы"
        ],
        message: "Сообщение",
        messagePlaceholder: "Напишите ваш вопрос или сообщение здесь...",
        submit: "Отправить сообщение",
        sending: "Отправка..."
      },
      successTitle: "Сообщение успешно отправлено.",
      successMessage: "Наша команда скоро свяжется с вами."
    },
  },
  // Chinese (Simplified) - 中文
  zh: {
    nav: { packages: "套餐", howItWorks: "如何运作", leaderboard: "排行榜", info: "信息", forShops: "合作商家", faq: "常见问题" },
    notices: { newRidersOnly: "仅限新骑手 — 尚未在Uber Eats或DoorDash注册（或至少其中一个）", newRidersShort: "仅限新骑手 — 未注册Uber Eats/DoorDash", newRidersImportant: "重要：此服务仅限尚未在Uber Eats或DoorDash注册的新骑手（或至少其中一个平台）", premiumOnly: "仅限高级会员", leaderboardPremium: "要参与排行榜并赢得现金奖金，您必须购买高级套餐" },
    hero: { headline: "在澳大利亚开始您的配送事业", subheadline: "Uber Eats和DoorDash完整注册支持。注册、找车、开始赚钱。", cta1: "立即开始", cta2: "查看套餐", badge: "零风险 • 轻松开始 • 真实奖金" },
    packages: { title: "选择您的套餐", subtitle: "一次性付款。无隐藏费用。全程支持。", base: { name: "基础版", price: "$49", description: "适合自主创业者", features: ["ABN申请准备（准备最终提交）", "Uber/DoorDash注册分步指南", "邮件支持", "包含推荐奖金"], cta: "选择基础版" }, premium: { name: "高级版", price: "$79", popular: "最受欢迎", description: "完整服务体验", features: ["基础套餐所有内容", "与我们信任的租赁合作伙伴直接协调", "我们准备并转发您的文件到车行以加快车辆设置", "我们帮助减少入职延迟，让您更快开始配送", "WhatsApp优先支持"], highlighted: "使用合作商家时可能需要承诺最少4周配送活动", cta: "选择高级版" } },
    howItWorks: { title: "如何运作", subtitle: "4个简单步骤开始配送", steps: [{ number: "01", title: "上传文件", description: "发送身份证、签证和驾照" }, { number: "02", title: "选择套餐", description: "基础版($49)或高级版($79)" }, { number: "03", title: "完成付款", description: "通过PayID安全银行转账" }, { number: "04", title: "开始赚钱", description: "我们指导您注册Uber/DoorDash" }] },
    documents: { title: "所需文件", subtitle: "购买前请发送给我们：", items: [{ icon: "id", title: "身份证明", desc: "有效护照或身份证" }, { icon: "car", title: "驾照", desc: "仅在使用踏板车或摩托车配送时需要。电动自行车配送无需驾照。踏板车/摩托车配送可能需要转换外国驾照。" }, { icon: "file", title: "有效签证", desc: "允许您在澳大利亚合法工作的签证副本" }], licenseWarning: { title: "重要：踏板车驾照要求", nsw_vic: "NSW和VIC：骑任何踏板车都需要摩托车驾照（R或RE级）。", qld: "QLD：持汽车驾照（C级）可骑50cc以下踏板车。" }, licenseTitle: "如何转换外国驾照", licenseSteps: ["联系您所在州的交通管理局", "提交外国驾照和所需翻译件", "完成验证和测试（如需）", "获得等效的澳大利亚驾照"], offices: [{ city: "布里斯班 (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" }, { city: "悉尼 (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" }, { city: "墨尔本 (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" }] },
    leaderboard: { title: "竞争赢取额外现金", subtitle: "每周每月", description: "与其他骑手竞争，赢取额外现金！", comingSoon: "排行榜即将推出", comingSoonText: "顶尖骑手将赢得独家奖金！", notifyBtn: "通知我", weekly: { title: "周排行榜", subtitle: "顶尖表现者周奖金", prize: "周奖" }, monthly: { title: "月排行榜", subtitle: "持续表现者更高奖励", prize: "月奖" }, tableHeaders: { rank: "排名", rider: "骑手", deliveries: "配送数", bonus: "奖金" }, sampleData: [{ rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" }, { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" }, { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" }, { rank: 4, name: "你？", deliveries: "—", bonus: "?" }] },
    forShops: { title: "与我们合作", subtitle: "您是租赁店或车辆提供商？", description: "我们为您送来新客户——无需承诺。", features: ["获得预筛选骑手线索", "无前期费用", "可选推荐奖金计划", "直接沟通渠道"], cta: "成为合作伙伴" },
    testimonials: { title: "骑手评价", items: [{ name: "Marco R.", location: "悉尼", text: "Just Delivery让一切变得简单！", rating: 5 }, { name: "Sofia L.", location: "墨尔本", text: "高级套餐物超所值。", rating: 5 }, { name: "Carlos M.", location: "布里斯班", text: "支持团队太棒了！", rating: 5 }] },
    faq: { title: "常见问题", items: [{ q: "谁可以使用此服务？", a: "此服务仅限尚未在Uber Eats或DoorDash注册的新骑手（或至少其中一个平台）。如果您已是注册骑手，此服务不适合您。" }, { q: "套餐价格包含什么？", a: "一次性付费包含注册支持。基础版包括ABN/Uber/DoorDash指导。高级版增加优先租赁合作伙伴协调和更快激活。" }, { q: "需要警察检查吗？", a: "部分平台可能需要警察检查。此检查由平台安排（如Uber可能收取约$49澳元）。此费用不包含在Just Delivery服务中。" }, { q: "高级版租赁优惠是什么？", a: "高级版用户获得与我们租赁合作伙伴的优先协调。视供应商情况可享受20%折扣。" }, { q: "需要什么文件？", a: "护照/身份证、澳大利亚电话号码、银行口座、租车需驾照。" }] },
    contact: { title: "准备好开始了吗？", subtitle: "上传文件并完成付款", form: { name: "全名", email: "电子邮箱", phone: "电话号码", city: "城市", cityPlaceholder: "选择城市", cities: ["悉尼", "墨尔本", "布里斯班", "珀斯", "阿德莱德", "黄金海岸"], package: "套餐", packagePlaceholder: "选择套餐", documents: "上传文件", documentsHint: "上传：护照/身份证、驾照、签证", submit: "提交并付款", processing: "处理中..." }, paymentSuccess: "付款成功！", paymentSuccessText: "感谢您！我们将在24小时内联系您。", paymentCancelled: "付款已取消", paymentCancelledText: "没关系！您可以随时重试。" },
    footer: { tagline: "您的配送事业从这里开始。", copyright: "Just Delivery. 保留所有权利。", privacyPolicy: "隐私政策", termsOfService: "服务条款", websiteDisclaimer: "网站免责声明", refundPolicy: "退款政策" },
    ui: { guideLink: "配送新手？阅读完整指南", guideSection: { title: "刚接触澳大利亚外卖配送？", subtitle: "在澳大利亚开始配送工作对新骑手来说可能会令人困惑。我们的完整指南解释了要求、车辆选择以及在Uber Eats和DoorDash等平台注册时需要避免的最常见错误。", cta: "阅读完整配送指南", chooseCity: "选择您的城市", sydneyDesc: "澳大利亚最大的市场，需求旺盛", melbourneDesc: "美食之都，咖啡文化繁荣", brisbaneDesc: "阳光明媚的气候，配送市场增长迅速" }, joinCompetition: "加入竞赛！", documentsSubmitted: "文件已提交！", thankYouSubmission: "感谢您的提交。", whatHappensNext: "接下来是什么？", backToHome: "返回首页", uploadDocuments: "上传文件", chooseFile: "选择文件", noFileSelected: "未选择文件", processing: "处理中...", submitReview: "提交审核", basePackage: "基础版 - $49 AUD", premiumPackage: "高级版 - $79 AUD" },
    contactUs: { title: "联系 Just Delivery", subtitle: "关于在澳大利亚从事配送工作有疑问？", description: "无论您已在澳大利亚还是计划不久后到达，都可以联系 Just Delivery 获取信息和指导。", description2: "即使您不确定配送应用如何运作或从何处开始，也请随时与我们联系。", helpWith: "我们很乐意回答以下问题：", topics: ["澳大利亚的外卖配送如何运作", "您可以使用哪些应用", "文件和要求", "自行车、摩托车和合法车辆", "如何更快开始工作", "关于配送工作的一般问题"], notRiderYet: "您还不需要成为骑手。", exploring: "如果您感到好奇或只是在探索选择，欢迎联系我们。", ourGoal: "我们的目标只是帮助人们了解如何正确开始在澳大利亚的配送工作。", formPrompt: "如果您有问题，请填写下面的表格，我们的团队会回复您。", form: { firstName: "名字", lastName: "姓氏", email: "电子邮箱", country: "国家", countryPlaceholder: "选择您的国家", city: "城市", cityPlaceholder: "您的城市（可选）", interest: "您对什么感兴趣？", interestPlaceholder: "选择一个选项", interestOptions: ["配送工作信息", "骑手入职", "车辆建议", "一般问题"], message: "留言", messagePlaceholder: "在此写下您的问题或留言...", submit: "发送消息", sending: "发送中..." }, successTitle: "消息发送成功。", successMessage: "我们的团队将尽快与您联系。" },
  },
  // Japanese - 日本語
  ja: {
    nav: { packages: "パッケージ", howItWorks: "仕組み", leaderboard: "リーダーボード", info: "情報", forShops: "ショップ向け", faq: "FAQ" },
    notices: { newRidersOnly: "新規ライダー限定 — まだUber EatsまたはDoorDashに登録していない方（または少なくとも一方）", newRidersShort: "新規ライダー限定 — Uber Eats/DoorDash未登録", newRidersImportant: "重要：このサービスはまだUber EatsまたはDoorDashに登録していない新規ライダー専用です（または少なくとも一方のプラットフォーム）", premiumOnly: "プレミアム会員限定", leaderboardPremium: "リーダーボードに参加してキャッシュボーナスを獲得するには、プレミアムパッケージを購入する必要があります" },
    hero: { headline: "オーストラリアでデリバリーキャリアを始めよう", subheadline: "Uber EatsとDoorDashの登録を完全サポート。登録、車両探し、収入開始まで。", cta1: "今すぐ始める", cta2: "パッケージを見る", badge: "リスクなし • 簡単スタート • 本物のボーナス" },
    packages: { title: "パッケージを選択", subtitle: "一回払い。隠れた費用なし。フルサポート。", base: { name: "ベーシック", price: "$49", description: "自分で始めたい方に最適", features: ["ABN申請準備（最終提出準備完了）", "Uber/DoorDash登録のステップバイステップガイド", "メールサポート", "紹介ボーナス込み"], cta: "ベーシックを選ぶ" }, premium: { name: "プレミアム", price: "$79", popular: "人気No.1", description: "フルサービス体験", features: ["ベーシックの全て込み", "信頼できるレンタルパートナーとの直接調整", "車両セットアップを迅速化するため書類を準備しショップに転送", "オンボーディングの遅延を減らし、より早く配達を開始できるようサポート", "WhatsApp優先サポート"], highlighted: "パートナーショップ利用時は最低4週間の配達活動コミットメントが適用される場合があります", cta: "プレミアムを選ぶ" } },
    howItWorks: { title: "仕組み", subtitle: "4つの簡単ステップでデリバリー開始", steps: [{ number: "01", title: "書類アップロード", description: "ID、ビザ、免許証を送信" }, { number: "02", title: "パッケージ選択", description: "ベーシック($49)またはプレミアム($79)" }, { number: "03", title: "支払い完了", description: "PayIDで安全な銀行振込" }, { number: "04", title: "収入開始", description: "Uber/DoorDash登録をサポート" }] },
    documents: { title: "必要書類", subtitle: "購入前にお送りください：", items: [{ icon: "id", title: "身分証明", desc: "有効なパスポートまたはIDカード" }, { icon: "car", title: "運転免許", desc: "スクーターまたはバイクで配達する場合のみ必要。電動バイク配達は免許不要。スクーター/バイク配達は外国免許の変換が必要な場合があります。" }, { icon: "file", title: "有効なビザ", desc: "オーストラリアで働くことを許可するビザのコピー" }], licenseWarning: { title: "重要：スクーター免許要件", nsw_vic: "NSW & VIC：すべてのスクーターにバイク免許（RまたはREクラス）が必要。", qld: "QLD：普通免許（Cクラス）で50ccまでのスクーターを運転可能。" }, licenseTitle: "外国免許の変換方法", licenseSteps: ["お住まいの州の交通局に連絡", "外国免許と翻訳を提出", "確認とテストを完了（必要な場合）", "同等のオーストラリア免許を取得"], offices: [{ city: "ブリスベン (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" }, { city: "シドニー (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" }, { city: "メルボルン (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" }] },
    leaderboard: { title: "競争して追加収入", subtitle: "毎週・毎月", description: "他のライダーと競争して追加キャッシュを獲得！", comingSoon: "リーダーボード近日公開", comingSoonText: "トップパフォーマーは限定ボーナスを獲得！", notifyBtn: "通知を受け取る", weekly: { title: "週間リーダーボード", subtitle: "トップパフォーマーに週間ボーナス", prize: "週間賞" }, monthly: { title: "月間リーダーボード", subtitle: "継続的なパフォーマーにより大きな報酬", prize: "月間賞" }, tableHeaders: { rank: "順位", rider: "ライダー", deliveries: "配達数", bonus: "ボーナス" }, sampleData: [{ rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" }, { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" }, { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" }, { rank: 4, name: "あなた？", deliveries: "—", bonus: "?" }] },
    forShops: { title: "パートナーになる", subtitle: "レンタルショップですか？", description: "新規顧客をお送りします — コミットメント不要。", features: ["事前審査済みライダーリードを取得", "初期費用なし", "オプションの紹介ボーナスプログラム", "直接コミュニケーションチャンネル"], cta: "パートナーになる" },
    testimonials: { title: "ライダーの声", items: [{ name: "Marco R.", location: "シドニー", text: "Just Deliveryですべてが簡単になりました！", rating: 5 }, { name: "Sofia L.", location: "メルボルン", text: "プレミアムパッケージは価値がありました。", rating: 5 }, { name: "Carlos M.", location: "ブリスベン", text: "素晴らしいサポートチーム！", rating: 5 }] },
    faq: { title: "よくある質問", items: [{ q: "誰がこのサービスを使えますか？", a: "このサービスはUber EatsまたはDoorDashにまだ登録していない新しいライダー専用です（少なくとも一方のプラットフォーム）。すでに登録済みのライダーの場合、このサービスは対象外です。" }, { q: "パッケージ料金に何が含まれますか？", a: "登録サポートの一回払い。ベーシックはABN/Uber/DoorDashを含む。プレミアムは優先レンタルパートナー調整と迅速なアクティベーションを追加。" }, { q: "警察チェックは必要ですか？", a: "一部のプラットフォームでは警察チェックが必要な場合があります。これはプラットフォームを通じて手配されます（例：Uberは約$49豪ドルを請求する場合があります）。この費用はJust Deliveryサービスに含まれていません。" }, { q: "プレミアムのレンタル特典とは？", a: "プレミアムユーザーはレンタルパートナーとの優先調整が可能。20%割引が適用される場合があります（空き状況による）。" }, { q: "どんな書類が必要？", a: "パスポート/ID、オーストラリアの電話番号、銀行口座、レンタル用免許。" }] },
    contact: { title: "始める準備はできましたか？", subtitle: "書類をアップロードして支払いを完了", form: { name: "氏名", email: "メール", phone: "電話番号", city: "都市", cityPlaceholder: "都市を選択", cities: ["シドニー", "メルボルン", "ブリスベン", "パース", "アデレード", "ゴールドコースト"], package: "パッケージ", packagePlaceholder: "パッケージを選択", documents: "書類アップロード", documentsHint: "アップロード：パスポート/ID、免許証、ビザ", submit: "送信して支払う", processing: "処理中..." }, paymentSuccess: "支払い完了！", paymentSuccessText: "ありがとうございます！24時間以内にご連絡します。", paymentCancelled: "支払いキャンセル", paymentCancelledText: "問題ありません！準備ができたら再度お試しください。" },
    footer: { tagline: "デリバリーキャリアはここから始まります。", copyright: "Just Delivery. All rights reserved.", privacyPolicy: "プライバシーポリシー", termsOfService: "利用規約", websiteDisclaimer: "免責事項", refundPolicy: "返金ポリシー" },
    ui: { guideLink: "デリバリー初心者？完全ガイドを読む", guideSection: { title: "オーストラリアでのフードデリバリーは初めてですか？", subtitle: "オーストラリアで配達の仕事を始めることは、新しいライダーにとって混乱を招くことがあります。私たちの完全ガイドでは、要件、車両オプション、Uber EatsやDoorDashなどのプラットフォームに登録する際に避けるべき最も一般的な間違いを説明しています。", cta: "配達ガイドの全文を読む", chooseCity: "都市を選択", sydneyDesc: "需要が高いオーストラリア最大の市場", melbourneDesc: "カフェ文化が栄える美食の都", brisbaneDesc: "晴れやかな気候と成長するデリバリー市場" }, joinCompetition: "競争に参加！", documentsSubmitted: "書類が送信されました！", thankYouSubmission: "送信ありがとうございます。", whatHappensNext: "次は何？", backToHome: "ホームに戻る", uploadDocuments: "書類をアップロード", chooseFile: "ファイルを選択", noFileSelected: "ファイルが選択されていません", processing: "処理中...", submitReview: "審査に送信", basePackage: "ベーシック - $49 AUD", premiumPackage: "プレミアム - $79 AUD" },
    contactUs: { title: "Just Deliveryに連絡", subtitle: "オーストラリアでの配達の仕事について質問がありますか？", description: "すでにオーストラリアにいる方も、近々到着予定の方も、Just Deliveryに連絡して情報やガイダンスを得ることができます。", description2: "配達アプリの仕組みや始め方がわからなくても、お気軽にお問い合わせください。", helpWith: "以下の質問にお答えします：", topics: ["オーストラリアでのフードデリバリーの仕組み", "どのアプリで働けるか", "書類と要件", "自転車、スクーター、合法な車両", "より早く仕事を始める方法", "配達の仕事に関する一般的な質問"], notRiderYet: "まだライダーである必要はありません。", exploring: "興味がある方や、選択肢を探っている方も、お気軽にお問い合わせください。", ourGoal: "私たちの目標は、オーストラリアで配達の仕事を正しく始める方法を理解していただくことです。", formPrompt: "ご質問がありましたら、以下のフォームにご記入ください。チームからご連絡いたします。", form: { firstName: "名", lastName: "姓", email: "メールアドレス", country: "国", countryPlaceholder: "国を選択", city: "都市", cityPlaceholder: "お住まいの都市（任意）", interest: "何に興味がありますか？", interestPlaceholder: "オプションを選択", interestOptions: ["配達の仕事の情報", "ライダー登録", "車両アドバイス", "一般的な質問"], message: "メッセージ", messagePlaceholder: "ここに質問やメッセージを書いてください...", submit: "メッセージを送信", sending: "送信中..." }, successTitle: "メッセージが正常に送信されました。", successMessage: "チームから近日中にご連絡いたします。" },
  },
  // Portuguese - Português
  pt: {
    nav: { packages: "Pacotes", howItWorks: "Como Funciona", leaderboard: "Classificação", info: "Info", forShops: "Para Lojas", faq: "FAQ" },
    notices: { newRidersOnly: "Apenas para NOVOS entregadores — ainda não registados no Uber Eats ou DoorDash (ou pelo menos um deles)", newRidersShort: "Apenas novos entregadores — Não registados no Uber Eats/DoorDash", newRidersImportant: "Importante: Este serviço é APENAS para novos entregadores ainda não registados no Uber Eats ou DoorDash (ou pelo menos uma das plataformas)", premiumOnly: "Apenas Membros Premium", leaderboardPremium: "Para participar na classificação e ganhar bónus em dinheiro, deve ter adquirido o pacote Premium" },
    hero: { headline: "Comece a Sua Carreira de Entregas na Austrália", subheadline: "Suporte completo de registo para Uber Eats e DoorDash. Registe-se, encontre um veículo e comece a ganhar — tudo com orientação especializada.", cta1: "Começar", cta2: "Ver Pacotes", badge: "Sem Risco • Início Fácil • Bónus Reais" },
    packages: { title: "Escolha o Seu Pacote", subtitle: "Pagamento único. Sem taxas ocultas. Suporte completo.", base: { name: "Básico", price: "$49", description: "Perfeito para quem quer começar sozinho", features: ["Preparação do pedido ABN (pronto para submissão final)", "Guia passo a passo para registo Uber/DoorDash", "Suporte por email", "Bónus de referência incluído"], cta: "Escolher Básico" }, premium: { name: "Premium", price: "$79", popular: "Mais Popular", description: "Experiência de serviço completo", features: ["Tudo no pacote Básico", "Coordenação direta com os nossos parceiros de aluguer de confiança", "Preparamos e encaminhamos os seus documentos para a loja para acelerar a configuração do veículo", "Ajudamos a reduzir atrasos no onboarding para começar a entregar mais rápido", "Suporte prioritário WhatsApp"], highlighted: "Compromisso mínimo de 4 semanas de atividade de entregas pode aplicar-se ao usar lojas parceiras", cta: "Escolher Premium" } },
    howItWorks: { title: "Como Funciona", subtitle: "Comece a entregar em 4 passos simples", steps: [{ number: "01", title: "Carregar Documentos", description: "Envie-nos o seu ID, visto e carta de condução" }, { number: "02", title: "Escolher Pacote", description: "Selecione Básico ($49) ou Premium ($79)" }, { number: "03", title: "Completar Pagamento", description: "Transferência bancária segura via PayID" }, { number: "04", title: "Começar a Ganhar", description: "Guiamo-lo através da configuração Uber/DoorDash" }] },
    documents: { title: "Documentos Necessários", subtitle: "Para iniciar a sua análise de elegibilidade, por favor carregue os seguintes documentos:", items: [{ icon: "id", title: "Prova de Identidade", desc: "Passaporte válido ou cartão de identificação" }, { icon: "car", title: "Carta de Condução", desc: "Apenas necessária se planeia entregar usando scooter ou mota. Entrega em e-bike não requer carta. Entrega em scooter/mota pode requerer conversão de carta estrangeira." }, { icon: "file", title: "Visto Válido", desc: "Cópia do visto que permite trabalhar legalmente na Austrália" }], licenseWarning: { title: "Importante: Requisitos de Carta para Scooter", nsw_vic: "NSW & VIC: Carta de mota (classe R ou RE) necessária para conduzir QUALQUER scooter, independentemente da cilindrada.", qld: "QLD: Com carta de carro (classe C) pode conduzir scooters até 50cc apenas." }, licenseTitle: "Como Converter Carta Estrangeira", licenseSteps: ["Contacte o Departamento de Transportes do seu estado", "Apresente a sua carta estrangeira e traduções necessárias", "Complete verificação e testes (se necessário)", "Receba a sua carta australiana equivalente"], offices: [{ city: "Brisbane (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" }, { city: "Sydney (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" }, { city: "Melbourne (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" }] },
    leaderboard: { title: "Compita e Ganhe Dinheiro Extra", subtitle: "Todas as Semanas e Meses", description: "Compita com outros entregadores e ganhe dinheiro extra todas as semanas e meses!", comingSoon: "Classificação Em Breve", comingSoonText: "Os melhores performers ganharão bónus exclusivos. Seja dos primeiros a competir!", notifyBtn: "Notificar-me Quando Ativo", weekly: { title: "Classificação Semanal", subtitle: "Bónus semanal para os melhores performers", prize: "Prémio Semanal" }, monthly: { title: "Classificação Mensal", subtitle: "Recompensas maiores para performers consistentes", prize: "Prémio Mensal" }, tableHeaders: { rank: "Posição", rider: "Entregador", deliveries: "Entregas", bonus: "Bónus" }, sampleData: [{ rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" }, { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" }, { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" }, { rank: 4, name: "Você?", deliveries: "—", bonus: "?" }] },
    forShops: { title: "Torne-se Parceiro", subtitle: "É uma loja de aluguer ou fornecedor de veículos?", description: "Enviamos-lhe novos clientes — sem compromisso.", features: ["Receba leads de entregadores pré-qualificados", "Sem custos iniciais", "Programa de bónus de referência opcional", "Canal de comunicação direto"], cta: "Tornar-se Parceiro" },
    testimonials: { title: "O Que Dizem os Entregadores", items: [{ name: "Marco R.", location: "Sydney", text: "Just Delivery tornou tudo fácil!", rating: 5 }, { name: "Sofia L.", location: "Melbourne", text: "O pacote Premium valeu a pena.", rating: 5 }, { name: "Carlos M.", location: "Brisbane", text: "Equipa de suporte fantástica!", rating: 5 }] },
    faq: { title: "Perguntas Frequentes", items: [{ q: "Quem pode usar este serviço?", a: "Este serviço é APENAS para novos entregadores que ainda não se registaram no Uber Eats ou DoorDash (ou pelo menos uma das plataformas). Se já é um entregador registado, este serviço não é para si." }, { q: "O que está incluído no preço do pacote?", a: "Pagamento único para suporte de registo. Básico inclui orientação ABN/Uber/DoorDash. Premium adiciona coordenação prioritária com parceiro de aluguer e ativação mais rápida." }, { q: "É necessária verificação policial?", a: "Algumas plataformas podem requerer verificação policial. Isto é organizado através da plataforma (ex: Uber pode cobrar ~$49 AUD). Esta taxa não está incluída no serviço Just Delivery." }, { q: "O que é o benefício de aluguer Premium?", a: "Utilizadores Premium obtêm coordenação prioritária com os nossos parceiros de aluguer. O desconto de 20% pode aplicar-se sujeito à disponibilidade do fornecedor." }, { q: "Quais documentos são necessários?", a: "Passaporte/ID, número de telefone australiano, conta bancária, carta de condução para aluguer." }] },
    contact: { title: "Pronto para Começar?", subtitle: "Carregue os seus documentos e complete o pagamento", form: { name: "Nome Completo", email: "Email", phone: "Número de Telefone", city: "Cidade", cityPlaceholder: "Selecione a cidade", cities: ["Sydney", "Melbourne", "Brisbane"], package: "Pacote", packagePlaceholder: "Selecione o pacote", documents: "Carregar Documentos", documentsHint: "Carregar: Passaporte/ID, Carta de Condução, Visto", submit: "Submeter e Pagar", processing: "A processar..." }, paymentSuccess: "Pagamento Bem Sucedido!", paymentSuccessText: "Obrigado! Contactaremos em 24 horas.", paymentCancelled: "Pagamento Cancelado", paymentCancelledText: "Sem problema! Tente novamente quando estiver pronto." },
    footer: { tagline: "A sua carreira de entregas começa aqui.", copyright: "Just Delivery. Todos os direitos reservados.", privacyPolicy: "Política de Privacidade", termsOfService: "Termos de Serviço", websiteDisclaimer: "Aviso Legal do Website", refundPolicy: "Política de Reembolso" },
    ui: { guideLink: "Novo nas entregas? Leia o guia completo", guideSection: { title: "Novo em Entregas de Comida na Austrália?", subtitle: "Começar a trabalhar em entregas na Austrália pode ser confuso para novos entregadores. O nosso guia completo explica os requisitos, opções de veículos e os erros mais comuns a evitar ao registar-se em plataformas como Uber Eats e DoorDash.", cta: "Ler o Guia Completo de Entregas", chooseCity: "Escolha a Sua Cidade", sydneyDesc: "O maior mercado da Austrália com alta demanda", melbourneDesc: "Capital gastronómica com cultura de café", brisbaneDesc: "Clima ensolarado e mercado em crescimento" }, joinCompetition: "Junte-se à Competição!", documentsSubmitted: "Documentos Submetidos!", thankYouSubmission: "Obrigado pela sua submissão.", whatHappensNext: "O que acontece a seguir?", backToHome: "Voltar ao Início", uploadDocuments: "Carregar Documentos", chooseFile: "Escolher arquivo", noFileSelected: "Nenhum arquivo selecionado", processing: "A processar...", submitReview: "Submeter para Análise", basePackage: "Básico - $49 AUD", premiumPackage: "Premium - $79 AUD" },
    contactUs: { title: "Contactar Just Delivery", subtitle: "Questões sobre trabalho de entregas na Austrália?", description: "Quer já esteja na Austrália ou planeie chegar em breve, pode contactar Just Delivery para informações e orientação.", description2: "Mesmo que não tenha a certeza de como funcionam as apps de entregas ou por onde começar, não hesite em contactar-nos.", helpWith: "Temos todo o gosto em responder a questões sobre:", topics: ["como funciona a entrega de comida na Austrália", "com que apps pode trabalhar", "documentos e requisitos", "bicicletas, scooters e veículos legais", "como começar a trabalhar mais rápido", "questões gerais sobre trabalho de entregas"], notRiderYet: "Não precisa de ser entregador ainda.", exploring: "Se está curioso ou apenas a explorar as suas opções, é bem-vindo a contactar-nos.", ourGoal: "O nosso objetivo é simplesmente ajudar as pessoas a compreender como começar o trabalho de entregas na Austrália da forma correta.", formPrompt: "Se tem questões, preencha o formulário abaixo e a nossa equipa entrará em contacto consigo.", form: { firstName: "Primeiro Nome", lastName: "Apelido", email: "Endereço de Email", country: "País", countryPlaceholder: "Selecione o seu país", city: "Cidade", cityPlaceholder: "A sua cidade (opcional)", interest: "Em que está interessado?", interestPlaceholder: "Selecione uma opção", interestOptions: ["Informações sobre trabalho de entregas", "Registo de entregador", "Conselhos sobre veículos", "Questões gerais"], message: "Mensagem", messagePlaceholder: "Escreva a sua questão ou mensagem aqui...", submit: "Enviar Mensagem", sending: "A enviar..." }, successTitle: "Mensagem enviada com sucesso.", successMessage: "A nossa equipa entrará em contacto consigo em breve." },
  },
  // Korean - 한국어
  ko: {
    nav: { packages: "패키지", howItWorks: "이용 방법", leaderboard: "리더보드", info: "정보", forShops: "매장용", faq: "FAQ" },
    notices: { newRidersOnly: "신규 라이더 전용 — 아직 Uber Eats 또는 DoorDash에 등록하지 않은 분 (또는 둘 중 하나)", newRidersShort: "신규 라이더 전용 — Uber Eats/DoorDash 미등록자", newRidersImportant: "중요: 이 서비스는 아직 Uber Eats 또는 DoorDash에 등록하지 않은 신규 라이더 전용입니다 (또는 두 플랫폼 중 하나)", premiumOnly: "프리미엄 회원 전용", leaderboardPremium: "리더보드에 참여하고 현금 보너스를 받으려면 프리미엄 패키지를 구매해야 합니다" },
    hero: { headline: "호주에서 배달 커리어를 시작하세요", subheadline: "Uber Eats & DoorDash 완전 등록 지원. 등록, 차량 찾기, 수익 시작까지 — 전문가 안내와 함께.", cta1: "시작하기", cta2: "패키지 보기", badge: "위험 없음 • 쉬운 시작 • 실제 보너스" },
    packages: { title: "패키지 선택", subtitle: "일회성 결제. 숨겨진 비용 없음. 완전 지원.", base: { name: "베이직", price: "$49", description: "스스로 시작하려는 분께 완벽", features: ["ABN 신청 준비 (최종 제출 준비 완료)", "Uber/DoorDash 등록 단계별 가이드", "이메일 지원", "추천 보너스 포함"], cta: "베이직 선택" }, premium: { name: "프리미엄", price: "$79", popular: "가장 인기", description: "풀 서비스 경험", features: ["베이직 패키지 전체 포함", "신뢰할 수 있는 렌탈 파트너와 직접 조정", "차량 설정을 빠르게 하기 위해 서류를 준비하고 매장에 전달", "온보딩 지연을 줄여 더 빨리 배달을 시작할 수 있도록 지원", "WhatsApp 우선 지원"], highlighted: "파트너 매장 이용 시 최소 4주 배달 활동 약정이 적용될 수 있음", cta: "프리미엄 선택" } },
    howItWorks: { title: "이용 방법", subtitle: "4단계로 간단하게 배달 시작", steps: [{ number: "01", title: "서류 업로드", description: "신분증, 비자, 면허증 보내기" }, { number: "02", title: "패키지 선택", description: "베이직 ($49) 또는 프리미엄 ($79)" }, { number: "03", title: "결제 완료", description: "PayID를 통한 안전한 은행 이체" }, { number: "04", title: "수익 시작", description: "Uber/DoorDash 설정을 안내해 드립니다" }] },
    documents: { title: "필요 서류", subtitle: "자격 심사를 시작하려면 다음 서류를 업로드해 주세요:", items: [{ icon: "id", title: "신분 증명", desc: "유효한 여권 또는 신분증" }, { icon: "car", title: "운전면허증", desc: "스쿠터나 오토바이로 배달할 계획인 경우에만 필요. 전기자전거 배달은 면허 불필요. 스쿠터/오토바이 배달은 외국 면허 전환이 필요할 수 있음." }, { icon: "file", title: "유효한 비자", desc: "호주에서 합법적으로 일할 수 있는 비자 사본" }], licenseWarning: { title: "중요: 스쿠터 면허 요건", nsw_vic: "NSW & VIC: 배기량에 관계없이 모든 스쿠터를 운전하려면 오토바이 면허 (R 또는 RE 클래스) 필요.", qld: "QLD: 자동차 면허 (C 클래스)로 50cc 이하 스쿠터만 운전 가능." }, licenseTitle: "외국 면허 전환 방법", licenseSteps: ["해당 주의 교통부에 연락", "외국 면허와 필요한 번역본 제출", "확인 및 테스트 완료 (필요시)", "동등한 호주 면허 취득"], offices: [{ city: "브리즈번 (QLD)", name: "Transport and Main Roads", address: "201 Charlotte St, Brisbane QLD 4000" }, { city: "시드니 (NSW)", name: "Service NSW", address: "55 King St, Sydney NSW 2000" }, { city: "멜버른 (VIC)", name: "VicRoads", address: "60 Denmark St, Kew VIC 3101" }] },
    leaderboard: { title: "경쟁하고 추가 수익 얻기", subtitle: "매주 & 매월", description: "다른 라이더와 경쟁하고 매주 매월 추가 현금을 벌어보세요!", comingSoon: "리더보드 곧 출시", comingSoonText: "최고 성과자는 독점 보너스를 받게 됩니다. 가장 먼저 경쟁에 참여하세요!", notifyBtn: "라이브되면 알림 받기", weekly: { title: "주간 리더보드", subtitle: "최고 성과자를 위한 주간 보너스", prize: "주간 상금" }, monthly: { title: "월간 리더보드", subtitle: "꾸준한 성과자를 위한 더 큰 보상", prize: "월간 상금" }, tableHeaders: { rank: "순위", rider: "라이더", deliveries: "배달", bonus: "보너스" }, sampleData: [{ rank: 1, name: "Alex M.", deliveries: 156, bonus: "$50" }, { rank: 2, name: "Sofia R.", deliveries: 142, bonus: "$30" }, { rank: 3, name: "Marco T.", deliveries: 128, bonus: "$20" }, { rank: 4, name: "당신?", deliveries: "—", bonus: "?" }] },
    forShops: { title: "파트너 되기", subtitle: "렌탈샵 또는 차량 공급업체이신가요?", description: "새로운 고객을 보내드립니다 — 약정 없음.", features: ["사전 심사된 라이더 리드 받기", "선불 비용 없음", "선택적 추천 보너스 프로그램", "직접 커뮤니케이션 채널"], cta: "파트너 되기" },
    testimonials: { title: "라이더들의 이야기", items: [{ name: "Marco R.", location: "시드니", text: "Just Delivery로 모든 게 쉬워졌어요!", rating: 5 }, { name: "Sofia L.", location: "멜버른", text: "프리미엄 패키지는 그만한 가치가 있었어요.", rating: 5 }, { name: "Carlos M.", location: "브리즈번", text: "훌륭한 지원팀!", rating: 5 }] },
    faq: { title: "자주 묻는 질문", items: [{ q: "이 서비스를 누가 사용할 수 있나요?", a: "이 서비스는 아직 Uber Eats 또는 DoorDash에 등록하지 않은 신규 라이더 전용입니다 (또는 두 플랫폼 중 하나). 이미 등록된 라이더라면 이 서비스는 해당되지 않습니다." }, { q: "패키지 가격에 무엇이 포함되나요?", a: "등록 지원을 위한 일회성 결제. 베이직은 ABN/Uber/DoorDash 안내 포함. 프리미엄은 우선 렌탈 파트너 조정과 빠른 활성화 추가." }, { q: "경찰 신원조회가 필요한가요?", a: "일부 플랫폼에서 경찰 신원조회가 필요할 수 있습니다. 이는 플랫폼을 통해 준비됩니다 (예: Uber는 ~$49 AUD 부과 가능). 이 비용은 Just Delivery 서비스에 포함되지 않습니다." }, { q: "프리미엄 렌탈 혜택은 무엇인가요?", a: "프리미엄 사용자는 렌탈 파트너와 우선 조정을 받습니다. 공급업체 가용성에 따라 20% 할인이 적용될 수 있습니다." }, { q: "어떤 서류가 필요한가요?", a: "여권/신분증, 호주 전화번호, 은행 계좌, 렌탈용 운전면허증." }] },
    contact: { title: "시작할 준비 되셨나요?", subtitle: "서류를 업로드하고 결제를 완료하세요", form: { name: "성명", email: "이메일", phone: "전화번호", city: "도시", cityPlaceholder: "도시 선택", cities: ["시드니", "멜버른", "브리즈번", "퍼스", "애들레이드", "골드코스트"], package: "패키지", packagePlaceholder: "패키지 선택", documents: "서류 업로드", documentsHint: "업로드: 여권/신분증, 운전면허증, 비자", submit: "제출 및 결제", processing: "처리 중..." }, paymentSuccess: "결제 성공!", paymentSuccessText: "감사합니다! 24시간 이내에 연락드리겠습니다.", paymentCancelled: "결제 취소됨", paymentCancelledText: "괜찮습니다! 준비되면 다시 시도하세요." },
    footer: { tagline: "배달 커리어가 여기서 시작됩니다.", copyright: "Just Delivery. All rights reserved.", privacyPolicy: "개인정보 처리방침", termsOfService: "서비스 약관", websiteDisclaimer: "웹사이트 면책조항", refundPolicy: "환불 정책" },
    ui: { guideLink: "배달이 처음이신가요? 전체 가이드 읽기", guideSection: { title: "호주에서 음식 배달이 처음이신가요?", subtitle: "호주에서 배달 일을 시작하는 것은 새로운 라이더에게 혼란스러울 수 있습니다. 저희의 완전한 가이드는 요건, 차량 옵션, 그리고 Uber Eats 및 DoorDash와 같은 플랫폼에 가입할 때 피해야 할 가장 흔한 실수들을 설명합니다.", cta: "배달 가이드 전체 읽기", chooseCity: "도시를 선택하세요", sydneyDesc: "수요가 높은 호주 최대 시장", melbourneDesc: "카페 문화가 번성하는 미식의 수도", brisbaneDesc: "화창한 기후와 성장하는 배달 시장" }, joinCompetition: "경쟁에 참여하세요!", documentsSubmitted: "서류가 제출되었습니다!", thankYouSubmission: "제출해 주셔서 감사합니다.", whatHappensNext: "다음은 무엇인가요?", backToHome: "홈으로 돌아가기", uploadDocuments: "서류 업로드", chooseFile: "파일 선택", noFileSelected: "선택된 파일 없음", processing: "처리 중...", submitReview: "검토용 제출", basePackage: "베이직 - $49 AUD", premiumPackage: "프리미엄 - $79 AUD" },
    contactUs: { title: "Just Delivery 연락하기", subtitle: "호주에서의 배달 일에 대해 질문이 있으신가요?", description: "이미 호주에 계시든 곧 도착 예정이시든, Just Delivery에 연락하여 정보와 안내를 받으실 수 있습니다.", description2: "배달 앱이 어떻게 작동하는지 또는 어디서 시작해야 하는지 확실하지 않더라도 편하게 연락해 주세요.", helpWith: "다음에 대한 질문에 기꺼이 답변해 드립니다:", topics: ["호주에서 음식 배달이 어떻게 작동하는지", "어떤 앱으로 일할 수 있는지", "서류와 요건", "자전거, 스쿠터 및 합법적인 차량", "더 빨리 일을 시작하는 방법", "배달 일에 대한 일반적인 질문"], notRiderYet: "아직 라이더일 필요는 없습니다.", exploring: "궁금하시거나 옵션을 탐색 중이시라면 언제든지 연락해 주세요.", ourGoal: "우리의 목표는 단순히 사람들이 호주에서 배달 일을 올바르게 시작하는 방법을 이해하도록 돕는 것입니다.", formPrompt: "질문이 있으시면 아래 양식을 작성해 주시면 저희 팀이 연락드리겠습니다.", form: { firstName: "이름", lastName: "성", email: "이메일 주소", country: "국가", countryPlaceholder: "국가를 선택하세요", city: "도시", cityPlaceholder: "귀하의 도시 (선택 사항)", interest: "무엇에 관심이 있으신가요?", interestPlaceholder: "옵션을 선택하세요", interestOptions: ["배달 일 정보", "라이더 등록", "차량 조언", "일반 질문"], message: "메시지", messagePlaceholder: "여기에 질문이나 메시지를 작성하세요...", submit: "메시지 보내기", sending: "보내는 중..." }, successTitle: "메시지가 성공적으로 전송되었습니다.", successMessage: "저희 팀이 곧 연락드리겠습니다." },
  },
};

const fadeInUp = { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5 } };
const stagger = { animate: { transition: { staggerChildren: 0.1 } } };

// Animated Counter Hook
const useCountUp = (end, duration = 2000, start = 0, trigger = true) => {
  const [count, setCount] = useState(start);
  
  useEffect(() => {
    if (!trigger) return;
    let startTime = null;
    let animationFrame;
    
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(start + (end - start) * easeOut));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, start, trigger]);
  
  return count;
};

// Fireworks Component
const Fireworks = ({ show }) => {
  if (!show) return null;
  
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-2 h-2 rounded-full"
          style={{
            left: `${30 + Math.random() * 40}%`,
            top: `${20 + Math.random() * 30}%`,
            background: ['#FFD700', '#FF6B6B', '#4ECDC4', '#6ABF4B', '#FF69B4'][i % 5],
          }}
          initial={{ scale: 0, opacity: 1 }}
          animate={{
            scale: [0, 1, 1.5, 0],
            opacity: [1, 1, 0.5, 0],
            x: (Math.random() - 0.5) * 200,
            y: (Math.random() - 0.5) * 200,
          }}
          transition={{
            duration: 1.5,
            delay: i * 0.05,
            ease: "easeOut",
          }}
        />
      ))}
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={`star-${i}`}
          className="absolute text-2xl"
          style={{
            left: `${20 + Math.random() * 60}%`,
            top: `${10 + Math.random() * 40}%`,
          }}
          initial={{ scale: 0, opacity: 1, rotate: 0 }}
          animate={{
            scale: [0, 1.5, 0],
            opacity: [1, 1, 0],
            rotate: 360,
          }}
          transition={{
            duration: 1.2,
            delay: 0.3 + i * 0.1,
            ease: "easeOut",
          }}
        >
          ✨
        </motion.div>
      ))}
    </div>
  );
};

// Animated Leaderboard Row
const AnimatedRow = ({ rank, name, deliveries, bonus, bonusColor, delay, onBonusComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showBonus, setShowBonus] = useState(false);
  const deliveryCount = useCountUp(deliveries, 1500, 0, isVisible);
  
  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  
  useEffect(() => {
    if (deliveryCount === deliveries && isVisible) {
      const timer = setTimeout(() => {
        setShowBonus(true);
        if (rank === 1 && onBonusComplete) {
          setTimeout(onBonusComplete, 300);
        }
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [deliveryCount, deliveries, isVisible, rank, onBonusComplete]);
  
  const getRankDisplay = () => {
    if (rank === 1) return <Crown className="w-6 h-6 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-6 h-6 text-gray-400" />;
    if (rank === 3) return <Medal className="w-6 h-6 text-amber-600" />;
    return <span className="text-gray-500 font-bold">{rank}</span>;
  };
  
  return (
    <motion.tr 
      className="border-t border-gray-800"
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: isVisible ? 1 : 0, x: isVisible ? 0 : -20 }}
      transition={{ duration: 0.5 }}
    >
      <td className="py-4">{getRankDisplay()}</td>
      <td className="py-4 text-white font-medium">{name}</td>
      <td className="py-4 text-right text-gray-400 font-mono text-lg">
        {isVisible ? deliveryCount.toLocaleString() : "—"}
      </td>
      <td className="py-4 text-right">
        <AnimatePresence>
          {showBonus && (
            <motion.span 
              className={`${bonusColor} font-bold text-lg`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: [0, 1.3, 1], opacity: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
            >
              ${bonus}
            </motion.span>
          )}
        </AnimatePresence>
      </td>
    </motion.tr>
  );
};

// Main Animated Leaderboard Component
const AnimatedLeaderboard = ({ title, subtitle, icon, iconBg, headerBg, bonusColor, tableHeaders, data, testId }) => {
  const [isInView, setIsInView] = useState(false);
  const [showFireworks, setShowFireworks] = useState(false);
  const ref = useRef(null);
  
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
        }
      },
      { threshold: 0.3 }
    );
    
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isInView]);
  
  const handleFirstPlaceBonus = () => {
    setShowFireworks(true);
    setTimeout(() => setShowFireworks(false), 2000);
  };
  
  return (
    <motion.div 
      ref={ref}
      initial={{ opacity: 0, y: 30 }} 
      whileInView={{ opacity: 1, y: 0 }} 
      viewport={{ once: true }} 
      className="bg-[#111111] border border-gray-800 rounded-2xl overflow-hidden relative" 
      data-testid={testId}
    >
      <Fireworks show={showFireworks} />
      <div className={`bg-gradient-to-r ${headerBg} to-transparent p-6 border-b border-gray-800`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 ${iconBg} rounded-full flex items-center justify-center`}>{icon}</div>
          <div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <p className="text-gray-400 text-sm">{subtitle}</p>
          </div>
        </div>
      </div>
      <div className="p-6">
        <table className="w-full">
          <thead>
            <tr className="text-gray-500 text-sm">
              <th className="text-left pb-4">{tableHeaders.rank}</th>
              <th className="text-left pb-4">{tableHeaders.rider}</th>
              <th className="text-right pb-4">{tableHeaders.deliveries}</th>
              <th className="text-right pb-4">{tableHeaders.bonus}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((rider, i) => (
              <AnimatedRow 
                key={i}
                rank={rider.rank}
                name={rider.name}
                deliveries={rider.deliveries}
                bonus={rider.bonus}
                bonusColor={bonusColor}
                delay={isInView ? i * 800 : 99999}
                onBonusComplete={i === 0 ? handleFirstPlaceBonus : undefined}
              />
            ))}
          </tbody>
        </table>
      </div>
    </motion.div>
  );
};

// Language configuration with native names and country codes for flags
const languages = [
  { code: "en", name: "English", countryCode: "gb" },
  { code: "it", name: "Italiano", countryCode: "it" },
  { code: "es", name: "Español", countryCode: "es" },
  { code: "fr", name: "Français", countryCode: "fr" },
  { code: "pt", name: "Português", countryCode: "br" },
  { code: "vi", name: "Tiếng Việt", countryCode: "vn" },
  { code: "tl", name: "Tagalog", countryCode: "ph" },
  { code: "ms", name: "Bahasa Melayu", countryCode: "my" },
  { code: "tr", name: "Türkçe", countryCode: "tr" },
  { code: "ru", name: "Русский", countryCode: "ru" },
  { code: "zh", name: "中文", countryCode: "cn" },
  { code: "ja", name: "日本語", countryCode: "jp" },
  { code: "ko", name: "한국어", countryCode: "kr" },
];

// Flag component using flagcdn.com
const FlagIcon = ({ countryCode, className = "w-5 h-5" }) => (
  <img 
    src={`https://flagcdn.com/24x18/${countryCode}.png`}
    srcSet={`https://flagcdn.com/48x36/${countryCode}.png 2x`}
    alt=""
    className={`${className} object-contain rounded-sm`}
    loading="lazy"
  />
);

export default function LandingPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [lang, setLang] = useState(() => {
    // Priority: URL param > localStorage > default
    const urlLang = searchParams.get('lang');
    if (urlLang && content[urlLang]) return urlLang;
    const savedLang = localStorage.getItem("preferredLang");
    return savedLang && content[savedLang] ? savedLang : "en";
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [formData, setFormData] = useState({ 
    name: "", email: "", phone: "", city: "", package: "", 
    preferredContact: "email"
  });
  // Separate document uploads
  const [idDocument, setIdDocument] = useState(null);
  const [visaDocument, setVisaDocument] = useState(null);
  const [licenceDocument, setLicenceDocument] = useState(null);
  const [whiteCardDocument, setWhiteCardDocument] = useState(null);
  // Consent checkboxes
  const [consentDocuments, setConsentDocuments] = useState(false);
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  // Contact form state
  const [contactFormData, setContactFormData] = useState({
    firstName: "", lastName: "", email: "", country: "Australia", city: "", interest: "", message: ""
  });
  const [isContactSubmitting, setIsContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);
  const t = content[lang];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle anchor scroll on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const handleLangChange = (newLang) => {
    setLang(newLang);
    localStorage.setItem("preferredLang", newLang);
    setSearchParams({ lang: newLang });
    setLangDropdownOpen(false);
  };

  const currentLangData = languages.find(l => l.code === lang) || languages[0];

  // Check for payment return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const sessionId = params.get("session_id");

    if (payment === "success" && sessionId) {
      setPaymentStatus("success");
      pollPaymentStatus(sessionId);
      window.history.replaceState({}, "", window.location.pathname);
    } else if (payment === "cancelled") {
      setPaymentStatus("cancelled");
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) return;
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (response.data.payment_status === "paid") {
        setPaymentStatus("success");
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Error polling payment status:", error);
    }
  };

  const handleFormChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleContactFormChange = (e) => setContactFormData({ ...contactFormData, [e.target.name]: e.target.value });

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields (country is always Australia, city is required dropdown)
    if (!contactFormData.firstName || !contactFormData.lastName || !contactFormData.email || !contactFormData.city || !contactFormData.interest || !contactFormData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsContactSubmitting(true);
    try {
      const response = await axios.post(`${API}/contact`, {
        first_name: contactFormData.firstName,
        last_name: contactFormData.lastName,
        email: contactFormData.email,
        country: "Australia",
        city: contactFormData.city,
        interest: contactFormData.interest,
        message: contactFormData.message
      });
      
      if (response.data.success) {
        setContactSuccess(true);
        // Reset form
        setContactFormData({ firstName: "", lastName: "", email: "", country: "Australia", city: "", interest: "", message: "" });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsContactSubmitting(false);
    }
  };

  // Handle individual document uploads - validate only, don't store file yet
  const handleDocumentUpload = (e, setDocument) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type. Allowed: PDF, JPG, PNG');
        e.target.value = '';
        return;
      }
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size: 10MB');
        e.target.value = '';
        return;
      }
      setDocument(file);
      toast.success(`${file.name} selected successfully`);
    }
  };

  // Upload a single file to Vercel Blob
  const uploadFileToBlob = async (file) => {
    try {
      const response = await fetch(`/api/upload?filename=${encodeURIComponent(file.name)}`, {
        method: 'POST',
        body: file,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Upload failed');
      }
      
      const data = await response.json();
      return { url: data.url, filename: file.name };
    } catch (error) {
      console.error('File upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.phone || !formData.city || !formData.package) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    // Validate ID document is required
    if (!idDocument) {
      toast.error("Proof of Identity document is required");
      return;
    }
    
    // Validate consent checkboxes
    if (!consentDocuments || !consentTerms || !consentPrivacy) {
      toast.error("Please agree to all consent checkboxes");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Step 1: Upload documents to Blob storage
      const uploadedFiles = [];
      const documentsToUpload = [
        { file: idDocument, label: 'ID Document' },
        { file: visaDocument, label: 'Visa Document' },
        { file: licenceDocument, label: 'Driver Licence' },
        { file: whiteCardDocument, label: 'White Card' },
      ].filter(d => d.file);

      toast.info(`Uploading ${documentsToUpload.length} document(s)...`);

      for (const doc of documentsToUpload) {
        try {
          const uploaded = await uploadFileToBlob(doc.file);
          uploadedFiles.push(uploaded);
          toast.success(`${doc.label} uploaded`);
        } catch (uploadError) {
          toast.error(`Failed to upload ${doc.label}: ${uploadError.message}`);
          setIsSubmitting(false);
          return;
        }
      }

      // Step 2: Submit form data with file URLs
      const submitData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        city: formData.city,
        package: formData.package,
        preferred_contact: formData.preferredContact,
        files: uploadedFiles
      };

      const response = await axios.post(`${API}/submissions`, submitData);

      if (response.data.success) {
        setSubmissionSuccess(true);
        toast.success("Application submitted successfully!");
        // Reset form
        setFormData({ name: "", email: "", phone: "", city: "", package: "", preferredContact: "email" });
        setIdDocument(null);
        setVisaDocument(null);
        setLicenceDocument(null);
        setWhiteCardDocument(null);
        setConsentDocuments(false);
        setConsentTerms(false);
        setConsentPrivacy(false);
      }
    } catch (error) {
      console.error("Error:", error);
      const errorMessage = error.response?.data?.detail || "An error occurred. Please try again.";
      toast.error(errorMessage);
    }
    setIsSubmitting(false);
  };

  const handleNotify = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/notify/leaderboard`, { email: notifyEmail });
      toast.success(response.data.message);
      setNotifyEmail("");
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to subscribe. Please try again.");
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-400" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-gray-500">{rank}</span>;
  };

  const docIcons = { id: IdCard, car: Car, file: FileText };

  return (
    <div className="min-h-screen bg-black">
      {/* Scrolling Banner */}
      <div className="fixed top-0 left-0 right-0 z-[60] bg-[#6ABF4B] overflow-hidden">
        <div className="animate-marquee whitespace-nowrap py-2">
          <span className="mx-4 text-sm font-bold text-white inline-flex items-center gap-2">
            🚴 Become an Uber Eats Rider in Australia • DoorDash Driver Registration • Delivery Jobs Sydney • Melbourne • Brisbane • Start your delivery career today
          </span>
          <span className="mx-4 text-sm font-bold text-white inline-flex items-center gap-2">
            🚴 Become an Uber Eats Rider in Australia • DoorDash Driver Registration • Delivery Jobs Sydney • Melbourne • Brisbane • Start your delivery career today
          </span>
          <span className="mx-4 text-sm font-bold text-white inline-flex items-center gap-2">
            🚴 Become an Uber Eats Rider in Australia • DoorDash Driver Registration • Delivery Jobs Sydney • Melbourne • Brisbane • Start your delivery career today
          </span>
          <span className="mx-4 text-sm font-bold text-white inline-flex items-center gap-2">
            🚴 Become an Uber Eats Rider in Australia • DoorDash Driver Registration • Delivery Jobs Sydney • Melbourne • Brisbane • Start your delivery career today
          </span>
        </div>
      </div>

      {/* Payment Status Modal */}
      {paymentStatus && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`bg-[#111111] border-2 ${paymentStatus === "success" ? "border-[#6ABF4B]" : "border-[#E53935]"} rounded-2xl p-8 max-w-md mx-4 text-center`}
          >
            {paymentStatus === "success" ? (
              <>
                <CheckCircle2 className="w-16 h-16 text-[#6ABF4B] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">{t.contact.paymentSuccess}</h3>
                <p className="text-gray-400 mb-6">{t.contact.paymentSuccessText}</p>
              </>
            ) : (
              <>
                <AlertCircle className="w-16 h-16 text-[#E53935] mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">{t.contact.paymentCancelled}</h3>
                <p className="text-gray-400 mb-6">{t.contact.paymentCancelledText}</p>
              </>
            )}
            <button
              onClick={() => setPaymentStatus(null)}
              className="px-6 py-3 bg-[#6ABF4B] text-white font-bold rounded-lg hover:bg-[#5aa93f] transition-all"
            >
              OK
            </button>
          </motion.div>
        </div>
      )}

      {/* Navigation */}
      <nav className="fixed top-10 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <a href="#" className="text-2xl md:text-3xl font-black" data-testid="logo">
              <span className="text-[#6ABF4B]">just</span>
              <span className="text-[#E53935]">delivery</span>
            </a>
            <div className="hidden lg:flex items-center gap-6">
              {["howItWorks", "packages", "leaderboard", "info", "faq"].map((key) => (
                <a 
                  key={key} 
                  href={`#${key === "howItWorks" ? "how-it-works" : key === "info" ? "contact-us" : key}`} 
                  className={`text-sm font-semibold transition-colors ${
                    key === "leaderboard" 
                      ? "text-yellow-400 hover:text-yellow-300 flex items-center gap-1" 
                      : key === "info"
                      ? "text-[#6ABF4B] hover:text-[#5aa93f] flex items-center gap-1"
                      : "text-white hover:text-[#6ABF4B]"
                  }`}
                >
                  {key === "leaderboard" && <Trophy className="w-4 h-4" />}
                  {key === "info" && <MessageCircle className="w-4 h-4" />}
                  {t.nav[key]}
                  {key === "leaderboard" && <span className="ml-1 px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded">NEW</span>}
                </a>
              ))}
              <Link 
                to={`/for-shops?lang=${lang}`}
                className="text-sm font-semibold transition-colors text-white hover:text-[#6ABF4B]"
              >
                {t.nav.forShops}
              </Link>
            </div>
            {/* Language Dropdown */}
            <div className="hidden md:block relative" ref={langDropdownRef}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
                data-testid="lang-dropdown-trigger"
              >
                <Globe className="w-4 h-4 text-gray-400" />
                <FlagIcon countryCode={currentLangData.countryCode} />
                <span className="text-white font-medium">{currentLangData.name}</span>
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${langDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {langDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 w-48 bg-gray-900 border border-gray-700 rounded-lg shadow-xl overflow-hidden z-50"
                  >
                    <div className="max-h-80 overflow-y-auto">
                      {languages.map((l) => (
                        <button
                          key={l.code}
                          onClick={() => handleLangChange(l.code)}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-gray-800 transition-colors ${lang === l.code ? "bg-[#6ABF4B]/20 text-[#6ABF4B]" : "text-white"}`}
                          data-testid={`lang-option-${l.code}`}
                        >
                          <FlagIcon countryCode={l.countryCode} />
                          <span className="font-medium">{l.name}</span>
                          {lang === l.code && <CheckCircle2 className="w-4 h-4 ml-auto" />}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button className="lg:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6 text-white" /> : <Menu className="w-6 h-6 text-white" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} className="lg:hidden bg-black border-b border-gray-800">
            <div className="px-4 py-4 space-y-3">
              {["howItWorks", "packages", "leaderboard", "info", "faq"].map((key) => (
                <a 
                  key={key} 
                  href={`#${key === "howItWorks" ? "how-it-works" : key === "info" ? "contact-us" : key}`} 
                  className={`block font-semibold ${key === "info" ? "text-[#6ABF4B]" : "text-white"}`} 
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t.nav[key]}
                </a>
              ))}
              <Link to={`/for-shops?lang=${lang}`} className="block text-white font-semibold" onClick={() => setMobileMenuOpen(false)}>
                {t.nav.forShops}
              </Link>
              <div className="pt-3 border-t border-gray-800">
                <p className="text-gray-500 text-sm mb-2 flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Select Language
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { handleLangChange(l.code); setMobileMenuOpen(false); }}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${lang === l.code ? "bg-[#6ABF4B] text-white" : "bg-gray-800 text-gray-300 hover:bg-gray-700"}`}
                      data-testid={`mobile-lang-${l.code}`}
                    >
                      <FlagIcon countryCode={l.countryCode} className="w-4 h-4" />
                      <span className="truncate">{l.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="relative z-10">
              <div className="inline-block mb-6 px-4 py-2 bg-gray-900 text-gray-300 text-sm font-medium rounded-full">{t.hero.badge}</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight leading-[1.1] text-white mb-6">{t.hero.headline}</h1>
              <p className="text-lg md:text-xl text-gray-400 mb-4 max-w-lg">{t.hero.subheadline}</p>
              {/* New Users Only Notice */}
              <div className="mb-8 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg max-w-lg">
                <p className="text-yellow-400 text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{t.notices?.newRidersOnly || "For NEW riders only — not yet registered on Uber Eats or DoorDash (or at least one of them)"}</span>
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#contact" className="px-8 py-4 bg-[#6ABF4B] text-white font-bold text-lg rounded-lg hover:bg-[#5aa93f] transition-all inline-flex items-center justify-center gap-2" data-testid="cta-get-started">
                  {t.hero.cta1} <ArrowRight className="w-5 h-5" />
                </a>
                <a href="#packages" className="px-8 py-4 bg-transparent border-2 border-gray-700 text-white font-bold text-lg rounded-lg hover:border-[#6ABF4B] hover:text-[#6ABF4B] transition-all inline-flex items-center justify-center gap-2" data-testid="cta-packages">
                  {t.hero.cta2}
                </a>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative flex justify-center">
              <img src="/logo.png" alt="Just Delivery" className="w-full max-w-md md:max-w-lg" data-testid="hero-logo" />
            </motion.div>
          </div>
        </div>
        <div className="absolute top-1/2 right-0 w-96 h-96 bg-[#6ABF4B]/10 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#E53935]/10 rounded-full blur-3xl -z-10"></div>
      </section>

      {/* NEW: Highlighted Guide Section */}
      <section className="py-12 md:py-16 bg-gradient-to-b from-black to-[#0a0a0a]" data-testid="guide-highlight-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-[#111111] border-2 border-[#6ABF4B]/50 rounded-2xl p-8 md:p-12 text-center overflow-hidden"
          >
            {/* Background glow effect */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-[#6ABF4B]/10 rounded-full blur-3xl -z-10"></div>
            
            {/* Icon */}
            <div className="w-16 h-16 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-8 h-8 text-[#6ABF4B]" />
            </div>
            
            {/* Title */}
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {t.ui?.guideSection?.title || "New to Food Delivery in Australia?"}
            </h2>
            
            {/* Subtitle */}
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
              {t.ui?.guideSection?.subtitle || "Starting delivery work in Australia can be confusing for new riders. Our complete guide explains the requirements, vehicle options, and the most common mistakes to avoid when signing up for platforms like Uber Eats and DoorDash."}
            </p>
            
            {/* CTA Button */}
            <Link
              to={`/how-to-start-delivery-australia?lang=${lang}`}
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#6ABF4B] text-white font-bold text-lg rounded-lg hover:bg-[#5aa93f] transition-all shadow-lg shadow-[#6ABF4B]/30"
              data-testid="guide-highlight-btn"
            >
              <BookOpen className="w-6 h-6" />
              {t.ui?.guideSection?.cta || "Read the Complete Delivery Guide"}
              <ArrowRight className="w-5 h-5" />
            </Link>

            {/* Choose Your City Section */}
            <div className="mt-12 pt-10 border-t border-gray-700/50">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
                {t.ui?.guideSection?.chooseCity || "Choose Your City"}
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {/* Sydney Card */}
                <Link
                  to="/how-to-start-delivery-sydney"
                  className="group bg-black/50 border border-gray-700 rounded-xl p-5 hover:border-[#6ABF4B] hover:bg-[#6ABF4B]/5 transition-all text-left"
                  data-testid="city-card-sydney"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <MapPin className="w-5 h-5 text-[#6ABF4B]" />
                    <span className="font-bold text-white group-hover:text-[#6ABF4B] transition-colors">Sydney</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t.ui?.guideSection?.sydneyDesc || "Australia's largest market with high demand"}
                  </p>
                </Link>

                {/* Melbourne Card */}
                <Link
                  to="/how-to-start-delivery-melbourne"
                  className="group bg-black/50 border border-gray-700 rounded-xl p-5 hover:border-[#6ABF4B] hover:bg-[#6ABF4B]/5 transition-all text-left"
                  data-testid="city-card-melbourne"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <MapPin className="w-5 h-5 text-[#6ABF4B]" />
                    <span className="font-bold text-white group-hover:text-[#6ABF4B] transition-colors">Melbourne</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t.ui?.guideSection?.melbourneDesc || "Food capital with thriving café culture"}
                  </p>
                </Link>

                {/* Brisbane Card */}
                <Link
                  to="/how-to-start-delivery-brisbane"
                  className="group bg-black/50 border border-gray-700 rounded-xl p-5 hover:border-[#6ABF4B] hover:bg-[#6ABF4B]/5 transition-all text-left"
                  data-testid="city-card-brisbane"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <MapPin className="w-5 h-5 text-[#6ABF4B]" />
                    <span className="font-bold text-white group-hover:text-[#6ABF4B] transition-colors">Brisbane</span>
                  </div>
                  <p className="text-sm text-gray-400">
                    {t.ui?.guideSection?.brisbaneDesc || "Sunny climate with growing delivery market"}
                  </p>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-[#0a0a0a]" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger} className="text-center mb-12 md:mb-16">
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-white mb-4">{t.howItWorks.title}</motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-400">{t.howItWorks.subtitle}</motion.p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.howItWorks.steps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="relative" data-testid={`step-${index}`}>
                <div className="text-6xl font-black text-[#6ABF4B]/20 mb-4">{step.number}</div>
                <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                <p className="text-gray-400">{step.description}</p>
                {index < 3 && <div className="hidden lg:block absolute top-8 right-0 w-1/2 border-t-2 border-dashed border-gray-800"></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages */}
      <section id="packages" className="py-16 md:py-24 bg-black" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger} className="text-center mb-12 md:mb-16">
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-white mb-4">{t.packages.title}</motion.h2>
            <motion.p variants={fadeInUp} className="text-lg text-gray-400 mb-4">{t.packages.subtitle}</motion.p>
            {/* New Users Notice */}
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
              <AlertCircle className="w-4 h-4 text-yellow-400" />
              <span className="text-yellow-400 text-sm font-medium">{t.notices?.newRidersShort || "New riders only — Not yet registered on Uber Eats/DoorDash"}</span>
            </motion.div>
          </motion.div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Base - $49 */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-[#111111] border border-gray-800 rounded-2xl p-8 hover:border-gray-700 transition-all" data-testid="package-base">
              <h3 className="text-2xl font-bold text-white mb-2">{t.packages.base.name}</h3>
              <p className="text-gray-400 mb-4">{t.packages.base.description}</p>
              <div className="mb-6"><span className="text-5xl font-black text-white">$49</span><span className="text-gray-500 ml-2">AUD</span></div>
              <ul className="space-y-3 mb-8">
                {t.packages.base.features.map((f, i) => <li key={i} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#6ABF4B] mt-0.5 flex-shrink-0" /><span className="text-gray-300">{f}</span></li>)}
              </ul>
              <a href="#contact" onClick={() => { setSelectedPackage("base"); setFormData({ ...formData, package: "base" }); }} className="w-full py-4 bg-transparent border-2 border-[#6ABF4B] text-[#6ABF4B] font-bold rounded-lg hover:bg-[#6ABF4B] hover:text-white transition-all flex items-center justify-center gap-2">
                {t.packages.base.cta} <ChevronRight className="w-5 h-5" />
              </a>
            </motion.div>
            {/* Premium - $79 - RECOMMENDED */}
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-[#111111] border-2 border-[#D4AF37] rounded-2xl p-8 relative shadow-lg shadow-[#D4AF37]/20" data-testid="package-premium">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-gradient-to-r from-[#D4AF37] to-[#F4E4BC] text-black text-sm font-bold rounded-full flex items-center gap-1.5">
                <Star className="w-4 h-4 fill-black" />
                {t.packages.premium.popular}
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{t.packages.premium.name}</h3>
              <p className="text-gray-400 mb-4">{t.packages.premium.description}</p>
              <div className="mb-6"><span className="text-5xl font-black text-white">$79</span><span className="text-gray-500 ml-2">AUD</span></div>
              <ul className="space-y-3 mb-4">
                {t.packages.premium.features.map((f, i) => <li key={i} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" /><span className="text-gray-300">{f}</span></li>)}
              </ul>
              {/* Highlighted Feature */}
              <div className="mb-6 p-4 bg-[#D4AF37]/10 border border-[#D4AF37] rounded-lg">
                <div className="flex items-start gap-3">
                  <Star className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0 fill-[#D4AF37]" />
                  <span className="text-[#D4AF37] font-semibold">{t.packages.premium.highlighted}</span>
                </div>
              </div>
              <a href="#contact" onClick={() => { setSelectedPackage("premium"); setFormData({ ...formData, package: "premium" }); }} className="w-full py-4 bg-gradient-to-r from-[#D4AF37] to-[#F4E4BC] text-black font-bold rounded-lg hover:from-[#C4A030] hover:to-[#E4D4AC] transition-all flex items-center justify-center gap-2">
                {t.packages.premium.cta} <ChevronRight className="w-5 h-5" />
              </a>
            </motion.div>
          </div>
          {/* Legal Disclaimer */}
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mt-8 p-4 bg-gray-900/50 border border-gray-800 rounded-lg max-w-4xl mx-auto">
            <p className="text-gray-400 text-xs text-center">
              <strong>Disclaimer:</strong> Just Delivery / JD NETWORK PTY LTD (ACN 695 764 071) is not affiliated with, endorsed by, or partnered with Uber Eats or DoorDash. 
              Approval decisions are made solely by the platforms. We provide guidance services only — platform approval is not guaranteed.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Leaderboard */}
      <section id="leaderboard" className="py-16 md:py-24 bg-[#0a0a0a]" data-testid="leaderboard-section">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger} className="text-center mb-12 md:mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/20 text-yellow-400 text-sm font-semibold rounded-full mb-4"><Trophy className="w-4 h-4" /> LIVE Leaderboard</motion.div>
            {/* Premium Only Badge */}
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-full mb-6 ml-2">
              <Crown className="w-4 h-4 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-semibold">{t.notices?.premiumOnly || "Premium Members Only"}</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-white mb-2">{t.leaderboard.title}</motion.h2>
            <motion.p variants={fadeInUp} className="text-2xl md:text-3xl font-bold text-[#6ABF4B] mb-4">{t.leaderboard.subtitle}</motion.p>
            <motion.p variants={fadeInUp} className="text-lg text-gray-400 max-w-2xl mx-auto mb-4">{t.leaderboard.description}</motion.p>
            <motion.p variants={fadeInUp} className="text-sm text-[#D4AF37] max-w-xl mx-auto">
              🏆 {t.notices?.leaderboardPremium || "To participate in the leaderboard and win cash bonuses, you must have purchased the Premium package"}
            </motion.p>
          </motion.div>
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Weekly */}
            <AnimatedLeaderboard 
              title={t.leaderboard.weekly.title}
              subtitle={t.leaderboard.weekly.subtitle}
              icon={<TrendingUp className="w-6 h-6 text-[#6ABF4B]" />}
              iconBg="bg-[#6ABF4B]/20"
              headerBg="from-[#6ABF4B]/20"
              bonusColor="text-[#6ABF4B]"
              tableHeaders={t.leaderboard.tableHeaders}
              data={[
                { rank: 1, name: "Marco R.", deliveries: 156, bonus: 50 },
                { rank: 2, name: "Sofia L.", deliveries: 142, bonus: 30 },
                { rank: 3, name: "Carlos M.", deliveries: 128, bonus: 20 },
              ]}
              testId="weekly-leaderboard"
            />
            {/* Monthly */}
            <AnimatedLeaderboard 
              title={t.leaderboard.monthly.title}
              subtitle={t.leaderboard.monthly.subtitle}
              icon={<Trophy className="w-6 h-6 text-yellow-400" />}
              iconBg="bg-yellow-500/20"
              headerBg="from-yellow-500/20"
              bonusColor="text-yellow-400"
              tableHeaders={t.leaderboard.tableHeaders}
              data={[
                { rank: 1, name: "Alex T.", deliveries: 624, bonus: 150 },
                { rank: 2, name: "Maria G.", deliveries: 568, bonus: 100 },
                { rank: 3, name: "David K.", deliveries: 512, bonus: 50 },
              ]}
              multiplier={4}
              testId="monthly-leaderboard"
            />
          </div>
          {/* Notify */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-gradient-to-r from-[#6ABF4B]/10 to-yellow-500/10 border border-gray-800 rounded-2xl p-8 text-center max-w-2xl mx-auto">
            <Bell className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-2">{t.ui?.joinCompetition || "Join the Competition!"}</h3>
            <p className="text-gray-400 mb-6">{t.ui?.notifyDescription || "Get notified about weekly challenges and exclusive bonus opportunities"}</p>
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input type="email" value={notifyEmail} onChange={(e) => setNotifyEmail(e.target.value)} placeholder={t.ui?.enterEmail || "Enter your email"} required className="flex-1 px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none" data-testid="notify-email" />
              <button type="submit" className="px-6 py-3 bg-[#6ABF4B] text-white font-bold rounded-lg hover:bg-[#5aa93f] transition-all flex items-center justify-center gap-2" data-testid="notify-btn"><Bell className="w-4 h-4" />{t.leaderboard.notifyBtn}</button>
            </form>
          </motion.div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact-us" className="py-16 md:py-24 bg-black" data-testid="contact-us-section">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Left side - Info */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#6ABF4B]/20 text-[#6ABF4B] text-sm font-semibold rounded-full mb-6">
                <MessageCircle className="w-4 h-4" />
                Contact
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.contactUs?.title || "Contact Just Delivery"}</h2>
              <p className="text-xl text-gray-400 mb-4">{t.contactUs?.subtitle || "Questions about delivery work in Australia?"}</p>
              
              <p className="text-gray-400 mb-4">{t.contactUs?.description || "Whether you are already in Australia or planning to arrive soon, you can contact Just Delivery for information and guidance."}</p>
              <p className="text-gray-400 mb-6">{t.contactUs?.description2 || "Even if you are not sure how delivery apps work or where to start, feel free to reach out."}</p>
              
              <p className="text-white font-semibold mb-3">{t.contactUs?.helpWith || "We are happy to answer questions about:"}</p>
              <ul className="space-y-2 mb-6">
                {(t.contactUs?.topics || [
                  "how food delivery works in Australia",
                  "which apps you can work with",
                  "documents and requirements",
                  "bikes, scooters and legal vehicles",
                  "how to start working faster",
                  "general questions about delivery work"
                ]).map((topic, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-400">
                    <CheckCircle2 className="w-4 h-4 text-[#6ABF4B] flex-shrink-0" />
                    <span>{topic}</span>
                  </li>
                ))}
              </ul>

              <p className="text-gray-300 mb-4">{t.contactUs?.notRiderYet || "You do not need to be a rider yet."}</p>
              <p className="text-gray-400 mb-4">{t.contactUs?.exploring || "If you are curious or just exploring your options, you are welcome to contact us."}</p>
              <p className="text-gray-400 mb-6">{t.contactUs?.ourGoal || "Our goal is simply to help people understand how to start delivery work in Australia the right way."}</p>
              
              <p className="text-[#6ABF4B] font-medium">{t.contactUs?.formPrompt || "If you have questions, fill out the form below and our team will get back to you."}</p>
            </motion.div>

            {/* Right side - Form */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="bg-[#111111] border border-gray-800 rounded-2xl p-6 md:p-8"
            >
              {contactSuccess ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-[#6ABF4B]" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">{t.contactUs?.successTitle || "Message sent successfully."}</h3>
                  <p className="text-gray-400 mb-6">{t.contactUs?.successMessage || "Our team will contact you soon."}</p>
                  <button 
                    onClick={() => setContactSuccess(false)}
                    className="px-6 py-3 bg-[#6ABF4B] text-white font-semibold rounded-lg hover:bg-[#5aa93f] transition-colors"
                    data-testid="contact-send-another"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">{t.contactUs?.form?.firstName || "First Name"} *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={contactFormData.firstName}
                        onChange={handleContactFormChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#6ABF4B] focus:outline-none"
                        data-testid="contact-firstname"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-400 mb-2">{t.contactUs?.form?.lastName || "Last Name"} *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={contactFormData.lastName}
                        onChange={handleContactFormChange}
                        required
                        className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#6ABF4B] focus:outline-none"
                        data-testid="contact-lastname"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">{t.contactUs?.form?.email || "Email Address"} *</label>
                    <input
                      type="email"
                      name="email"
                      value={contactFormData.email}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#6ABF4B] focus:outline-none"
                      data-testid="contact-email"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">{t.contactUs?.form?.city || "City"} *</label>
                    <select
                      name="city"
                      value={contactFormData.city}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none"
                      data-testid="contact-city"
                    >
                      <option value="">{t.contactUs?.form?.cityPlaceholder || "Select your city"}</option>
                      <option value="Sydney">Sydney</option>
                      <option value="Melbourne">Melbourne</option>
                      <option value="Brisbane">Brisbane</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">{t.contactUs?.form?.interest || "What are you interested in?"} *</label>
                    <select
                      name="interest"
                      value={contactFormData.interest}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none"
                      data-testid="contact-interest"
                    >
                      <option value="">{t.contactUs?.form?.interestPlaceholder || "Select an option"}</option>
                      {(t.contactUs?.form?.interestOptions || [
                        "Delivery work information",
                        "Rider onboarding",
                        "Vehicle advice",
                        "General questions"
                      ]).map((option, i) => (
                        <option key={i} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">{t.contactUs?.form?.message || "Message"} *</label>
                    <textarea
                      name="message"
                      value={contactFormData.message}
                      onChange={handleContactFormChange}
                      required
                      rows={5}
                      placeholder={t.contactUs?.form?.messagePlaceholder || "Write your question or message here..."}
                      className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-[#6ABF4B] focus:outline-none resize-none"
                      data-testid="contact-message"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isContactSubmitting}
                    className="w-full px-6 py-4 bg-[#6ABF4B] text-white font-bold rounded-lg hover:bg-[#5aa93f] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit"
                  >
                    {isContactSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {t.contactUs?.form?.sending || "Sending..."}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t.contactUs?.form?.submit || "Send Message"}
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Form with Documents */}
      <section id="contact" className="py-16 md:py-24 bg-[#0a0a0a]" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          {/* Success Screen after submission */}
          {submissionSuccess ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }} 
              animate={{ opacity: 1, scale: 1 }} 
              className="bg-[#111111] border-2 border-[#6ABF4B] rounded-2xl p-8 md:p-12 text-center"
              data-testid="submission-success"
            >
              <div className="w-20 h-20 bg-[#6ABF4B]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-[#6ABF4B]" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">{t.ui?.documentsSubmitted || "Documents Submitted!"}</h2>
              <p className="text-xl text-gray-300 mb-6">{t.ui?.thankYouSubmission || "Thank you for your submission."}</p>
              
              <div className="bg-black rounded-xl p-6 mb-8 text-left max-w-lg mx-auto">
                <h3 className="text-lg font-semibold text-[#6ABF4B] mb-4">{t.ui?.whatHappensNext || "What happens next?"}</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">1</span>
                    </span>
                    <span className="text-gray-300">{t.ui?.reviewDocuments || "We will review your documents within"} <strong className="text-white">{t.ui?.hours24to48 || "24-48 hours"}</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">2</span>
                    </span>
                    <span className="text-gray-300">{t.ui?.ifEligible || "If eligible, you will receive"} <strong className="text-white">{t.ui?.paymentInstructionsEmail || "payment instructions via email"}</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-[#6ABF4B] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-sm font-bold">3</span>
                    </span>
                    <span className="text-gray-300">{t.ui?.afterPayment || "After payment confirmation, we'll start your"} <strong className="text-white">{t.ui?.onboardingProcess || "onboarding process"}</strong></span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6 max-w-lg mx-auto">
                <p className="text-yellow-400 text-sm flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{t.ui?.additionalInfo || "If we need additional information, we will contact you via your preferred method."}</span>
                </p>
              </div>

              <button 
                onClick={() => setSubmissionSuccess(false)}
                className="px-8 py-3 bg-transparent border-2 border-gray-700 text-white font-semibold rounded-lg hover:border-[#6ABF4B] transition-colors"
              >
                {t.ui?.backToHome || "Back to Home"}
              </button>
            </motion.div>
          ) : (
            <>
              <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
                <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-white mb-4">{t.contact.title}</motion.h2>
                <motion.p variants={fadeInUp} className="text-lg text-gray-400 mb-4">{t.ui?.submitForReview || "Submit your documents for eligibility review"}</motion.p>
                {/* New Users Warning */}
                <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 px-4 py-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                  <span className="text-red-400 text-sm font-medium">{t.notices?.newRidersImportant || "Important: This service is ONLY for new riders not yet registered on Uber Eats or DoorDash (or at least one of the two platforms)"}</span>
                </motion.div>
              </motion.div>

              {/* Legal Disclaimer - Required */}
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-gray-900/50 border border-gray-700 rounded-xl p-4 mb-8 text-center">
                <p className="text-gray-400 text-sm">
                  <strong className="text-white">JD NETWORK PTY LTD</strong> {t.ui?.legalDisclaimer || "(operating as Just Delivery) is an independent onboarding support service and is"} <strong className="text-yellow-400">{t.ui?.notAffiliated || "not affiliated with, endorsed by, or partnered with Uber Eats or DoorDash"}</strong>. 
                  {t.ui?.platformApproval || "Platform approval decisions are made solely by the respective platforms."}
                </p>
              </motion.div>

              {/* Documents Required Section */}
              <motion.div id="required-documents" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-[#111111] border border-gray-800 rounded-2xl p-8 mb-8 scroll-mt-24">
                <h3 className="text-2xl font-bold text-white mb-2">{t.documents.title}</h3>
                <p className="text-gray-400 mb-6">{t.documents.subtitle}</p>
                <div className="grid md:grid-cols-3 gap-4 mb-8">
                  {t.documents.items.map((item, i) => {
                    const Icon = docIcons[item.icon];
                    return (
                      <div key={i} className="p-4 bg-black rounded-lg border border-gray-800">
                        <Icon className="w-8 h-8 text-[#6ABF4B] mb-3" />
                        <h4 className="text-white font-semibold mb-1">{item.title}</h4>
                        <p className="text-gray-400 text-sm">{item.desc}</p>
                      </div>
                    );
                  })}
                </div>

                {/* License Warning Box - Highlighted */}
                <div className="bg-[#E53935]/10 border-2 border-[#E53935] rounded-xl p-6 mb-8">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-[#E53935] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-lg font-bold text-[#E53935] mb-3">{t.documents.licenseWarning.title}</h4>
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <span className="px-2 py-1 bg-[#E53935] text-white text-xs font-bold rounded">NSW & VIC</span>
                          <p className="text-white font-medium">{t.documents.licenseWarning.nsw_vic}</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="px-2 py-1 bg-[#6ABF4B] text-white text-xs font-bold rounded">QLD</span>
                          <p className="text-white font-medium">{t.documents.licenseWarning.qld}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* License Conversion Info */}
                <div id="licence-conversion" className="bg-black rounded-lg p-6 border border-gray-800">
                  <h4 className="text-lg font-bold text-[#6ABF4B] mb-4 flex items-center gap-2"><Car className="w-5 h-5" />{t.documents.licenseTitle}</h4>
                  <ol className="list-decimal list-inside text-gray-400 space-y-2 mb-6">
                    {t.documents.licenseSteps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                  <div className="grid md:grid-cols-3 gap-4">
                    {t.documents.offices.map((office, i) => (
                      <div key={i} className="p-3 bg-[#111111] rounded-lg">
                        <p className="text-[#6ABF4B] font-semibold text-sm">{office.city}</p>
                        <p className="text-white text-sm">{office.name}</p>
                        <p className="text-gray-500 text-xs">{office.address}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Registration Form */}
              <motion.form initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} onSubmit={handleSubmit} className="bg-[#111111] border border-gray-800 rounded-2xl p-8">
                <div className="space-y-6">
                  {/* Personal Info */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t.contact.form.name} *</label>
                    <input type="text" name="name" value={formData.name} onChange={handleFormChange} required className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none transition-colors" data-testid="form-name" />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{t.contact.form.email} *</label>
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} required className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none transition-colors" data-testid="form-email" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{t.contact.form.phone} *</label>
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none transition-colors" data-testid="form-phone" />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{t.contact.form.city} *</label>
                      <select name="city" value={formData.city} onChange={handleFormChange} required className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none transition-colors" data-testid="form-city">
                        <option value="">{t.contact.form.cityPlaceholder}</option>
                        {t.contact.form.cities.map((city) => <option key={city} value={city}>{city}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">{t.contact.form.package} *</label>
                      <select name="package" value={formData.package} onChange={handleFormChange} required className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white focus:border-[#6ABF4B] focus:outline-none transition-colors" data-testid="form-package">
                        <option value="">{t.contact.form.packagePlaceholder}</option>
                        <option value="base">{t.ui?.basePackage || "Base - $49 AUD"}</option>
                        <option value="premium">{t.ui?.premiumPackage || "Premium - $79 AUD"}</option>
                      </select>
                    </div>
                  </div>

                  {/* Document Uploads - Separate Fields */}
                  <div className="border-t border-gray-800 pt-6">
                    <h4 className="text-lg font-semibold text-white mb-2">{t.ui?.uploadDocuments || "Upload Documents"}</h4>
                    <p className="text-gray-500 text-sm mb-4">{t.ui?.acceptedFormats || "Accepted formats: PDF, JPG, PNG (Max 10MB each)"}</p>
                    
                    <div className="space-y-4">
                      {/* Proof of Identity - Required */}
                      <div className="p-4 bg-black rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                            <IdCard className="w-4 h-4 text-[#6ABF4B]" />
                            {t.ui?.proofOfIdentity || "Proof of Identity (Passport/ID)"} *
                          </label>
                          {idDocument && <span className="text-[#6ABF4B] text-xs flex items-center gap-1"><FileCheck className="w-3 h-3" /> {t.ui?.uploaded || "Uploaded"}</span>}
                        </div>
                        <div className="relative">
                          <input 
                            type="file" 
                            id="id-document-input"
                            onChange={(e) => handleDocumentUpload(e, setIdDocument)}
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            data-testid="form-id-document"
                          />
                          <div className="flex items-center gap-3">
                            <span className="py-2 px-4 rounded-lg bg-[#6ABF4B]/20 text-[#6ABF4B] font-semibold text-sm cursor-pointer hover:bg-[#6ABF4B]/30 transition-colors">
                              {t.ui?.chooseFile || "Choose file"}
                            </span>
                            <span className="text-gray-400 text-sm">
                              {idDocument ? idDocument.name : (t.ui?.noFileSelected || "No file selected")}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Work Rights / Visa */}
                      <div className="p-4 bg-black rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-[#6ABF4B]" />
                            {t.ui?.workRightsVisa || "Work Rights / Visa (VEVO or Visa Grant)"}
                          </label>
                          {visaDocument && <span className="text-[#6ABF4B] text-xs flex items-center gap-1"><FileCheck className="w-3 h-3" /> {t.ui?.uploaded || "Uploaded"}</span>}
                        </div>
                        <div className="relative">
                          <input 
                            type="file" 
                            id="visa-document-input"
                            onChange={(e) => handleDocumentUpload(e, setVisaDocument)}
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            data-testid="form-visa-document"
                          />
                          <div className="flex items-center gap-3">
                            <span className="py-2 px-4 rounded-lg bg-[#6ABF4B]/20 text-[#6ABF4B] font-semibold text-sm cursor-pointer hover:bg-[#6ABF4B]/30 transition-colors">
                              {t.ui?.chooseFile || "Choose file"}
                            </span>
                            <span className="text-gray-400 text-sm">
                              {visaDocument ? visaDocument.name : (t.ui?.noFileSelected || "No file selected")}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Australian Driver Licence - Optional */}
                      <div className="p-4 bg-black rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-medium text-gray-300 flex items-center gap-2">
                            <Car className="w-4 h-4 text-[#6ABF4B]" />
                            {t.ui?.driverLicence || "Driver Licence"} <span className="text-gray-500 text-xs">{t.ui?.onlyForScooter || "(Only required for scooter/motorbike delivery)"}</span>
                          </label>
                          {licenceDocument && <span className="text-[#6ABF4B] text-xs flex items-center gap-1"><FileCheck className="w-3 h-3" /> {t.ui?.uploaded || "Uploaded"}</span>}
                        </div>
                        <div className="relative">
                          <input 
                            type="file" 
                            id="licence-document-input"
                            onChange={(e) => handleDocumentUpload(e, setLicenceDocument)}
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            data-testid="form-licence-document"
                          />
                          <div className="flex items-center gap-3">
                            <span className="py-2 px-4 rounded-lg bg-[#6ABF4B]/20 text-[#6ABF4B] font-semibold text-sm cursor-pointer hover:bg-[#6ABF4B]/30 transition-colors">
                              {t.ui?.chooseFile || "Choose file"}
                            </span>
                            <span className="text-gray-400 text-sm">
                              {licenceDocument ? licenceDocument.name : (t.ui?.noFileSelected || "No file selected")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Privacy Notice */}
                    <p className="text-gray-500 text-xs mt-4 flex items-start gap-2">
                      <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      {t.ui?.privacyNotice || "Your documents are stored securely and encrypted. We only use them to verify your eligibility and will not share with third parties without your consent."}
                    </p>
                  </div>

                  {/* Consent Checkboxes - Required */}
                  <div className="border-t border-gray-800 pt-6">
                    <h4 className="text-lg font-semibold text-white mb-4">{t.ui?.consentAgreements || "Consent & Agreements"}</h4>
                    <div className="space-y-4">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={consentDocuments}
                          onChange={(e) => setConsentDocuments(e.target.checked)}
                          className="mt-1 w-5 h-5 rounded border-gray-600 bg-black text-[#6ABF4B] focus:ring-[#6ABF4B] focus:ring-offset-0 cursor-pointer"
                          data-testid="consent-documents"
                        />
                        <span className="text-gray-300 text-sm group-hover:text-white transition-colors" dangerouslySetInnerHTML={{ __html: t.ui?.consentDocumentsText || "I confirm that all documents submitted are <strong>accurate and authentic</strong>. I understand that submitting false documents may result in rejection and legal consequences." }}>
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={consentTerms}
                          onChange={(e) => setConsentTerms(e.target.checked)}
                          className="mt-1 w-5 h-5 rounded border-gray-600 bg-black text-[#6ABF4B] focus:ring-[#6ABF4B] focus:ring-offset-0 cursor-pointer"
                          data-testid="consent-terms"
                        />
                        <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
                          {t.ui?.consentTermsText || "I confirm that I have read and agree to the"} <a href="/terms" target="_blank" className="text-[#6ABF4B] underline hover:no-underline">{t.footer?.termsOfService || "Terms of Service"}</a>, <a href="/privacy" target="_blank" className="text-[#6ABF4B] underline hover:no-underline">{t.footer?.privacyPolicy || "Privacy Policy"}</a> {t.ui?.andRiderAgreement || "and Rider Service Agreement. I understand that Just Delivery is not affiliated with Uber Eats or DoorDash and platform approval is not guaranteed."}
                        </span>
                      </label>

                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={consentPrivacy}
                          onChange={(e) => setConsentPrivacy(e.target.checked)}
                          className="mt-1 w-5 h-5 rounded border-gray-600 bg-black text-[#6ABF4B] focus:ring-[#6ABF4B] focus:ring-offset-0 cursor-pointer"
                          data-testid="consent-privacy"
                        />
                        <span className="text-gray-300 text-sm group-hover:text-white transition-colors">
                          {t.ui?.consentPrivacyText || "I consent to the processing of my personal data as described in the"} <a href="/privacy" target="_blank" className="text-[#6ABF4B] underline hover:no-underline">{t.footer?.privacyPolicy || "Privacy Policy"}</a>.
                        </span>
                      </label>
                    </div>
                  </div>

                  {/* What Happens Next - Mini Box */}
                  <div className="bg-black border border-gray-800 rounded-lg p-5">
                    <h4 className="text-sm font-semibold text-[#6ABF4B] mb-3">{t.ui?.whatHappensNext || "What happens next?"}</h4>
                    <ol className="space-y-2 text-sm text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="text-[#6ABF4B] font-bold">1.</span>
                        {t.ui?.reviewDocs24h || "We review your documents (24–48h)"}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#6ABF4B] font-bold">2.</span>
                        {t.ui?.receiveConfirmation || "You receive eligibility confirmation"}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#6ABF4B] font-bold">3.</span>
                        {t.ui?.completePayment || "You complete payment"}
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#6ABF4B] font-bold">4.</span>
                        {t.ui?.beginOnboarding || "We begin onboarding support"}
                      </li>
                    </ol>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting || !consentDocuments || !consentTerms || !consentPrivacy}
                    className="w-full py-4 bg-[#6ABF4B] text-white font-bold text-lg rounded-lg hover:bg-[#5aa93f] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="form-submit"
                  >
                    {isSubmitting ? (
                      <><Loader2 className="w-5 h-5 animate-spin" />{t.ui?.processing || "Processing..."}</>
                    ) : (
                      <><FileCheck className="w-5 h-5" />{t.ui?.submitReview || "Submit for Review"}</>
                    )}
                  </button>
                  <p className="text-center text-gray-500 text-sm">
                    {t.ui?.noPaymentNow || "No payment required now. You will receive payment instructions after document review."}
                  </p>
                </div>
              </motion.form>
            </>
          )}
        </div>
      </section>

      {/* FAQ - Bottom */}
      <section id="faq" className="py-16 md:py-24 bg-[#0a0a0a]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <motion.div initial="initial" whileInView="animate" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.h2 variants={fadeInUp} className="text-4xl md:text-5xl font-bold text-white">{t.faq.title}</motion.h2>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-[#111111] border border-gray-800 rounded-xl">
            <Accordion type="single" collapsible className="w-full">
              {t.faq.items.map((item, index) => (
                <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-800 last:border-b-0" data-testid={`faq-item-${index}`}>
                  <AccordionTrigger className="px-6 py-4 text-white text-left hover:no-underline hover:text-[#6ABF4B] transition-colors">{item.q}</AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-gray-400">{item.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-black border-t border-gray-800" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
            <span className="text-xl font-black"><span className="text-[#6ABF4B]">just</span><span className="text-[#E53935]">delivery</span></span>
            <p className="text-gray-400 text-center">{t.footer.tagline}</p>
            <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-center">
              <a href="/privacy" className="text-sm text-gray-400 hover:text-[#6ABF4B] transition-colors" data-testid="footer-privacy">{t.footer?.privacyPolicy || "Privacy Policy"}</a>
              <span className="text-gray-600">|</span>
              <a href="/terms" className="text-sm text-gray-400 hover:text-[#6ABF4B] transition-colors" data-testid="footer-terms">{t.footer?.termsOfService || "Terms of Service"}</a>
              <span className="text-gray-600">|</span>
              <a href="/disclaimer" className="text-sm text-gray-400 hover:text-[#6ABF4B] transition-colors" data-testid="footer-disclaimer">{t.footer?.websiteDisclaimer || "Website Disclaimer"}</a>
              <span className="text-gray-600">|</span>
              <a href="/refund" className="text-sm text-gray-400 hover:text-[#6ABF4B] transition-colors" data-testid="footer-refund">{t.footer?.refundPolicy || "Refund Policy"}</a>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6">
            <div className="flex flex-col gap-4 text-center">
              <p className="text-sm text-gray-500">© {new Date().getFullYear()} JD NETWORK PTY LTD (ACN 695 764 071) operating as Just Delivery</p>
              <p className="text-xs text-gray-500">Just Delivery operates as an independent onboarding consultancy and referral coordination service in Australia.</p>
              <p className="text-xs text-gray-600 italic">
                {t.footer?.serviceDisclaimer || "Just Delivery is an independent onboarding assistance service and is not affiliated with Uber Eats, DoorDash, or any delivery platform."}
              </p>
              <p className="text-xs text-gray-600">
                <strong>{t.ui?.disclaimer || "Disclaimer:"}</strong> {t.ui?.disclaimerText || "JD NETWORK PTY LTD is not affiliated with, endorsed by, or partnered with Uber Eats or DoorDash. Platform approval decisions are made solely by the respective platforms."}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
